import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Users, 
  Building2, 
  Plus,
  Edit,
  Trash2,
  Settings,
  Eye,
  Download,
  Mail,
  User,
  Key,
  FileText,
  Upload,
  ExternalLink,
  Monitor,
  Target,
  BarChart3,
  Send,
  Clock,
  CheckCircle,
  History,
  Database,
  Search,
  AlertCircle,
  AlertTriangle,
  Check,
  Calendar,
  Cloud,
  RefreshCw,
  Building
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import EmailAdmin from '../components/EmailAdmin';
import LoginHistory from '../components/LoginHistory';
import { useToast } from '../components/Toast';
import MobileBannerSettings from '../components/MobileBannerSettings';
import ServiceMessageSettings from '../components/ServiceMessageSettings';
import FeatureFlagsPanel from '../components/FeatureFlagsPanel';
import IdentityLinkingPanel from '../components/IdentityLinkingPanel';
// Simple Rich Text Editor Components

// EMERGENCY FIX: Force use of window.location.origin to bypass env var cache issues
// This ensures API calls ALWAYS use the correct server based on current domain
// ORIGINAL CODE (for reverting if needed):
// const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SuperAdmin = () => {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('organizations'); // Default to organizations tab
  const [organizations, setOrganizations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState({
    totalOrgs: 0,
    activeOrgs: 0,
    totalUsers: 0,
    superAdmins: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showCreateSuperUserModal, setShowCreateSuperUserModal] = useState(false);
  const [newSuperUserForm, setNewSuperUserForm] = useState({
    full_name: '',
    email: '',
    password: ''
  });
  const [showUploadRestore, setShowUploadRestore] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [financialStats, setFinancialStats] = useState({
    organizations: { total: 0, start_plan: 0, unlimited_plan: 0 },
    revenue: { start_revenue: 0, unlimited_revenue: 0, total_revenue: 0 },
    expenses: { emergent_platform: 220, amazon_ses: 50, total: 270 },
    profit: { monthly_profit: 0, profit_margin: 0, break_even_point: 1 },
    growth: { current_orgs: 0, growth_target: 50, progress_percent: 0 }
  });
  const [privacyPolicy, setPrivacyPolicy] = useState({
    content: '',
    updated_at: null,
    editing: false,
    viewMode: 'html' // 'html' or 'preview'
  });
  
  // System/Migration state
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [templateMigrationLoading, setTemplateMigrationLoading] = useState(false);
  const [templateMigrationResult, setTemplateMigrationResult] = useState(null);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Pending members diagnostic state
  const [pendingDiagnoseLoading, setPendingDiagnoseLoading] = useState(false);
  const [pendingDiagnoseResult, setPendingDiagnoseResult] = useState(null);
  const [pendingFixLoading, setPendingFixLoading] = useState(false);
  const [pendingFixResult, setPendingFixResult] = useState(null);

  // Ghost members cleanup state
  const [ghostDiagnoseLoading, setGhostDiagnoseLoading] = useState(false);
  const [ghostDiagnoseResult, setGhostDiagnoseResult] = useState(null);
  const [ghostCleanupLoading, setGhostCleanupLoading] = useState(false);
  const [ghostCleanupResult, setGhostCleanupResult] = useState(null);
  const [selectedGhostEmails, setSelectedGhostEmails] = useState([]);

  // Banner cleanup state
  const [bannerCleanupLoading, setBannerCleanupLoading] = useState(false);
  const [bannerCleanupResult, setBannerCleanupResult] = useState(null);
  const [bannerCleanupOrgId, setBannerCleanupOrgId] = useState('');

  // Cloudinary to S3 migration state
  const [profileS3MigrationLoading, setProfileS3MigrationLoading] = useState(false);
  const [profileS3MigrationResult, setProfileS3MigrationResult] = useState(null);
  const [profileS3MigrationOrgId, setProfileS3MigrationOrgId] = useState('');
  const [profileS3DryRun, setProfileS3DryRun] = useState(true);
  
  // Extended S3 migration state
  const [s3MigrationStatus, setS3MigrationStatus] = useState(null);
  const [s3MigrationStatusLoading, setS3MigrationStatusLoading] = useState(false);
  const [userDocsS3Loading, setUserDocsS3Loading] = useState(false);
  const [userDocsS3Result, setUserDocsS3Result] = useState(null);
  const [userDocsS3DryRun, setUserDocsS3DryRun] = useState(true);
  const [userDocsS3Type, setUserDocsS3Type] = useState('all');
  const [trainingImagesS3Loading, setTrainingImagesS3Loading] = useState(false);
  const [trainingImagesS3Result, setTrainingImagesS3Result] = useState(null);
  const [trainingImagesS3DryRun, setTrainingImagesS3DryRun] = useState(true);
  const [orgLogosS3Loading, setOrgLogosS3Loading] = useState(false);
  const [orgLogosS3Result, setOrgLogosS3Result] = useState(null);
  const [orgLogosS3DryRun, setOrgLogosS3DryRun] = useState(true);

  // Fix document paths state
  const [diagnoseDocPathsLoading, setDiagnoseDocPathsLoading] = useState(false);
  const [diagnoseDocPathsResult, setDiagnoseDocPathsResult] = useState(null);
  const [fixDocPathsLoading, setFixDocPathsLoading] = useState(false);
  const [fixDocPathsResult, setFixDocPathsResult] = useState(null);
  
  // Birthdate migration state
  const [birthdateMigrationLoading, setBirthdateMigrationLoading] = useState(false);
  const [birthdateMigrationResult, setBirthdateMigrationResult] = useState(null);
  const [profileImageMigrationLoading, setProfileImageMigrationLoading] = useState(false);
  const [profileImageMigrationResult, setProfileImageMigrationResult] = useState(null);
  const [trainingImageMigrationLoading, setTrainingImageMigrationLoading] = useState(false);
  const [trainingImageMigrationResult, setTrainingImageMigrationResult] = useState(null);


  // Auto-discovery state
  const [discoverDocsLoading, setDiscoverDocsLoading] = useState(false);
  const [discoverDocsResult, setDiscoverDocsResult] = useState(null);
  const [autoRelinkLoading, setAutoRelinkLoading] = useState(false);
  const [autoRelinkResult, setAutoRelinkResult] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [backupsList, setBackupsList] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  
  // Organization selection
  const [organizationsList, setOrganizationsList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgsLoading, setOrgsLoading] = useState(false);

  // Resend verification emails state
  const [unverifiedLoading, setUnverifiedLoading] = useState(false);
  const [unverifiedResult, setUnverifiedResult] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [selectedUnverifiedEmails, setSelectedUnverifiedEmails] = useState([]);

  // Fix pending members state
  const [selectedPendingEmails, setSelectedPendingEmails] = useState([]);
  const [fixPendingLoading, setFixPendingLoading] = useState(false);
  const [fixPendingResult, setFixPendingResult] = useState(null);

  // All users database state
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [allUsersData, setAllUsersData] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [updatingUserStatus, setUpdatingUserStatus] = useState({});
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userStatusFilters, setUserStatusFilters] = useState({
    inactive: false,
    not_approved: false,
    not_verified: false,
    locked: false,
    deleted: false,
  });

  // Locked accounts state
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [lockedAccountsLoading, setLockedAccountsLoading] = useState(false);

  // Super Admin email change state
  const [emailChangeSearch, setEmailChangeSearch] = useState('');
  const [emailChangeSearchResults, setEmailChangeSearchResults] = useState([]);
  const [emailChangeSearching, setEmailChangeSearching] = useState(false);
  const [emailChangeSelectedUser, setEmailChangeSelectedUser] = useState(null);
  const [emailChangeNewEmail, setEmailChangeNewEmail] = useState('');
  const [emailChangeReason, setEmailChangeReason] = useState('');
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailAuditLogs, setEmailAuditLogs] = useState([]);
  const [emailAuditLoading, setEmailAuditLoading] = useState(false);

  // Super Admin edit state
  const [showEditSuperUserModal, setShowEditSuperUserModal] = useState(false);
  const [editingSuperUser, setEditingSuperUser] = useState(null);
  const [superUserEditForm, setSuperUserEditForm] = useState({
    full_name: '',
    email: '',
    password: '',
    active: true
  });

  // Reports state
  const [reportConfigs, setReportConfigs] = useState([]);
  const [showCreateReportModal, setShowCreateReportModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [newReportForm, setNewReportForm] = useState({
    name: '',
    recipient_emails: [''],
    interval_months: 1,
    enabled: true,
    include_financial_data: true,
    include_growth_metrics: true,
    include_customer_details: true,
    custom_day_of_month: 1,  // Default to 1st day
    custom_month: 12  // Default to December for yearly
  });

  useEffect(() => {
    loadSuperAdminData();
  }, []);

  const loadSuperAdminData = async () => {
    try {
      console.log('🎯🎯🎯 Loading Super Admin data...');
      // Load organizations
      const orgsResponse = await axios.get(`${API_BASE}/api/organizations`);
      console.log('🎯🎯🎯 Organizations loaded:', orgsResponse.data);
      setOrganizations(orgsResponse.data);

      // Load all users
      const usersResponse = await axios.get(`${API_BASE}/api/users`);
      setAllUsers(usersResponse.data);

      // Calculate stats
      const totalOrgs = orgsResponse.data.length;
      const activeOrgs = orgsResponse.data.filter(org => org.active).length;
      const totalUsers = usersResponse.data.length;
      const superAdmins = usersResponse.data.filter(u => u.role === 'super_admin').length;

      setStats({ totalOrgs, activeOrgs, totalUsers, superAdmins });

      // Load financial statistics
      await loadFinancialStats();

      // Load privacy policy
      await loadPrivacyPolicy();

      // Load report configurations
      await loadReports();

    } catch (error) {
      console.error('Failed to load super admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load financial statistics
  const loadFinancialStats = async () => {
    try {
      console.log('🎯💰 Loading financial statistics...');
      
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.get(`${API_BASE}/api/super-admin/financial-stats`, { headers });
      
      console.log('💵 Financial stats loaded:', response.data);
      setFinancialStats(response.data);

    } catch (error) {
      console.error('Failed to load financial statistics:', error);
      // Use fallback data if API fails
      const totalOrgs = stats.totalOrgs || 0;
      setFinancialStats({
        organizations: { 
          total: totalOrgs, 
          start_plan: Math.ceil(totalOrgs * 0.7), 
          unlimited_plan: Math.floor(totalOrgs * 0.3) 
        },
        revenue: { 
          start_revenue: Math.ceil(totalOrgs * 0.7) * 299,
          unlimited_revenue: Math.floor(totalOrgs * 0.3) * 499,
          total_revenue: Math.ceil(totalOrgs * 0.7) * 299 + Math.floor(totalOrgs * 0.3) * 499
        },
        expenses: { emergent_platform: 220, amazon_ses: 50, total: 270 },
        profit: { 
          monthly_profit: (Math.ceil(totalOrgs * 0.7) * 299 + Math.floor(totalOrgs * 0.3) * 499) - 270,
          profit_margin: totalOrgs > 0 ? ((Math.ceil(totalOrgs * 0.7) * 299 + Math.floor(totalOrgs * 0.3) * 499) - 270) / (Math.ceil(totalOrgs * 0.7) * 299 + Math.floor(totalOrgs * 0.3) * 499) * 100 : 0,
          break_even_point: 1
        },
        growth: { current_orgs: totalOrgs, growth_target: 50, progress_percent: (totalOrgs / 50) * 100 }
      });
    }
  };

  // Load privacy policy
  const loadPrivacyPolicy = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/super-admin/privacy-policy`, { headers });
      setPrivacyPolicy({
        content: response.data.content || '',
        updated_at: response.data.updated_at,
        editing: false
      });
    } catch (error) {
      console.log('Privacy policy not found, will create new');
      setPrivacyPolicy({
        content: '',
        updated_at: null,
        editing: false
      });
    }
  };

  // Save privacy policy
  const savePrivacyPolicy = async () => {
    try {
      console.log('🔄 Saving privacy policy...', { content: privacyPolicy.content });
      
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.put(`${API_BASE}/api/super-admin/privacy-policy`, 
        { content: privacyPolicy.content },
        { headers }
      );

      console.log('✅ Privacy policy saved:', response.data);
      toast.success('Personvernerklæring lagret');
      setPrivacyPolicy(prev => ({ ...prev, editing: false, showPreview: false }));
      await loadPrivacyPolicy(); // Refresh data

    } catch (error) {
      console.error('❌ Save privacy policy error:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Kunne ikke lagre personvernerklæring: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Preview privacy policy in new window
  const previewPrivacyPolicyInNewWindow = () => {
    const previewWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    
    if (previewWindow) {
      const formattedContent = privacyPolicy.content || 'Ingen innhold lagt til ennå.';

      previewWindow.document.write(`
        <!DOCTYPE html>
        <html lang="no">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Forhåndsvisning - Personvernerklæring</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px; 
              line-height: 1.6; 
              color: #333; 
              background-color: #f8f9fa;
            }
            .container {
              background: white;
              padding: 40px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            h1 { color: #1E2330; text-align: center; margin-bottom: 30px; }
            h2 { color: #1E2330; margin: 30px 0 15px 0; border-bottom: 2px solid #FFD700; padding-bottom: 5px; }
            h3 { color: #1E2330; margin: 25px 0 10px 0; }
            p { margin: 15px 0; }
            ul, ol { margin: 15px 0; padding-left: 25px; }
            li { margin: 5px 0; }
            strong { color: #1E2330; }
            .header { 
              text-align: center; 
              margin-bottom: 40px; 
              border-bottom: 2px solid #FFD700; 
              padding-bottom: 20px; 
            }
            .close-btn {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #ff4444;
              color: white;
              border: none;
              border-radius: 50%;
              width: 50px;
              height: 50px;
              font-size: 24px;
              cursor: pointer;
              box-shadow: 0 2px 10px rgba(0,0,0,0.2);
              z-index: 1000;
            }
            .preview-badge {
              position: fixed;
              top: 20px;
              left: 20px;
              background: #2196f3;
              color: white;
              padding: 5px 10px;
              border-radius: 15px;
              font-size: 12px;
              z-index: 1000;
            }
          </style>
        </head>
        <body>
          <div class="preview-badge">📋 FORHÅNDSVISNING</div>
          <button class="close-btn" onclick="window.close()" title="Lukk forhåndsvisning">&times;</button>
          <div class="container">
            <div class="header">
              <h1>Personvernerklæring</h1>
              <p><strong>Aktivlogg</strong></p>
              <p>Digital treningslogg for pistolklubber og skytterlag</p>
            </div>
            
            <div class="content">
              ${formattedContent}
            </div>
            
            <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; text-align: center; color: #666; font-size: 14px;">
              <p>Forhåndsvisning generert: ${new Date().toLocaleDateString('no-NO')} kl. ${new Date().toLocaleTimeString('no-NO')}</p>
            </div>
          </div>
        </body>
        </html>
      `);
      previewWindow.document.close();
    } else {
      toast.error('Kunne ikke åpne forhåndsvisning. Tillat popup-vinduer i nettleseren.');
    }
  };

  // ============== REPORTS FUNCTIONS ==============

  const loadReports = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/reports/configs`, { headers });
      // Backend returns {configs: [...]} - extract the array
      setReportConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Failed to load report configurations:', error);
      toast.error('Kunne ikke laste rapport-konfigurasjoner');
    }
  };

  const loadReportData = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/reports/data`, { headers });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to load report data:', error);
      toast.error('Kunne ikke laste rapport-data');
    }
  };

  const createReport = async () => {
    try {
      // Validate form
      if (!newReportForm.name.trim()) {
        toast.error('Rapport-navn er påkrevd');
        return;
      }

      // Filter out empty emails
      const validEmails = newReportForm.recipient_emails.filter(email => email.trim());
      if (validEmails.length === 0) {
        toast.error('Minst én epost-adresse er påkrevd');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      const reportData = {
        ...newReportForm,
        recipient_emails: validEmails
      };

      await axios.post(`${API_BASE}/api/reports/configs`, reportData, { headers });
      
      toast.success('Rapport-konfigurasjon opprettet');
      setShowCreateReportModal(false);
      setNewReportForm({
        name: '',
        recipient_emails: [''],
        interval_months: 1,
        enabled: true,
        include_financial_data: true,
        include_growth_metrics: true,
        include_customer_details: true
      });
      await loadReports();

    } catch (error) {
      console.error('Failed to create report:', error);
      toast.error(`Kunne ikke opprette rapport: ${error.response?.data?.detail || error.message}`);
    }
  };

  const updateReport = async (reportId, updates) => {
    try {
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      await axios.put(`${API_BASE}/api/reports/configs/${reportId}`, updates, { headers });
      
      toast.success('Rapport-konfigurasjon oppdatert');
      setEditingReport(null);
      await loadReports();

    } catch (error) {
      console.error('Failed to update report:', error);
      toast.error(`Kunne ikke oppdatere rapport: ${error.response?.data?.detail || error.message}`);
    }
  };

  const deleteReport = async (reportId, reportName) => {
    if (!window.confirm(`Er du sikker på at du vil slette rapporten "${reportName}"? Dette kan ikke angres.`)) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.delete(`${API_BASE}/api/reports/configs/${reportId}`, { headers });
      
      toast.success('Rapport-konfigurasjon slettet');
      await loadReports();

    } catch (error) {
      console.error('Failed to delete report:', error);
      toast.error(`Kunne ikke slette rapport: ${error.response?.data?.detail || error.message}`);
    }
  };

  const sendTestReport = async (reportId) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/api/reports/configs/${reportId}/test`, {}, { headers });
      
      toast.success('Test-rapport sendt! Sjekk epost-innboksen din.');

    } catch (error) {
      console.error('Failed to send test report:', error);
      toast.error(`Kunne ikke sende test-rapport: ${error.response?.data?.detail || error.message}`);
    }
  };

  const addEmailField = () => {
    setNewReportForm(prev => ({
      ...prev,
      recipient_emails: [...prev.recipient_emails, '']
    }));
  };

  const removeEmailField = (index) => {
    setNewReportForm(prev => ({
      ...prev,
      recipient_emails: prev.recipient_emails.filter((_, i) => i !== index)
    }));
  };

  const updateEmailField = (index, value) => {
    setNewReportForm(prev => ({
      ...prev,
      recipient_emails: prev.recipient_emails.map((email, i) => i === index ? value : email)
    }));
  };

  // Delete super user
  const deleteSuperUser = async (userId, userName) => {
    if (!window.confirm(`Er du sikker på at du vil slette super admin "${userName}"? Dette kan ikke angres.`)) {
      return;
    }

    try {
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      await axios.delete(`${API_BASE}/api/super-admin/users/${userId}`, { headers });
      
      toast.success(`Super admin "${userName}" ble slettet`);
      await loadSuperAdminData(); // Refresh data

    } catch (error) {
      console.error('Delete super user error:', error);
      toast.error('Kunne ikke slette super admin');
    }
  };

  // Export all organization data for termination (CSV + backup ZIP)
  const handleExportOrganizationData = async (org) => {
    try {
      if (!confirm(`Er du sikker på at du vil eksportere alle data for "${org.name}"? Dette brukes ved avslutning av abonnement.`)) {
        return;
      }

      // Show loading indicator
      toast.info('Eksporterer organisasjonsdata... Dette kan ta noen minutter.', 8000);

      // Export all data as CSV files
      const response = await axios.get(`${API_BASE}/api/super-admin/organizations/${org.id}/export-all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Create and download ZIP file with all data
      const blob = new Blob([response.data], { type: 'application/zip' });
      const link = document.createElement('a');
      link.download = `${org.name.replace(/[^a-zA-Z0-9]/g, '-')}-full-export-${new Date().toISOString().split('T')[0]}.zip`;
      link.href = URL.createObjectURL(blob);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Komplett organisasjonsdata for "${org.name}" er eksportert som ZIP-fil.`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Kunne ikke eksportere organisasjonsdata. Prøv igjen.');
    }
  };

  // Create complete backup for reimport
  const handleBackupOrganization = async (org) => {
    try {
      if (!confirm(`Lag komplett backup av "${org.name}"? Dette kan brukes til reimport senere.`)) {
        return;
      }

      toast.info('Lager komplett backup... Dette kan ta noen minutter.', 8000);

      const response = await axios.get(`${API_BASE}/api/super-admin/organizations/${org.id}/backup`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const link = document.createElement('a');
      link.download = `${org.name.replace(/[^a-zA-Z0-9]/g, '-')}-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.href = URL.createObjectURL(blob);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Komplett backup av "${org.name}" er opprettet og lastet ned.`);
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error('Kunne ikke lage backup. Prøv igjen.');
    }
  };

  // Impersonate organization using AuthContext
  const handleImpersonateOrganization = async (org) => {
    try {
      console.log('🎯 Starting impersonation for:', org.name);
      
      if (!confirm(`Logg inn som "${org.name}"? Du vil se applikasjonen fra deres perspektiv.`)) {
        console.log('🎯 User cancelled impersonation');
        return;
      }

      console.log('🎯 User confirmed impersonation');
      toast.info('Starter impersonation...', 3000);

      // Use AuthContext startImpersonation function to properly update user state
      console.log('🎯 Calling startImpersonation with org.id:', org.id);
      const success = await startImpersonation(org.id);

      if (success) {
        console.log('🎯 Impersonation successful, navigating to /admin');
        toast.success(`Impersonerer nå ${org.name}`, 2000);
        
        // Navigate to admin page
        navigate('/admin');
      } else {
        console.log('🎯 Impersonation failed');
        toast.error('Kunne ikke logge inn som organisasjonen. Prøv igjen.');
      }
      
    } catch (error) {
      console.error('🎯 Impersonation failed:', error);
      toast.error('Kunne ikke logge inn som organisasjonen. Prøv igjen.');
    }
  };

  // Edit organization
  const handleEditOrganization = (org) => {
    setEditingOrg(org);
    setShowCreateOrgModal(true); // Gjenbruk samme modal for redigering
  };

  // Handle demo toggle
  const handleToggleDemo = async (org) => {
    try {
      const currentStatus = org.subscription_status;
      const isCurrentlyDemo = currentStatus === 'demo';
      
      const confirmMessage = isCurrentlyDemo 
        ? `Deaktivere demo for "${org.name}"? Organisasjonen settes til aktiv status.`
        : `Aktivere 30-dagers demo for "${org.name}"?`;
      
      if (!confirm(confirmMessage)) {
        return;
      }

      const response = await axios.post(
        `${API_BASE}/api/organizations/${org.id}/toggle-demo`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      const statusText = response.data.subscription_status === 'demo' ? 'Demo aktivert' : 'Demo deaktivert';
      toast.success(`${statusText} for "${org.name}"`);
      
      // Refresh organizations list
      loadSuperAdminData();
    } catch (error) {
      console.error('Toggle demo failed:', error);
      toast.error('Kunne ikke endre demo status. Prøv igjen.');
    }
  };

  // Handle activate permanent (for expired demos)
  const handleActivatePermanent = async (org) => {
    try {
      const confirmMessage = `Aktivere permanent abonnement for "${org.name}"?\n\nAlle data fra demo-perioden bevares.\nOrganisasjonen får full tilgang uten utløpsdato.`;
      
      if (!confirm(confirmMessage)) {
        return;
      }

      const response = await axios.put(
        `${API_BASE}/api/organizations/${org.id}`,
        {
          is_demo: false,
          demo_expires_at: null,
          subscription_status: 'active'
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success(`"${org.name}" er nå permanent aktiv! Alle data er bevart.`);
      
      // Refresh organizations list
      loadSuperAdminData();
    } catch (error) {
      console.error('Activate permanent failed:', error);
      toast.error('Kunne ikke aktivere permanent status. Prøv igjen.');
    }
  };

  // Handle backup file upload and restore
  const handleBackupUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Kun JSON backup-filer er tillatt.');
      return;
    }

    try {
      if (!confirm(`Er du sikker på at du vil importere backup-filen "${file.name}"? Dette vil opprette en ny organisasjon.`)) {
        return;
      }

      toast.info('Importerer backup... Dette kan ta flere minutter.', 10000);

      const formData = new FormData();
      formData.append('backup_file', file);

      const response = await axios.post(`${API_BASE}/api/super-admin/restore-backup`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Backup importert! Organisasjon "${response.data.organization_name}" er gjenopprettet.`);
      loadSuperAdminData(); // Refresh data
    } catch (error) {
      console.error('Backup restore failed:', error);
      toast.error('Kunne ikke importere backup. Kontroller filen og prøv igjen.');
    }

    // Reset file input
    event.target.value = '';
  };

  const restoreBackup = async () => {
    if (!restoreFile) {
      toast.error('Vennligst velg en backup-fil');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('backup_file', restoreFile);
      
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.post(`${API_BASE}/api/super-admin/restore-backup`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Backup gjenopprettet: ${response.data.organization_name}`);
      setShowUploadRestore(false);
      setRestoreFile(null);
      await loadSuperAdminData(); // Refresh data
      
    } catch (error) {
      console.error('Restore backup error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke gjenopprette backup');
    }
  };

  const createSuperUser = async () => {
    try {
      if (!newSuperUserForm.full_name || !newSuperUserForm.email || !newSuperUserForm.password) {
        toast.error('Alle felter må fylles ut');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      
      const userData = {
        full_name: newSuperUserForm.full_name,
        email: newSuperUserForm.email,
        password: newSuperUserForm.password,
        role: 'super_admin',
        approved: true,
        active: true,
        organization_id: null // Super admins don't belong to specific organizations
      };

      await axios.post(`${API_BASE}/api/super-admin/users`, userData, { headers });
      
      toast.success(`Super Admin "${newSuperUserForm.full_name}" opprettet!`);
      setShowCreateSuperUserModal(false);
      setNewSuperUserForm({ full_name: '', email: '', password: '' });
      await loadSuperAdminData(); // Refresh data
      
    } catch (error) {
      console.error('Create super user error:', error);
      const errorMessage = error.response?.data?.detail || 'Kunne ikke opprette Super Admin bruker';
      toast.error(errorMessage);
    }
  };

  // Open edit Super User modal
  const openEditSuperUser = (superUser) => {
    setEditingSuperUser(superUser);
    setSuperUserEditForm({
      full_name: superUser.full_name,
      email: superUser.email,
      password: '', // Don't pre-fill password
      active: superUser.active
    });
    setShowEditSuperUserModal(true);
  };

  // Update Super User
  const updateSuperUser = async () => {
    try {
      if (!superUserEditForm.full_name || !superUserEditForm.email) {
        toast.error('Navn og e-post må fylles ut');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      
      const updateData = {
        full_name: superUserEditForm.full_name,
        email: superUserEditForm.email,
        active: superUserEditForm.active
      };

      // Only include password if provided
      if (superUserEditForm.password && superUserEditForm.password.trim() !== '') {
        updateData.password = superUserEditForm.password;
      }

      await axios.put(`${API_BASE}/api/users/${editingSuperUser.id}`, updateData, { headers });
      
      toast.success(`Super Admin "${superUserEditForm.full_name}" oppdatert!`);
      setShowEditSuperUserModal(false);
      setEditingSuperUser(null);
      setSuperUserEditForm({ full_name: '', email: '', password: '', active: true });
      await loadSuperAdminData(); // Refresh data
      
    } catch (error) {
      console.error('Update super user error:', error);
      const errorMessage = error.response?.data?.detail || 'Kunne ikke oppdatere Super Admin bruker';
      toast.error(errorMessage);
    }
  };

  // Delete organization with confirmation
  const handleDeleteOrganization = async (org) => {
    try {
      if (!confirm(`Er du SIKKER på at du vil slette "${org.name}"? Dette kan ikke angres!`)) {
        return;
      }

      const doubleConfirm = prompt(`Skriv organisasjonens navn "${org.name}" for å bekrefte sletting:`);
      if (doubleConfirm !== org.name) {
        toast.warning('Feil navn. Sletting avbrutt.');
        return;
      }

      await axios.delete(`${API_BASE}/api/super-admin/organizations/${org.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success(`Organisasjonen "${org.name}" er slettet.`);
      loadSuperAdminData(); // Refresh data
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Kunne ikke slette organisasjonen. Prøv igjen.');
    }
  };

  const CreateOrgModal = ({ onClose, onCreate }) => {
    const [formData, setFormData] = useState({
      name: editingOrg?.name || '',
      slug: editingOrg?.slug || '',
      description: editingOrg?.description || '',
      admin_email: editingOrg?.admin_email || '',
      admin_full_name: editingOrg?.admin_full_name || '',
      admin_password: '' // Password for both CREATE and EDIT modes
    });
    const [orgAdminUser, setOrgAdminUser] = useState(null);
    const [loadingAdmin, setLoadingAdmin] = useState(false);
    const [emailCheck, setEmailCheck] = useState({ checking: false, exists: false, clubs: [], userName: '' });

    // Debounced live email lookup — disables password field when user already exists in another club
    useEffect(() => {
      if (editingOrg) return undefined; // Only check for NEW org creation
      const email = (formData.admin_email || '').trim().toLowerCase();
      if (!email || !email.includes('@') || email.length < 5) {
        setEmailCheck({ checking: false, exists: false, clubs: [], userName: '' });
        return undefined;
      }
      const timer = setTimeout(async () => {
        try {
          setEmailCheck((prev) => ({ ...prev, checking: true }));
          const res = await axios.get(`${API_BASE}/api/admin/lookup-email`, {
            params: { email },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          const clubs = (res.data?.clubs || []);
          setEmailCheck({
            checking: false,
            exists: !!res.data?.exists,
            clubs,
            userName: res.data?.user_full_name || '',
          });
          if (res.data?.exists) {
            // Clear password when linking (backend ignores it, but avoid confusion)
            setFormData((prev) => (prev.admin_password ? { ...prev, admin_password: '' } : prev));
          }
        } catch (e) {
          setEmailCheck({ checking: false, exists: false, clubs: [], userName: '' });
        }
      }, 500);
      return () => clearTimeout(timer);
    }, [formData.admin_email, editingOrg]);

    // Load org admin when editing
    useEffect(() => {
      if (editingOrg) {
        loadOrgAdmin();
      }
    }, [editingOrg]);

    const loadOrgAdmin = async () => {
      try {
        setLoadingAdmin(true);
        const response = await axios.get(`${API_BASE}/api/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        // Find org_admin for this organization
        const admin = response.data.find(u => 
          u.role === 'org_admin' && 
          u.organization_id === editingOrg.id
        );
        setOrgAdminUser(admin);
      } catch (error) {
        console.error('Failed to load org admin:', error);
      } finally {
        setLoadingAdmin(false);
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (editingOrg) {
          // Update existing organization
          await axios.put(`${API_BASE}/api/organizations/${editingOrg.id}`, formData);
          
          // Update admin password if provided
          if (formData.admin_password && formData.admin_password.trim() !== '' && orgAdminUser) {
            await axios.put(
              `${API_BASE}/api/users/${orgAdminUser.id}`,
              { password: formData.admin_password },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            toast.success(`Organisasjon oppdatert og admin passord endret!`);
          } else {
            toast.success(`Organisasjonen "${formData.name}" er oppdatert.`);
          }
        } else {
          // Create new organization - INCLUDE PASSWORD!
          const res = await axios.post(`${API_BASE}/api/organizations`, formData);
          if (res.data?.linked_existing_user) {
            toast.success(
              `Organisasjonen "${formData.name}" er opprettet og knyttet til den eksisterende brukeren ${formData.admin_email}. ` +
              `De kan logge inn med sitt eksisterende passord og vil kunne bytte mellom klubbene i menyen.`,
              { duration: 8000 }
            );
          } else {
            toast.success(`Organisasjonen "${formData.name}" er opprettet! Admin kan nå logge inn med e-post og passord.`, {
              duration: 6000
            });
          }
        }
        onCreate();
        onClose();
      } catch (error) {
        const errorMsg = error.response?.data?.detail || `Kunne ikke ${editingOrg ? 'oppdatere' : 'opprette'} organisasjon`;
        toast.error(errorMsg);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold text-white mb-4">
            {editingOrg ? 'Rediger organisasjon' : 'Opprett ny organisasjon'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Organisasjonsnavn
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  // Auto-generer slug fra navnet
                  const autoSlug = newName
                    .toLowerCase()
                    .replace(/æ/g, 'ae')
                    .replace(/ø/g, 'o')
                    .replace(/å/g, 'a')
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/--+/g, '-')
                    .replace(/^-|-$/g, '');
                  
                  setFormData({...formData, name: newName, slug: autoSlug});
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Slug (URL-vennlig navn)
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Beskrivelse
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                rows="3"
              />
            </div>
            
            <hr className="border-gray-600" />
            <h3 className="text-lg font-medium text-white">Administrator</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Admin navn
              </label>
              <input
                type="text"
                value={formData.admin_full_name}
                onChange={(e) => setFormData({...formData, admin_full_name: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Admin e-post
              </label>
              <input
                type="email"
                value={formData.admin_email}
                onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                required={!editingOrg}
                disabled={editingOrg}
                data-testid="create-org-admin-email"
              />
              {editingOrg && (
                <p className="text-xs text-gray-400 mt-1">
                  E-post kan ikke endres etter opprettelse
                </p>
              )}
              {!editingOrg && emailCheck.checking && (
                <p className="text-xs text-gray-400 mt-1">Sjekker om e-post finnes fra før…</p>
              )}
              {!editingOrg && emailCheck.exists && (
                <div
                  className="mt-2 p-3 rounded-lg border border-yellow-500/50 bg-yellow-500/10 text-yellow-200 text-xs"
                  data-testid="linking-existing-user-banner"
                >
                  <div className="font-bold text-yellow-300 mb-1">
                    🔗 Denne brukeren finnes fra før
                    {emailCheck.userName ? ` — ${emailCheck.userName}` : ''}
                  </div>
                  <div>
                    Er allerede medlem i:{' '}
                    <strong>{emailCheck.clubs.map((c) => c.organization_name).filter(Boolean).join(', ')}</strong>.
                    Passordet gjenbrukes automatisk — brukeren logger inn med sitt eksisterende passord og kan bytte klubb i menyen.
                  </div>
                </div>
              )}
            </div>

            {/* Password field for both CREATE and EDIT modes */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {editingOrg ? (
                  <>Sett nytt passord for admin <span className="text-gray-500">(valgfritt)</span></>
                ) : emailCheck.exists ? (
                  <>Admin passord <span className="text-gray-500">(gjenbrukes fra eksisterende bruker)</span></>
                ) : (
                  <>Admin passord <span className="text-red-400">*</span></>
                )}
              </label>
              <input
                type="password"
                value={formData.admin_password}
                onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white ${(!editingOrg && emailCheck.exists) ? 'opacity-50 cursor-not-allowed' : ''}`}
                placeholder={(!editingOrg && emailCheck.exists) ? 'Gjenbrukes fra eksisterende bruker' : '••••••••'}
                minLength="6"
                required={!editingOrg && !emailCheck.exists}
                disabled={!editingOrg && emailCheck.exists}
                data-testid="create-org-admin-password"
              />
              {editingOrg ? (
                <>
                  {loadingAdmin ? (
                    <p className="text-xs text-gray-400 mt-1">Laster admin info...</p>
                  ) : orgAdminUser ? (
                    <p className="text-xs text-green-400 mt-1">
                      ✓ Admin: {orgAdminUser.full_name} ({orgAdminUser.email})
                    </p>
                  ) : (
                    <p className="text-xs text-yellow-400 mt-1">
                      ⚠️ Ingen admin funnet for denne organisasjonen
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    La stå tomt for å beholde nåværende passord
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-400 mt-1">
                  Admin kan logge inn med e-post og dette passordet
                </p>
              )}
            </div>

            
            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Avbryt
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded hover:bg-yellow-400"
              >
                Opprett
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Run email verification migration
  const runEmailVerificationMigration = async () => {
    if (!window.confirm('Er du sikker på at du vil kjøre email_verified migreringen? Dette vil sette email_verified=true for alle brukere.')) {
      return;
    }

    setMigrationLoading(true);
    setMigrationResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/fix-email-verified`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setMigrationResult(response.data);
      
      if (response.data.success) {
        toast.success(`✅ Migrering fullført! ${response.data.verified_users} brukere verifisert.`);
      } else {
        toast.warning(`⚠️ Migrering fullført med advarsler. Sjekk resultatet.`);
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('❌ Migrering feilet: ' + (error.response?.data?.detail || error.message));
      setMigrationResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setMigrationLoading(false);
    }
  };

  // Run email template migration
  const runEmailTemplateMigration = async () => {
    if (!window.confirm('Er du sikker på at du vil oppdatere e-postmalene? Dette vil kopiere fungerende mal-struktur til "Medlemskap godkjent"-malen.')) {
      return;
    }

    setTemplateMigrationLoading(true);
    setTemplateMigrationResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/fix-email-templates`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setTemplateMigrationResult(response.data);
      
      if (response.data.success) {
        toast.success(`✅ E-postmaler oppdatert! ${response.data.templates_updated} mal(er) fikset.`);
      } else {
        toast.warning(`⚠️ Noe gikk galt. Sjekk resultatet.`);
      }
    } catch (error) {
      console.error('Template migration error:', error);
      toast.error('❌ Mal-oppdatering feilet: ' + (error.response?.data?.detail || error.message));
      setTemplateMigrationResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setTemplateMigrationLoading(false);
    }
  };

  // Send test email for diagnostics
  const sendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast.error('⚠️ Vennligst skriv inn en gyldig e-postadresse');
      return;
    }

    setTestEmailLoading(true);
    setTestEmailResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/send-test-email`,
        { email: testEmailAddress },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setTestEmailResult(response.data);
      
      if (response.data.success) {
        toast.success(`✅ Test e-post sendt til ${testEmailAddress}!`);
      } else {
        toast.warning(`⚠️ E-post sending feilet. Sjekk resultatet.`);
      }
    } catch (error) {
      console.error('Test email error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.error || error.message;
      
      toast.error('❌ Test e-post feilet: ' + errorMsg);
      setTestEmailResult({ 
        error: errorMsg,
        error_type: errorDetail?.error_type || 'Unknown',
        success: false 
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  // Migrate birthdates to DD.MM.YYYY format
  const migrateBirthdates = async () => {
    setBirthdateMigrationLoading(true);
    setBirthdateMigrationResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-birthdates`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setBirthdateMigrationResult(response.data);
      
      const { updated, skipped, errors, total_users } = response.data;
      if (updated === 0 && errors === 0) {
        toast.success(`✅ Alle ${total_users} fødselsdatoer er allerede i korrekt format!`);
      } else if (updated > 0 && errors === 0) {
        toast.success(`✅ Migrering fullført! ${updated} fødselsdatoer oppdatert, ${skipped} hoppet over.`);
      } else if (errors > 0) {
        toast.warning(`⚠️ Migrering fullført med ${errors} feil. ${updated} oppdatert, ${skipped} hoppet over.`);
      }
    } catch (error) {
      console.error('Birthdate migration error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('❌ Migrering feilet: ' + errorMsg);
      setBirthdateMigrationResult({ error: errorMsg, total_users: 0, updated: 0, skipped: 0, errors: 1 });
    } finally {
      setBirthdateMigrationLoading(false);
    }
  };


  // Migrate profile images to Cloudinary
  const migrateProfileImages = async () => {
    setProfileImageMigrationLoading(true);
    setProfileImageMigrationResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-profile-images`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setProfileImageMigrationResult(response.data);
      
      const { migrated, failed, total_users } = response.data;
      if (migrated === 0 && failed === 0) {
        toast.success(`✅ Ingen profilbilder å migrere - alle bruker allerede Cloudinary!`);
      } else if (migrated > 0 && failed === 0) {
        toast.success(`✅ Migrering fullført! ${migrated} profilbilder migrert til Cloudinary.`);
      } else if (failed > 0) {
        toast.warning(`⚠️ Migrering fullført med ${failed} feil. ${migrated} migrert av ${total_users}.`);
      }
    } catch (error) {
      console.error('Profile image migration error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('❌ Migrering feilet: ' + errorMsg);
      setProfileImageMigrationResult({ error: errorMsg, total_users: 0, migrated: 0, failed: 1 });
    } finally {
      setProfileImageMigrationLoading(false);
    }
  };


  // Migrate training diary images to Cloudinary
  const migrateTrainingImages = async () => {
    setTrainingImageMigrationLoading(true);
    setTrainingImageMigrationResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-training-images`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setTrainingImageMigrationResult(response.data);
      
      const { images_migrated, images_failed, sessions_updated } = response.data;
      if (images_migrated === 0 && images_failed === 0) {
        toast.success(`✅ Ingen treningsbilder å migrere - alle bruker allerede Cloudinary!`);
      } else if (images_migrated > 0 && images_failed === 0) {
        toast.success(`✅ Migrering fullført! ${images_migrated} treningsbilder migrert til Cloudinary.`);
      } else if (images_failed > 0) {
        toast.warning(`⚠️ Migrering fullført med ${images_failed} feil. ${images_migrated} migrert.`);
      }
    } catch (error) {
      console.error('Training image migration error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('❌ Migrering feilet: ' + errorMsg);
      setTrainingImageMigrationResult({ error: errorMsg, images_migrated: 0, images_failed: 1 });
    } finally {
      setTrainingImageMigrationLoading(false);
    }
  };

  // Diagnose pending members discrepancy
  const diagnosePendingMembers = async () => {
    setPendingDiagnoseLoading(true);
    setPendingDiagnoseResult(null);

    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/diagnose-pending-members`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setPendingDiagnoseResult(response.data);
      
      const oldCount = response.data.summary.old_pending;
      if (oldCount === 0) {
        toast.success('✅ Ingen gamle medlemmer funnet! Systemet er rent.');
      } else {
        toast.warning(`⚠️ Fant ${oldCount} gamle medlemmer (>30 dager) som kan forårsake avvik.`);
      }
    } catch (error) {
      console.error('Diagnose pending members error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Diagnose feilet: ' + errorMsg);
      setPendingDiagnoseResult({ error: errorMsg });
    } finally {
      setPendingDiagnoseLoading(false);
    }
  };

  // Fix old pending members by deactivating them
  const fixOldPendingMembers = async () => {
    if (!pendingDiagnoseResult || pendingDiagnoseResult.summary.old_pending === 0) {
      toast.error('⚠️ Kjør diagnose først for å se hva som skal fikses!');
      return;
    }

    const confirmed = window.confirm(
      `Dette vil deaktivere ${pendingDiagnoseResult.summary.old_pending} gamle medlemmer (>30 dager) som venter på godkjenning.\n\n` +
      `✅ BESKYTTELSE: Medlemmer som har treningsøkter (aktiv bruk) vil IKKE bli deaktivert!\n\n` +
      `De vil ikke lenger telles i "Venter godkjenning" på Dashboard.\n\n` +
      `Er du sikker på at du vil fortsette?`
    );

    if (!confirmed) return;

    setPendingFixLoading(true);
    setPendingFixResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/fix-pending-members`,
        { action: 'deactivate' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setPendingFixResult(response.data);
      
      const fixedCount = response.data.total_fixed;
      const skippedCount = response.data.total_skipped || 0;
      
      if (skippedCount > 0) {
        toast.success(`✅ Deaktiverte ${fixedCount} gamle medlemmer! ${skippedCount} medlemmer med aktivitet ble hoppet over.`, {
          duration: 6000
        });
      } else {
        toast.success(`✅ Deaktiverte ${fixedCount} gamle medlemmer! Dashboard-telling skal nå stemme.`);
      }
      
      // Clear diagnose result so user can run again
      setPendingDiagnoseResult(null);
      
    } catch (error) {
      console.error('Fix pending members error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Fix feilet: ' + errorMsg);
      setPendingFixResult({ error: errorMsg });
    } finally {
      setPendingFixLoading(false);
    }
  };

  // Diagnose ghost members (approved=false)
  const diagnoseGhostMembers = async () => {
    setGhostDiagnoseLoading(true);
    setGhostDiagnoseResult(null);
    setGhostCleanupResult(null); // Clear previous cleanup result
    setSelectedGhostEmails([]); // Clear previous selections

    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/diagnose-ghost-members`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setGhostDiagnoseResult(response.data);
      
      const ghostCount = response.data.total_ghosts;
      if (ghostCount === 0) {
        toast.success('✅ Ingen ghost-medlemmer funnet! Databasen er ren.');
      } else {
        toast.warning(`⚠️ Fant ${ghostCount} ghost-medlemmer (approved=false) som kan slettes.`);
      }
    } catch (error) {
      console.error('Diagnose ghost members error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Diagnose feilet: ' + errorMsg);
      setGhostDiagnoseResult({ error: errorMsg });
    } finally {
      setGhostDiagnoseLoading(false);
    }
  };

  // Toggle individual ghost member selection
  const toggleGhostSelection = (email) => {
    setSelectedGhostEmails(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  // Select all ghost members
  const selectAllGhosts = () => {
    if (ghostDiagnoseResult && ghostDiagnoseResult.ghost_details) {
      const allEmails = ghostDiagnoseResult.ghost_details.map(ghost => ghost.email);
      setSelectedGhostEmails(allEmails);
    }
  };

  // Deselect all ghost members
  const deselectAllGhosts = () => {
    setSelectedGhostEmails([]);
  };

  // Cleanup (delete) selected ghost members permanently
  const cleanupGhostMembers = async () => {
    if (!ghostDiagnoseResult || ghostDiagnoseResult.total_ghosts === 0) {
      toast.error('⚠️ Kjør diagnose først for å se hva som skal slettes!');
      return;
    }

    if (selectedGhostEmails.length === 0) {
      toast.error('⚠️ Velg minst ett ghost-medlem for sletting!');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ PERMANENT SLETTING ⚠️\n\n` +
      `Dette vil PERMANENT slette ${selectedGhostEmails.length} valgte ghost-medlemmer.\n\n` +
      `Disse er medlemmer med approved=false som fortsatt er aktive.\n\n` +
      `DENNE HANDLINGEN KAN IKKE ANGRES!\n\n` +
      `Er du helt sikker på at du vil fortsette?`
    );

    if (!confirmed) return;

    setGhostCleanupLoading(true);
    setGhostCleanupResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/cleanup-ghost-members`,
        { emails: selectedGhostEmails },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setGhostCleanupResult(response.data);
      
      const deletedCount = response.data.deleted_count;
      toast.success(`✅ Slettet ${deletedCount} ghost-medlemmer permanent! Dashboard oppdateres.`, {
        duration: 6000
      });
      
      // Clear selections and diagnose result so user can run again
      setSelectedGhostEmails([]);
      setGhostDiagnoseResult(null);
      
    } catch (error) {
      console.error('Cleanup ghost members error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Cleanup feilet: ' + errorMsg);
      setGhostCleanupResult({ error: errorMsg });
    } finally {
      setGhostCleanupLoading(false);
    }
  };

  // Cleanup banners for organization
  const cleanupBanners = async () => {
    if (!bannerCleanupOrgId) {
      toast.error('Velg en organisasjon først');
      return;
    }

    const confirmed = window.confirm(
      'Er du sikker på at du vil fjerne ALLE salgsbannere for denne organisasjonen?\n\n' +
      'Dette vil:\n' +
      '• Fjerne alle banner-URLer fra databasen\n' +
      '• Rydde opp ødelagte/gamle bannere\n\n' +
      'Filer i S3/Cloudinary slettes IKKE. Du kan laste opp nye bannere etterpå.'
    );

    if (!confirmed) return;

    setBannerCleanupLoading(true);
    setBannerCleanupResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/cleanup-banners`,
        { organization_id: bannerCleanupOrgId },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        setBannerCleanupResult(response.data);
      } else {
        toast.error('Banner-opprydding feilet');
      }
    } catch (error) {
      console.error('Banner cleanup error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('❌ Feil: ' + errorMsg);
      setBannerCleanupResult({ error: errorMsg });
    } finally {
      setBannerCleanupLoading(false);
    }
  };

  // Migrate profile images from Cloudinary to S3
  const migrateProfileImagesToS3 = async () => {
    if (!profileS3DryRun) {
      const confirmed = window.confirm(
        'Er du sikker på at du vil migrere profilbilder til S3?\n\n' +
        'Dette vil:\n' +
        '• Laste ned bilder fra Cloudinary\n' +
        '• Laste opp til AWS S3 via CloudFront\n' +
        '• Oppdatere database med nye S3 URL-er\n\n' +
        'Cloudinary-bildene blir IKKE slettet.'
      );
      if (!confirmed) return;
    }

    setProfileS3MigrationLoading(true);
    setProfileS3MigrationResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-profile-images-to-s3`,
        { 
          organization_id: profileS3MigrationOrgId || undefined,
          dry_run: profileS3DryRun
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data) {
        if (profileS3DryRun) {
          toast.info(`📊 Fant ${response.data.total_users} brukere å migrere`);
        } else {
          toast.success(`✅ Migrert ${response.data.migrated} profilbilder til S3`);
        }
        setProfileS3MigrationResult(response.data);
      }
    } catch (error) {
      console.error('Profile S3 migration error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('❌ Feil: ' + errorMsg);
      setProfileS3MigrationResult({ error: errorMsg });
    } finally {
      setProfileS3MigrationLoading(false);
    }
  };

  // Fetch S3 migration status
  const fetchS3MigrationStatus = async () => {
    setS3MigrationStatusLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/s3-migration-status`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setS3MigrationStatus(response.data);
    } catch (error) {
      console.error('S3 migration status error:', error);
      toast.error('Kunne ikke hente migrasjonsstatus');
    } finally {
      setS3MigrationStatusLoading(false);
    }
  };

  // Migrate user documents to S3
  const migrateUserDocsToS3 = async () => {
    const confirmMsg = userDocsS3DryRun 
      ? 'Telle dokumenter som kan migreres?' 
      : 'VIKTIG: Dette vil migrere dokumenter til S3.\n\nCloudinary-dokumentene blir IKKE slettet.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setUserDocsS3Loading(true);
    setUserDocsS3Result(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-user-documents-to-s3`,
        { 
          document_type: userDocsS3Type,
          dry_run: userDocsS3DryRun 
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setUserDocsS3Result(response.data);
      if (userDocsS3DryRun) {
        toast.info('📋 Telling fullført');
      } else {
        toast.success('✅ Migrering fullført!');
      }
    } catch (error) {
      console.error('User docs S3 migration error:', error);
      toast.error('❌ Feil: ' + (error.response?.data?.detail || error.message));
      setUserDocsS3Result({ error: error.response?.data?.detail || error.message });
    } finally {
      setUserDocsS3Loading(false);
    }
  };

  // Migrate training images to S3
  const migrateTrainingImagesToS3 = async () => {
    const confirmMsg = trainingImagesS3DryRun 
      ? 'Telle treningsbilder som kan migreres?' 
      : 'VIKTIG: Dette vil migrere treningsbilder til S3.\n\nCloudinary-bildene blir IKKE slettet.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setTrainingImagesS3Loading(true);
    setTrainingImagesS3Result(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-training-images-to-s3`,
        { dry_run: trainingImagesS3DryRun },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setTrainingImagesS3Result(response.data);
      if (trainingImagesS3DryRun) {
        toast.info('📋 Telling fullført');
      } else {
        toast.success('✅ Migrering fullført!');
      }
    } catch (error) {
      console.error('Training images S3 migration error:', error);
      toast.error('❌ Feil: ' + (error.response?.data?.detail || error.message));
      setTrainingImagesS3Result({ error: error.response?.data?.detail || error.message });
    } finally {
      setTrainingImagesS3Loading(false);
    }
  };

  // Migrate org logos to S3
  const migrateOrgLogosToS3 = async () => {
    const confirmMsg = orgLogosS3DryRun 
      ? 'Telle logoer som kan migreres?' 
      : 'VIKTIG: Dette vil migrere organisasjonslogoer til S3.\n\nCloudinary-logoene blir IKKE slettet.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setOrgLogosS3Loading(true);
    setOrgLogosS3Result(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/migrate-org-logos-to-s3`,
        { dry_run: orgLogosS3DryRun },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setOrgLogosS3Result(response.data);
      if (orgLogosS3DryRun) {
        toast.info('📋 Telling fullført');
      } else {
        toast.success('✅ Migrering fullført!');
      }
    } catch (error) {
      console.error('Org logos S3 migration error:', error);
      toast.error('❌ Feil: ' + (error.response?.data?.detail || error.message));
      setOrgLogosS3Result({ error: error.response?.data?.detail || error.message });
    } finally {
      setOrgLogosS3Loading(false);
    }
  };

  // Diagnose document paths (read-only)
  const handleDiagnoseDocumentPaths = async () => {
    setDiagnoseDocPathsLoading(true);
    setDiagnoseDocPathsResult(null);

    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/diagnose-document-paths`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setDiagnoseDocPathsResult(response.data);
        if (response.data.stats.users_with_issues === 0) {
          toast.success('✅ Ingen problemer funnet!');
        } else {
          toast.info(`📋 Fant ${response.data.stats.users_with_issues} brukere med problemer`);
        }
      }
    } catch (error) {
      console.error('Diagnose document paths error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Kunne ikke diagnostisere';
      toast.error(`Feil: ${errorMsg}`);
      setDiagnoseDocPathsResult({
        success: false,
        error: errorMsg
      });
    } finally {
      setDiagnoseDocPathsLoading(false);
    }
  };

  // List all organizations
  const handleListOrganizations = async () => {
    setOrgsLoading(true);
    
    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/list-organizations`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setOrganizationsList(response.data.organizations);
      }
    } catch (error) {
      console.error('List organizations error:', error);
      toast.error('Kunne ikke hente organisasjoner');
    } finally {
      setOrgsLoading(false);
    }
  };

  // Load organizations on mount
  useEffect(() => {
    handleListOrganizations();
  }, []);

  // Discover documents on disk
  const handleDiscoverDocuments = async () => {
    if (!selectedOrgId) {
      toast.error('Vennligst velg en organisasjon først!');
      return;
    }
    
    setDiscoverDocsLoading(true);
    setDiscoverDocsResult(null);
    setSelectedUserIds([]);  // Reset user selection when scanning new org

    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/discover-documents?organization_id=${selectedOrgId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setDiscoverDocsResult(response.data);
        toast.success(`✅ Funnet ${response.data.total_files_on_disk} filer for ${response.data.users_with_files} brukere`);
      }
    } catch (error) {
      console.error('Discover documents error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Kunne ikke scanne dokumenter';
      toast.error(`Feil: ${errorMsg}`);
      setDiscoverDocsResult({
        success: false,
        error: errorMsg
      });
    } finally {
      setDiscoverDocsLoading(false);
    }
  };

  // List available backups
  const handleListBackups = async () => {
    setBackupsLoading(true);
    
    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/list-backups`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setBackupsList(response.data.backups);
        if (response.data.backups.length === 0) {
          toast.info('Ingen backups funnet ennå');
        } else {
          toast.success(`Funnet ${response.data.backups.length} backup-filer`);
        }
      }
    } catch (error) {
      console.error('List backups error:', error);
      toast.error('Kunne ikke hente backup-liste');
    } finally {
      setBackupsLoading(false);
    }
  };

  // Auto-relink documents based on discovery
  const handleAutoRelinkDocuments = async (dryRun = true) => {
    const userCount = selectedUserIds.length > 0 ? selectedUserIds.length : 'ALLE';
    const confirmMessage = dryRun 
      ? `Dette vil vise hvilke filer som vil bli re-linket for ${userCount} bruker(e) (DRY-RUN - ingen faktiske endringer).\n\nFortsette?`
      : `⚠️ ADVARSEL: Dette vil gjøre FAKTISKE endringer for ${userCount} bruker(e) i databasen!\n\nEn backup vil bli tatt automatisk.\n\nEr du SIKKER på at du vil fortsette?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setAutoRelinkLoading(true);
    setAutoRelinkResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/auto-relink-documents?dry_run=${dryRun}`,
        {
          user_ids: selectedUserIds.length > 0 ? selectedUserIds : null,
          organization_id: selectedOrgId || null
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setAutoRelinkResult(response.data);
        
        if (dryRun) {
          toast.success(`📋 Dry-run: Ville linket ${response.data.stats.files_linked} filer for ${response.data.stats.users_updated} brukere`);
        } else {
          toast.success(`✅ Linket ${response.data.stats.files_linked} filer for ${response.data.stats.users_updated} brukere!`);
          // Refresh backups list after actual run
          handleListBackups();
        }
      } else {
        toast.error(response.data.message || 'Re-linking feilet');
      }
    } catch (error) {
      console.error('Auto-relink error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Kunne ikke re-linke dokumenter';
      toast.error(`Feil: ${errorMsg}`);
      setAutoRelinkResult({
        success: false,
        error: errorMsg
      });
    } finally {
      setAutoRelinkLoading(false);
    }
  };

  // Fix document paths with dry-run option
  const handleFixDocumentPaths = async (dryRun = true) => {
    const confirmMessage = dryRun 
      ? 'Dette vil vise hva som kommer til å endres (DRY-RUN - ingen faktiske endringer).\n\nFortsette?'
      : '⚠️ ADVARSEL: Dette vil gjøre FAKTISKE endringer i databasen!\n\nEn backup vil bli tatt automatisk.\n\nEr du SIKKER på at du vil fortsette?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setFixDocPathsLoading(true);
    setFixDocPathsResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/fix-document-paths?dry_run=${dryRun}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setFixDocPathsResult(response.data);
        
        if (dryRun) {
          toast.success(`📋 Dry-run: Ville fikse ${response.data.changes.length} brukere`);
        } else {
          toast.success(`✅ Fikset ${response.data.changes.length} brukere (backup: ${response.data.backup?.filename})`);
        }
      } else {
        toast.error(response.data.message || 'Fikset feilet');
      }
    } catch (error) {
      console.error('Fix document paths error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Kunne ikke fikse dokumentpaths';
      toast.error(`Feil: ${errorMsg}`);
      setFixDocPathsResult({
        success: false,
        error: errorMsg
      });
    } finally {
      setFixDocPathsLoading(false);
    }
  };

  // Find unverified users
  const findUnverifiedUsers = async () => {
    setUnverifiedLoading(true);
    setUnverifiedResult(null);
    setResendResult(null);
    setSelectedUnverifiedEmails([]);

    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/find-unverified-users`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setUnverifiedResult(response.data);
      
      const unverifiedCount = response.data.total_unverified;
      if (unverifiedCount === 0) {
        toast.success('✅ Ingen uverifiserte brukere funnet!');
      } else {
        toast.warning(`⚠️ Fant ${unverifiedCount} brukere med uverifisert e-post.`);
      }
    } catch (error) {
      console.error('Find unverified users error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Søk feilet: ' + errorMsg);
      setUnverifiedResult({ error: errorMsg });
    } finally {
      setUnverifiedLoading(false);
    }
  };

  // Toggle individual unverified user selection
  const toggleUnverifiedSelection = (email) => {
    setSelectedUnverifiedEmails(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  // Select all unverified users
  const selectAllUnverified = () => {
    if (unverifiedResult && unverifiedResult.user_details) {
      const allEmails = unverifiedResult.user_details.map(user => user.email);
      setSelectedUnverifiedEmails(allEmails);
    }
  };

  // Deselect all unverified users
  const deselectAllUnverified = () => {
    setSelectedUnverifiedEmails([]);
  };

  // Resend verification emails to selected users
  const resendVerificationEmails = async () => {
    if (!unverifiedResult || unverifiedResult.total_unverified === 0) {
      toast.error('⚠️ Kjør søk først for å finne brukere!');
      return;
    }

    if (selectedUnverifiedEmails.length === 0) {
      toast.error('⚠️ Velg minst én bruker for å sende e-post!');
      return;
    }

    const confirmed = window.confirm(
      `📧 SEND VERIFIKASJONS-E-POST\n\n` +
      `Dette vil sende ny verifikasjons-e-post til ${selectedUnverifiedEmails.length} valgte brukere.\n\n` +
      `E-postene vil inneholde nye verifikasjonslenker som utløper om 24 timer.\n\n` +
      `Er du sikker på at du vil fortsette?`
    );

    if (!confirmed) return;

    setResendLoading(true);
    setResendResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/resend-verification-emails`,
        { emails: selectedUnverifiedEmails },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setResendResult(response.data);
      
      const sentCount = response.data.sent_count;
      const failedCount = response.data.failed_count;
      
      if (failedCount > 0) {
        toast.success(`✅ Sendt ${sentCount} e-poster! ${failedCount} feilet.`, {
          duration: 6000
        });
      } else {
        toast.success(`✅ Sendt ${sentCount} verifikasjons-e-poster!`);
      }
      
      // Clear selections and result so user can run again
      setSelectedUnverifiedEmails([]);
      setUnverifiedResult(null);
      
    } catch (error) {
      console.error('Resend verification emails error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Sending feilet: ' + errorMsg);
      setResendResult({ error: errorMsg });
    } finally {
      setResendLoading(false);
    }
  };

  // Toggle pending member selection
  const togglePendingSelection = (email) => {
    setSelectedPendingEmails(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  // Select all pending
  const selectAllPending = () => {
    if (pendingDiagnoseResult && pendingDiagnoseResult.pending_members) {
      const allEmails = pendingDiagnoseResult.pending_members.map(m => m.email);
      setSelectedPendingEmails(allEmails);
    }
  };

  // Deselect all pending
  const deselectAllPending = () => {
    setSelectedPendingEmails([]);
  };

  // Fix selected pending members
  const fixSelectedPendingMembers = async () => {
    if (selectedPendingEmails.length === 0) {
      toast.error('⚠️ Velg minst ett medlem for godkjenning!');
      return;
    }

    const confirmed = window.confirm(
      `✅ GODKJENN MEDLEMMER\n\n` +
      `Dette vil sette approved=true for ${selectedPendingEmails.length} valgte medlemmer.\n\n` +
      `Medlemmene vil da ikke lenger vises som "Venter godkjenning".\n\n` +
      `Er du sikker?`
    );

    if (!confirmed) return;

    setFixPendingLoading(true);
    setFixPendingResult(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/fix-pending-members-selected`,
        { emails: selectedPendingEmails },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setFixPendingResult(response.data);
      
      const fixedCount = response.data.fixed_count;
      toast.success(`✅ Godkjente ${fixedCount} medlemmer!`);
      
      // Clear selections and diagnose result
      setSelectedPendingEmails([]);
      setPendingDiagnoseResult(null);
      
    } catch (error) {
      console.error('Fix pending members error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Godkjenning feilet: ' + errorMsg);
      setFixPendingResult({ error: errorMsg });
    } finally {
      setFixPendingLoading(false);
    }
  };

  // Filter users based on search query and status checkboxes
  const filterUsers = (users) => {
    let filtered = users;
    
    // Status filter - if any checkbox is selected, apply filters
    const hasActiveFilters = Object.values(userStatusFilters).some(v => v);
    
    if (hasActiveFilters) {
      filtered = filtered.filter(user => {
        // If multiple checkboxes are selected, user must match at least one condition (OR logic)
        let matches = false;
        
        if (userStatusFilters.inactive && user.active === false) {
          matches = true;
        }
        if (userStatusFilters.not_approved && user.approved === false) {
          matches = true;
        }
        if (userStatusFilters.not_verified && user.email_verified === false) {
          matches = true;
        }
        if (userStatusFilters.deleted && user.is_deleted === true) {
          matches = true;
        }
        if (userStatusFilters.locked) {
          // Check if user is locked
          const isLocked = lockedAccounts.some(lockout => lockout.email === user.email);
          console.log(`Checking lock status for ${user.email}:`, isLocked, 'lockedAccounts:', lockedAccounts.map(l => l.email));
          if (isLocked) {
            matches = true;
          }
        }
        
        return matches;
      });
    }
    
    // Search query filter
    if (!userSearchQuery.trim()) return filtered;
    
    const query = userSearchQuery.toLowerCase();
    return filtered.filter(user => 
      user.email.toLowerCase().includes(query) ||
      user.name.toLowerCase().includes(query)
    );
  };

  // Restore soft-deleted user: back to Deaktivert or Aktiv status
  const restoreDeletedUser = async (userId, email, name, targetStatus) => {
    if (!userId) {
      toast.error('Kan ikke gjenopprette: Mangler bruker-ID');
      return;
    }
    const targetLabel = targetStatus === 'active' ? 'Aktivert' : 'Deaktivert';
    if (!window.confirm(`Gjenopprett "${name || email}" til status "${targetLabel}"?`)) return;

    setUpdatingUserStatus(prev => ({ ...prev, [`${email}-restore`]: true }));
    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/users/${userId}/restore`,
        { target: targetStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(response.data?.message || `Bruker gjenopprettet til ${targetLabel}`);
      await fetchAllUsersDatabase();
    } catch (error) {
      console.error('Restore user error:', error);
      const msg = error.response?.data?.detail || error.message;
      toast.error('❌ Gjenoppretting feilet: ' + msg);
    } finally {
      setUpdatingUserStatus(prev => {
        const next = { ...prev };
        delete next[`${email}-restore`];
        return next;
      });
    }
  };

  // Fetch all users from database
  const fetchAllUsersDatabase = async () => {
    setAllUsersLoading(true);
    setAllUsersData(null);

    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/all-users-database`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setAllUsersData(response.data);
      
      // Also fetch locked accounts
      await fetchLockedAccounts();
      
      toast.success(`✅ Lastet ${response.data.total_users} brukere fra database`);
    } catch (error) {
      console.error('Fetch all users error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Lasting feilet: ' + errorMsg);      setAllUsersData({ error: errorMsg });
    } finally {
      setAllUsersLoading(false);
    }
  };

  // Fetch locked accounts
  const fetchLockedAccounts = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/superadmin/locked-accounts`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const lockouts = response.data.lockouts || [];
      setLockedAccounts(lockouts);
      console.log('🔒 Locked accounts loaded:', lockouts.length);
      console.log('🔒 Full lockouts data:', lockouts);
      if (lockouts.length > 0) {
        console.log('🔒 Locked emails:', lockouts.map(l => l.email));
      } else {
        console.log('⚠️ No locked accounts returned from API');
      }
    } catch (error) {
      console.error('❌ Fetch locked accounts error:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  // Unlock account
  const unlockAccount = async (email) => {
    if (!window.confirm(`Er du sikker på at du vil låse opp kontoen til ${email}?`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/unlock-account`,
        { email },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success(`🔓 ${response.data.message}`);
        // Refresh data
        await fetchAllUsersDatabase();
      }
    } catch (error) {
      console.error('Unlock account error:', error);
      toast.error('❌ Opplåsing feilet: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Update user status (approved, active, email_verified)
  const updateUserStatus = async (email, field, value) => {
    const key = `${email}-${field}`;
    setUpdatingUserStatus(prev => ({ ...prev, [key]: true }));

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/update-user-status`,
        { email, field, value },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success(`✅ Oppdatert ${field} for ${email}`);
        // Refresh the list
        fetchAllUsersDatabase();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Update user status error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Oppdatering feilet: ' + errorMsg);
    } finally {
      setUpdatingUserStatus(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }
  };

  // Send password reset link
  const sendPasswordResetLink = async (email, userName) => {
    const confirmed = window.confirm(
      `📧 SEND PASSORD-RESET LENKE\n\n` +
      `Send passord-reset lenke til:\n\n` +
      `Navn: ${userName}\n` +
      `E-post: ${email}\n\n` +
      `Lenken utløper om 1 uke (7 dager).\n\n` +
      `Er du sikker?`
    );

    if (!confirmed) return;

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/send-password-reset-link`,
        { email },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success(`✅ Passord-reset lenke sendt til ${email}!`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Send password reset error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Sending feilet: ' + errorMsg);
    }
  };

  // Delete user permanently
  const deleteUserPermanent = async (email, userName) => {
    const confirmed = window.confirm(
      `⚠️ PERMANENT SLETTING ⚠️\n\n` +
      `Dette vil PERMANENT slette brukeren:\n\n` +
      `Navn: ${userName}\n` +
      `E-post: ${email}\n\n` +
      `DENNE HANDLINGEN KAN IKKE ANGRES!\n\n` +
      `Er du helt sikker?`
    );

    if (!confirmed) return;

    setDeleteUserLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/api/superadmin/delete-user-permanent`,
        { email },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success(`✅ ${email} er slettet permanent!`);
        // Refresh the list
        fetchAllUsersDatabase();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Delete user error:', error);
      const errorMsg = error.response?.data?.detail?.error || error.message;
      toast.error('❌ Sletting feilet: ' + errorMsg);
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // Fetch email change audit log
  const fetchEmailAuditLog = async () => {
    setEmailAuditLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/admin/email-change-audit?limit=50`, { headers });
      setEmailAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Fetch audit log error:', error);
    } finally {
      setEmailAuditLoading(false);
    }
  };

  // Search users for email change (Super Admin)
  const searchUsersForEmailChange = async (query) => {
    if (!query || query.length < 2) {
      setEmailChangeSearchResults([]);
      return;
    }
    setEmailChangeSearching(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/users`, { headers });
      const q = query.toLowerCase();
      const results = response.data.filter(u =>
        (u.full_name && u.full_name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q))
      ).slice(0, 10);
      setEmailChangeSearchResults(results);
    } catch (error) {
      console.error('Search users error:', error);
      toast.error('Kunne ikke søke etter brukere');
    } finally {
      setEmailChangeSearching(false);
    }
  };

  // Change user email (Super Admin - no password required)
  const handleSuperAdminEmailChange = async () => {
    if (!emailChangeSelectedUser) {
      toast.error('Velg en bruker først');
      return;
    }
    if (!emailChangeNewEmail || !emailChangeNewEmail.includes('@')) {
      toast.error('Skriv inn en gyldig ny e-postadresse');
      return;
    }
    if (emailChangeNewEmail.toLowerCase() === emailChangeSelectedUser.email.toLowerCase()) {
      toast.error('Ny e-postadresse er den samme som nåværende');
      return;
    }

    setEmailChangeLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.put(
        `${API_BASE}/api/admin/users/${emailChangeSelectedUser.id}/email`,
        { new_email: emailChangeNewEmail.trim().toLowerCase(), reason: emailChangeReason || 'Endret av Super Admin' },
        { headers }
      );

      toast.success(`E-post endret fra ${emailChangeSelectedUser.email} til ${emailChangeNewEmail}`);
      // Reset form and refresh audit log
      setEmailChangeSelectedUser(null);
      setEmailChangeNewEmail('');
      setEmailChangeReason('');
      setEmailChangeSearch('');
      setEmailChangeSearchResults([]);
      fetchEmailAuditLog();
    } catch (error) {
      console.error('Email change error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke endre e-postadresse');
    } finally {
      setEmailChangeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Oversikt', icon: Shield },
    { id: 'organizations', label: 'Organisasjoner', icon: Building2 },
    { id: 'users', label: 'Super-brukere', icon: Users },
    { id: 'loginhistory', label: 'Innloggingshistorikk', icon: History },
    { id: 'email', label: 'E-post Administrasjon', icon: Mail },
    { id: 'reports', label: 'Automatiske Rapporter', icon: BarChart3 },
    { id: 'system', label: 'System', icon: Database },
    { id: 'settings', label: 'Systeminnstillinger', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Super Admin Dashboard</h1>
          <p className="text-gray-400">Administrer hele Aktivlogg-plattformen</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="px-3 py-1 bg-green-500/20 rounded-full">
            <span className="text-green-400 text-sm">Tilkoblet backend</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-yellow-500 text-yellow-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Totale organisasjoner</p>
                  <p className="text-2xl font-bold text-white">{stats.totalOrgs}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Building2 className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Aktive organisasjoner</p>
                  <p className="text-2xl font-bold text-white">{stats.activeOrgs}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Users className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Totale brukere</p>
                  <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Shield className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Super-administratorer</p>
                  <p className="text-2xl font-bold text-white">{stats.superAdmins}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Organizations */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Siste organisasjoner</h2>
            <div className="space-y-3">
              {organizations.slice(0, 5).map((org) => (
                <div 
                  key={org.id} 
                  onClick={() => {
                    setActiveTab('organizations');
                    // Scroll to the organization in the table (optional)
                    setTimeout(() => {
                      const orgRow = document.getElementById(`org-${org.id}`);
                      if (orgRow) {
                        orgRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        orgRow.classList.add('bg-yellow-500/20');
                        setTimeout(() => orgRow.classList.remove('bg-yellow-500/20'), 2000);
                      }
                    }, 100);
                  }}
                  className="flex items-center justify-between p-4 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                      <Building2 className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium group-hover:text-yellow-400 transition-colors">{org.name}</p>
                      <p className="text-gray-400 text-sm">/{org.slug} • {org.member_count || 0} medlemmer</p>
                      {org.last_admin_login && (
                        <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Sist: {org.last_admin_login.name} - {new Date(org.last_admin_login.timestamp).toLocaleDateString('no-NO')} {new Date(org.last_admin_login.timestamp).toLocaleTimeString('no-NO', {hour: '2-digit', minute: '2-digit'})}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      org.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {org.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-400 capitalize">
                      {org.subscription_plan || 'Start'}
                    </span>
                    <Eye className="h-4 w-4 text-gray-400 group-hover:text-yellow-400 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'organizations' && (
        <div className="space-y-6">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Organizations */}
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">Totale organisasjoner</p>
                <Building2 className="h-5 w-5 text-yellow-500" />
              </div>
              <p className="text-3xl font-bold text-white">{organizations.length}</p>
              <p className="text-xs text-gray-500 mt-1">Inkluderer inaktive</p>
            </div>

            {/* Active Organizations */}
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">Aktive organisasjoner</p>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-3xl font-bold text-white">
                {organizations.filter(org => org.active && org.subscription_status !== 'expired').length}
              </p>
              <p className="text-xs text-gray-500 mt-1">Aktive og demo</p>
            </div>

            {/* Total Users */}
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">Totale brukere</p>
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-white">
                {organizations.reduce((sum, org) => sum + (org.member_count || 0), 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">På tvers av alle organisasjoner</p>
            </div>

            {/* Super Admins */}
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">Super-administratorer</p>
                <Shield className="h-5 w-5 text-purple-500" />
              </div>
              <p className="text-3xl font-bold text-white">{stats.superAdminsCount || 0}</p>
              <p className="text-xs text-gray-500 mt-1">System administratorer</p>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Alle organisasjoner</h2>
              <div className="flex space-x-3">
                <div className="relative">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleBackupUpload}
                    className="hidden"
                    id="backup-upload"
                  />
                  <label
                    htmlFor="backup-upload"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 transition-colors cursor-pointer"
                  >
                    <Upload className="h-5 w-5 mr-2" />
                    Importer Backup
                  </label>
                </div>
                <button
                  onClick={() => setShowCreateOrgModal(true)}
                  className="flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Ny organisasjon
                </button>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Organisasjon</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Slug</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Medlemmer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Admins</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Abonnement</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Demo Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Handlinger</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {organizations.map((org) => (
                    <tr key={org.id} id={`org-${org.id}`} className="transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-white">{org.name}</div>
                          <div className="text-sm text-gray-400">{org.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-400">
                        /{org.slug}
                      </td>
                      <td className="px-4 py-4 text-sm text-white">
                        {org.member_count || 0}
                      </td>
                      <td className="px-4 py-4 text-sm text-white">
                        {org.admin_count || 0}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          org.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {org.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-400 capitalize">
                          {org.subscription_plan || 'Start'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {org.subscription_status === 'expired' ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                            <Clock className="h-3 w-3" />
                            Demo utløpt
                          </span>
                        ) : org.subscription_status === 'demo' ? (
                          (() => {
                            const daysRemaining = org.demo_expires_at ? 
                              Math.ceil((new Date(org.demo_expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                            const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7;
                            
                            return (
                              <div className="flex flex-col gap-1">
                                <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 w-fit ${
                                  isExpiringSoon ? 'bg-orange-500/20 text-orange-400 animate-pulse' : 'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  <Clock className="h-3 w-3" />
                                  {isExpiringSoon ? `⚠️ ${daysRemaining} dager igjen` : 'Demo aktiv'}
                                </span>
                                {daysRemaining !== null && (
                                  <span className="text-xs text-gray-500">
                                    Utløper: {new Date(org.demo_expires_at).toLocaleDateString('no-NO')}
                                  </span>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">
                            Normal
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex space-x-1">
                          <button 
                            type="button"
                            onClick={() => {
                              console.log('🎯🎯🎯 IMPERSONATION BUTTON CLICKED!');
                              console.log('🎯🎯🎯 Org object:', org);
                              handleImpersonateOrganization(org);
                            }}
                            className="text-purple-400 hover:text-purple-300 p-1"
                            title="Logg inn som denne organisasjonen"
                          >
                            <User className="h-4 w-4" />
                          </button>
                          {/* Demo Toggle - Show Clock for active demo or gray for normal */}
                          <button 
                            onClick={() => handleToggleDemo(org)}
                            className={`p-1 transition-colors ${
                              org.subscription_status === 'demo' 
                                ? 'text-green-400 hover:text-green-300' 
                                : 'text-gray-400 hover:text-gray-300'
                            }`}
                            title={
                              org.subscription_status === 'demo' 
                                ? `Demo aktiv - Utløper: ${new Date(org.demo_expires_at).toLocaleDateString('no-NO')}` 
                                : 'Aktiver 30-dagers demo'
                            }
                          >
                            <Clock className="h-4 w-4" />
                          </button>
                          {/* Activate Permanent - Only show for expired demos */}
                          {org.subscription_status === 'expired' && (
                            <button 
                              onClick={() => handleActivatePermanent(org)}
                              className="p-1 text-green-400 hover:text-green-300 transition-colors"
                              title="Aktiver permanent (alle data bevares)"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          <button 
                            onClick={() => handleEditOrganization(org)}
                            className="text-yellow-400 hover:text-yellow-300 p-1"
                            title="Rediger organisasjon"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleBackupOrganization(org)}
                            className="text-green-400 hover:text-green-300 p-1"
                            title="Last ned komplett backup"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleExportOrganizationData(org)}
                            className="text-blue-400 hover:text-blue-300 p-1"
                            title="Eksporter data for avslutning"
                          >
                            <FileText className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteOrganization(org)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title="Slett organisasjon"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {organizations.map((org) => (
                <div key={org.id} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-white font-medium">{org.name}</h3>
                      <p className="text-gray-400 text-sm">{org.email}</p>
                      <p className="text-gray-400 text-sm">/{org.slug}</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        org.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {org.active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-400 capitalize">
                        {org.subscription_plan || 'Start'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-300">
                      <span>{org.member_count || 0} medlemmer • {org.admin_count || 0} admins</span>
                    </div>
                    <div className="flex space-x-1">
                      <button 
                        type="button"
                        onClick={() => {
                          console.log('🎯🎯🎯 IMPERSONATION BUTTON CLICKED (Card View)!');
                          console.log('🎯🎯🎯 Org object:', org);
                          handleImpersonateOrganization(org);
                        }}
                        className="text-purple-400 hover:text-purple-300 p-2"
                        title="Logg inn som denne organisasjonen"
                      >
                        <User className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleToggleDemo(org)}
                        className={`p-2 transition-colors ${
                          org.subscription_status === 'demo' 
                            ? 'text-green-400 hover:text-green-300' 
                            : 'text-gray-400 hover:text-gray-300'
                        }`}
                        title={
                          org.subscription_status === 'demo' 
                            ? `Demo aktiv - Utløper: ${new Date(org.demo_expires_at).toLocaleDateString('no-NO')}` 
                            : 'Aktiver 30-dagers demo'
                        }
                      >
                        <Clock className="h-4 w-4" />
                      </button>
                      {/* Activate Permanent - Only show for expired demos */}
                      {org.subscription_status === 'expired' && (
                        <button 
                          onClick={() => handleActivatePermanent(org)}
                          className="p-2 text-green-400 hover:text-green-300 transition-colors"
                          title="Aktiver permanent (alle data bevares)"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleEditOrganization(org)}
                        className="text-yellow-400 hover:text-yellow-300 p-2"
                        title="Rediger organisasjon"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleBackupOrganization(org)}
                        className="text-green-400 hover:text-green-300 p-2"
                        title="Last ned komplett backup"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleExportOrganizationData(org)}
                        className="text-blue-400 hover:text-blue-300 p-2"
                        title="Eksporter data for avslutning"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteOrganization(org)}
                        className="text-red-400 hover:text-red-300 p-2"
                        title="Slett organisasjon"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Super-brukere</h2>
            <button 
              onClick={() => setShowCreateSuperUserModal(true)}
              className="flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Ny Super-bruker
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Navn</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">E-post</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Opprettet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Handlinger</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {allUsers.filter(u => u.role === 'super_admin').map((superUser) => (
                  <tr key={superUser.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {superUser.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {superUser.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(superUser.created_at).toLocaleDateString('no-NO')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        superUser.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {superUser.active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => openEditSuperUser(superUser)}
                          className="text-yellow-400 hover:text-yellow-300"
                          title="Rediger super admin"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        {superUser.id !== user.id && (
                          <button 
                            onClick={() => deleteSuperUser(superUser.id, superUser.full_name)}
                            className="text-red-400 hover:text-red-300"
                            title="Slett super admin"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'email' && (
        <EmailAdmin />
      )}

      {activeTab === 'loginhistory' && (
        <LoginHistory />
      )}

      {activeTab === 'reports' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-white">Automatiske Rapporter</h2>
              <p className="text-gray-400">Konfigurer automatisk sending av forretningsrapporter til eiere</p>
            </div>
            <button
              onClick={() => setShowCreateReportModal(true)}
              className="flex items-center space-x-2 bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Ny Rapport</span>
            </button>
          </div>

          {/* Current Report Data */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <button
              onClick={loadReportData}
              className="bg-gray-800 rounded-lg p-6 hover:bg-gray-700 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Se Gjeldende Rapport-data</h3>
                  <p className="text-gray-400 text-sm">Vis økonomisk oversikt og vekstmålinger</p>
                </div>
                <BarChart3 className="h-8 w-8 text-yellow-500" />
              </div>
            </button>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Aktive Rapporter</h3>
                  <p className="text-3xl font-bold text-yellow-500">{reportConfigs.filter(r => r.enabled).length}</p>
                </div>
                <Clock className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Totale Konfigurasjoner</h3>
                  <p className="text-3xl font-bold text-blue-500">{reportConfigs.length}</p>
                </div>
                <Mail className="h-8 w-8 text-blue-500" />
              </div>
            </div>
          </div>

          {/* Report Data Display */}
          {reportData && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Gjeldende Forretningsdata</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-green-600 rounded-lg p-4">
                  <h4 className="text-white text-sm font-medium">Månedlig Inntekt</h4>
                  <p className="text-2xl font-bold text-white">{reportData.financial?.monthly_revenue?.total?.toLocaleString()} NOK</p>
                </div>
                <div className="bg-red-600 rounded-lg p-4">
                  <h4 className="text-white text-sm font-medium">Månedlige Utgifter</h4>
                  <p className="text-2xl font-bold text-white">{reportData.financial?.monthly_expenses?.total?.toLocaleString()} NOK</p>
                </div>
                <div className="bg-yellow-600 rounded-lg p-4">
                  <h4 className="text-white text-sm font-medium">Overskudd</h4>
                  <p className="text-2xl font-bold text-white">{reportData.financial?.profit?.monthly_profit?.toLocaleString()} NOK</p>
                </div>
                <div className="bg-blue-600 rounded-lg p-4">
                  <h4 className="text-white text-sm font-medium">Totale Klubber</h4>
                  <p className="text-2xl font-bold text-white">{reportData.growth?.total_clubs}</p>
                </div>
              </div>
            </div>
          )}

          {/* Report Configurations List */}
          <div className="bg-gray-800 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Rapport-konfigurasjoner</h3>
            </div>
            <div className="p-6">
              {reportConfigs.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="mx-auto h-12 w-12 text-gray-500" />
                  <h3 className="mt-2 text-sm font-medium text-gray-300">Ingen rapporter konfigurert</h3>
                  <p className="mt-1 text-sm text-gray-500">Kom i gang ved å opprette din første automatiske rapport</p>
                  <button
                    onClick={() => setShowCreateReportModal(true)}
                    className="mt-4 bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg"
                  >
                    Opprett Første Rapport
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {reportConfigs.map((report) => (
                    <div key={report.id} className="border border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-white">{report.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              report.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {report.enabled ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-gray-400 space-y-1">
                            <p>Interval: Hver {report.interval_months} måned{report.interval_months !== 1 ? 'er' : ''}</p>
                            <p>Mottakere: {report.recipient_emails.join(', ')}</p>
                            {report.last_sent && (
                              <p>Sist sendt: {new Date(report.last_sent).toLocaleDateString('no-NO')}</p>
                            )}
                            {report.next_scheduled && (
                              <p>Neste sending: {new Date(report.next_scheduled).toLocaleDateString('no-NO')}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => sendTestReport(report.id)}
                            className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm"
                          >
                            <Send className="h-4 w-4" />
                            <span>Test Send</span>
                          </button>
                          <button
                            onClick={() => setEditingReport(report)}
                            className="flex items-center space-x-1 bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Rediger</span>
                          </button>
                          <button
                            onClick={() => deleteReport(report.id, report.name)}
                            className="flex items-center space-x-1 bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-sm"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Slett</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-2">System Vedlikehold</h2>
            <p className="text-gray-400 mb-6">Kjør system-operasjoner og database-migreringer</p>

            {/* Feature Flags */}
            <FeatureFlagsPanel />

            {/* Identity Linking (Multi-Klubb) */}
            <IdentityLinkingPanel />

            {/* Super Admin Email Change */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-yellow-500 mb-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Mail className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2" data-testid="email-change-title">
                    Endre brukers e-postadresse
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Søk etter en bruker og endre e-postadressen deres. Brukeren vil bli varslet på både gammel og ny e-post.
                  </p>

                  {/* Search field */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-1">Søk etter bruker</label>
                    <div className="relative">
                      <input
                        type="text"
                        data-testid="email-change-search-input"
                        value={emailChangeSearch}
                        onChange={(e) => {
                          setEmailChangeSearch(e.target.value);
                          searchUsersForEmailChange(e.target.value);
                        }}
                        placeholder="Skriv navn eller e-post..."
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 pr-10"
                      />
                      <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>

                    {/* Search results dropdown */}
                    {emailChangeSearchResults.length > 0 && !emailChangeSelectedUser && (
                      <div className="mt-2 bg-gray-700 rounded-lg border border-gray-600 max-h-60 overflow-y-auto">
                        {emailChangeSearchResults.map((u) => (
                          <button
                            key={u.id}
                            data-testid={`email-change-user-${u.id}`}
                            onClick={() => {
                              setEmailChangeSelectedUser(u);
                              setEmailChangeSearchResults([]);
                              setEmailChangeSearch('');
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-600 transition-colors border-b border-gray-600 last:border-b-0"
                          >
                            <div className="font-medium text-white">{u.full_name}</div>
                            <div className="text-sm text-gray-400">{u.email} — {u.role === 'org_admin' ? 'Admin' : u.role === 'super_admin' ? 'Super Admin' : 'Medlem'}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {emailChangeSearching && (
                      <div className="mt-2 text-gray-400 text-sm">Søker...</div>
                    )}
                  </div>

                  {/* Selected user card */}
                  {emailChangeSelectedUser && (
                    <div className="bg-gray-700 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-white font-medium">{emailChangeSelectedUser.full_name}</h4>
                          <p className="text-gray-400 text-sm">Nåværende e-post: <span className="text-yellow-400">{emailChangeSelectedUser.email}</span></p>
                          <p className="text-gray-500 text-xs mt-1">
                            Rolle: {emailChangeSelectedUser.role === 'org_admin' ? 'Admin' : emailChangeSelectedUser.role === 'super_admin' ? 'Super Admin' : 'Medlem'}
                            {emailChangeSelectedUser.organization_id && ` | Org: ${emailChangeSelectedUser.organization_id}`}
                          </p>
                        </div>
                        <button
                          data-testid="email-change-clear-user"
                          onClick={() => {
                            setEmailChangeSelectedUser(null);
                            setEmailChangeNewEmail('');
                            setEmailChangeReason('');
                          }}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          &times;
                        </button>
                      </div>

                      {/* New email input */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-300 mb-1">Ny e-postadresse</label>
                        <input
                          type="email"
                          data-testid="email-change-new-email"
                          value={emailChangeNewEmail}
                          onChange={(e) => setEmailChangeNewEmail(e.target.value)}
                          placeholder="ny.epost@eksempel.no"
                          className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white placeholder-gray-400"
                        />
                      </div>

                      {/* Reason (optional) */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-1">Begrunnelse (valgfritt)</label>
                        <input
                          type="text"
                          data-testid="email-change-reason"
                          value={emailChangeReason}
                          onChange={(e) => setEmailChangeReason(e.target.value)}
                          placeholder="F.eks. bruker byttet e-postleverandør"
                          className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white placeholder-gray-400"
                        />
                      </div>

                      {/* Submit button */}
                      <button
                        data-testid="email-change-submit"
                        onClick={handleSuperAdminEmailChange}
                        disabled={emailChangeLoading || !emailChangeNewEmail}
                        className="px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {emailChangeLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            <span>Endrer...</span>
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            <span>Endre e-postadresse</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Email Change Audit Log */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500 mb-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <History className="h-8 w-8 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-white" data-testid="email-audit-title">
                      E-postendring logg
                    </h3>
                    <button
                      data-testid="email-audit-refresh"
                      onClick={fetchEmailAuditLog}
                      disabled={emailAuditLoading}
                      className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center space-x-1.5"
                    >
                      {emailAuditLoading ? (
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      <span>{emailAuditLogs.length > 0 ? 'Oppdater' : 'Last inn'}</span>
                    </button>
                  </div>
                  <p className="text-gray-400 mb-4">
                    Oversikt over alle e-postendringer utfort av administratorer og Super Admin.
                  </p>

                  {emailAuditLogs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="email-audit-table">
                        <thead>
                          <tr className="border-b border-gray-600 text-left">
                            <th className="py-2 pr-4 text-gray-400 font-medium">Tidspunkt</th>
                            <th className="py-2 pr-4 text-gray-400 font-medium">Bruker</th>
                            <th className="py-2 pr-4 text-gray-400 font-medium">Gammel e-post</th>
                            <th className="py-2 pr-4 text-gray-400 font-medium">Ny e-post</th>
                            <th className="py-2 pr-4 text-gray-400 font-medium">Endret av</th>
                            <th className="py-2 text-gray-400 font-medium">Begrunnelse</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailAuditLogs.map((log) => (
                            <tr key={log.id} className="border-b border-gray-700 hover:bg-gray-700/50 transition-colors">
                              <td className="py-2.5 pr-4 text-gray-300 whitespace-nowrap">
                                {new Date(log.changed_at).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                <span className="text-gray-500 ml-1 text-xs">
                                  {new Date(log.changed_at).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </td>
                              <td className="py-2.5 pr-4 text-white font-medium">{log.user_name}</td>
                              <td className="py-2.5 pr-4 text-red-400">{log.old_email}</td>
                              <td className="py-2.5 pr-4 text-green-400">{log.new_email}</td>
                              <td className="py-2.5 pr-4 text-gray-300">
                                {log.changed_by_name}
                                <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-gray-600 text-gray-400">
                                  {log.changed_by_role === 'super_admin' ? 'Super Admin' : log.changed_by_role === 'admin' ? 'Admin' : log.changed_by_role}
                                </span>
                              </td>
                              <td className="py-2.5 text-gray-400 text-xs max-w-[200px] truncate">{log.reason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : !emailAuditLoading ? (
                    <p className="text-gray-500 text-sm italic">Klikk "Last inn" for a se endringsloggen.</p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* All Users Database View */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-indigo-500">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Database className="h-8 w-8 text-indigo-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Database Medlemmer Oversikt
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Visuell oversikt av ALLE brukere i databasen gruppert per organisasjon. 
                    Inkluderer aktive, ghost-medlemmer, og inaktive/slettede brukere som fortsatt sperrer e-postadresser.
                  </p>

                  {/* Load Button */}
                  {!allUsersData && (
                    <button
                      onClick={fetchAllUsersDatabase}
                      disabled={allUsersLoading}
                      className="px-6 py-3 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mb-4"
                    >
                      {allUsersLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Laster...</span>
                        </>
                      ) : (
                        <>
                          <Database className="h-5 w-5" />
                          <span>Last Inn Database</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* All Users Data Display */}
                  {allUsersData && !allUsersData.error && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h4 className="font-semibold text-white">
                            📊 Totalt {allUsersData.total_users} brukere i database
                          </h4>
                          {(userSearchQuery || Object.values(userStatusFilters).some(v => v)) && (
                            <p className="text-sm text-indigo-300 mt-1">
                              Viser {Object.values(allUsersData.organizations).reduce((acc, org) => acc + filterUsers(org.users).length, 0)} filtrerte resultater
                            </p>
                          )}
                        </div>
                        <button
                          onClick={fetchAllUsersDatabase}
                          className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-500 transition-colors"
                        >
                          🔄 Oppdater
                        </button>
                      </div>

                      {/* Search Field */}
                      <div className="mb-4">
                        <div className="relative">
                          <input
                            type="text"
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            placeholder="Søk etter navn eller e-post..."
                            className="w-full px-4 py-3 pl-10 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                          />
                          <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                          {userSearchQuery && (
                            <button
                              onClick={() => setUserSearchQuery('')}
                              className="absolute right-3 top-3 px-2 py-1 text-gray-400 hover:text-white transition-colors"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Filter Checkboxes */}
                      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 mb-4">
                        <h5 className="text-sm font-medium text-gray-300 mb-3">Hurtigfilter for problematiske medlemmer:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <label className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                            <input
                              type="checkbox"
                              checked={userStatusFilters.inactive}
                              onChange={(e) => setUserStatusFilters({...userStatusFilters, inactive: e.target.checked})}
                              className="h-4 w-4 text-red-500 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                            />
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                              <span className="text-sm text-gray-300">Inaktive</span>
                            </div>
                          </label>
                          
                          <label className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                            <input
                              type="checkbox"
                              checked={userStatusFilters.not_approved}
                              onChange={(e) => setUserStatusFilters({...userStatusFilters, not_approved: e.target.checked})}
                              className="h-4 w-4 text-purple-500 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                            />
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                              <span className="text-sm text-gray-300">Ikke godkjente</span>
                            </div>
                          </label>
                          
                          <label className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                            <input
                              type="checkbox"
                              checked={userStatusFilters.not_verified}
                              onChange={(e) => setUserStatusFilters({...userStatusFilters, not_verified: e.target.checked})}
                              className="h-4 w-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
                            />
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                              <span className="text-sm text-gray-300">Ikke verifiserte</span>
                            </div>
                          </label>
                          
                          <label className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                            <input
                              type="checkbox"
                              checked={userStatusFilters.locked}
                              onChange={(e) => setUserStatusFilters({...userStatusFilters, locked: e.target.checked})}
                              className="h-4 w-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-600"
                            />
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                              <span className="text-sm text-gray-300">Låste kontoer</span>
                            </div>
                          </label>

                          <label className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors" data-testid="filter-deleted-label">
                            <input
                              type="checkbox"
                              checked={userStatusFilters.deleted}
                              onChange={(e) => setUserStatusFilters({...userStatusFilters, deleted: e.target.checked})}
                              className="h-4 w-4 text-gray-400 bg-gray-700 border-gray-600 rounded focus:ring-gray-400"
                              data-testid="filter-deleted-checkbox"
                            />
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                              <span className="text-sm text-gray-300">Slettet</span>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {/* Filter indicators */}
                      {(userSearchQuery || Object.values(userStatusFilters).some(v => v)) && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {userSearchQuery && (
                            <span className="inline-flex items-center px-3 py-1 bg-indigo-600/20 border border-indigo-600/50 rounded-full text-sm text-indigo-300">
                              🔍 "{userSearchQuery}"
                              <button
                                onClick={() => setUserSearchQuery('')}
                                className="ml-2 text-indigo-400 hover:text-white"
                              >
                                ✕
                              </button>
                            </span>
                          )}
                          {userStatusFilters.inactive && (
                            <span className="inline-flex items-center px-3 py-1 bg-red-600/20 border border-red-600/50 rounded-full text-sm text-red-300">
                              ❌ Inaktive
                              <button
                                onClick={() => setUserStatusFilters({...userStatusFilters, inactive: false})}
                                className="ml-2 text-red-400 hover:text-white"
                              >
                                ✕
                              </button>
                            </span>
                          )}
                          {userStatusFilters.not_approved && (
                            <span className="inline-flex items-center px-3 py-1 bg-purple-600/20 border border-purple-600/50 rounded-full text-sm text-purple-300">
                              🔄 Ikke godkjente
                              <button
                                onClick={() => setUserStatusFilters({...userStatusFilters, not_approved: false})}
                                className="ml-2 text-purple-400 hover:text-white"
                              >
                                ✕
                              </button>
                            </span>
                          )}
                          {userStatusFilters.not_verified && (
                            <span className="inline-flex items-center px-3 py-1 bg-orange-600/20 border border-orange-600/50 rounded-full text-sm text-orange-300">
                              📧 Ikke verifiserte
                              <button
                                onClick={() => setUserStatusFilters({...userStatusFilters, not_verified: false})}
                                className="ml-2 text-orange-400 hover:text-white"
                              >
                                ✕
                              </button>
                            </span>
                          )}
                        </div>
                      )}

                      {/* Legend */}
                      <div className="p-3 bg-gray-900 rounded border border-gray-700 mb-4">
                        <p className="text-white font-medium mb-2">🎨 Fargekoder:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-green-500 rounded"></div>
                            <span className="text-gray-300">Aktiv medlem</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-purple-500 rounded"></div>
                            <span className="text-gray-300">Ghost (ikke godkjent)</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span className="text-gray-300">Inaktiv (sperrer e-post)</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-gray-400 rounded"></div>
                            <span className="text-gray-300">Slettet (bevart data)</span>
                          </div>
                        </div>
                      </div>

                      {/* Organizations */}
                      {Object.entries(allUsersData.organizations).map(([orgName, orgData], orgIdx) => {
                        const filteredUsers = filterUsers(orgData.users);
                        
                        // Skip organization if no users match search
                        if (filteredUsers.length === 0) return null;
                        
                        return (
                        <div key={orgIdx} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                          <h5 className="text-lg font-semibold text-white mb-3">
                            🏢 {orgName} ({filteredUsers.length} {userSearchQuery ? 'av ' + orgData.total_users : ''} brukere)
                          </h5>
                          
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {filteredUsers.map((user, userIdx) => (
                              <div 
                                key={userIdx} 
                                className={`p-3 rounded border-l-4 ${
                                  user.is_deleted ? 'border-gray-400 bg-gray-800/40' :
                                  user.category === 'active' ? 'border-green-500 bg-green-900/10' :
                                  user.category === 'ghost' ? 'border-purple-500 bg-purple-900/10' :
                                  user.category === 'inactive' ? 'border-red-500 bg-red-900/10' :
                                  'border-gray-500 bg-gray-900/10'
                                }`}
                              >
                                <div className="space-y-2">
                                  {/* User Info */}
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="text-white font-medium text-sm">{user.email}</p>
                                      {/* Lockout Status Badge */}
                                      {lockedAccounts.some(lockout => lockout.email === user.email) && (
                                        <span className="inline-flex items-center text-xs text-red-400 bg-red-900/30 px-2 py-0.5 rounded border border-red-600" title="Konto låst">
                                          🔒 Låst
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-gray-400 text-xs">Navn: {user.name} | Opprettet: {user.created_at.split('T')[0]}</p>
                                    {user.role !== 'member' && (
                                      <span className="inline-block mt-1 text-xs text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded">
                                        👤 {user.role}
                                      </span>
                                    )}
                                  </div>

                                  {/* Compact Single Row: Toggles + Buttons */}
                                  <div className="flex items-center space-x-2 pt-1">
                                    {/* Toggle Switches - Compact */}
                                    <div className="flex items-center space-x-2">
                                      {/* Godkjent Toggle */}
                                      <div className="flex items-center space-x-1 bg-gray-800/50 px-2 py-1 rounded">
                                        <span className="text-xs text-gray-300 whitespace-nowrap">Godkjent</span>
                                        <button
                                          onClick={() => updateUserStatus(user.email, 'approved', !user.approved)}
                                          disabled={updatingUserStatus[`${user.email}-approved`]}
                                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                                            user.approved ? 'bg-green-500' : 'bg-gray-600'
                                          } ${updatingUserStatus[`${user.email}-approved`] ? 'opacity-50' : ''}`}
                                        >
                                          <span
                                            className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                                              user.approved ? 'translate-x-3.5' : 'translate-x-0.5'
                                            }`}
                                          />
                                        </button>
                                      </div>

                                      {/* Aktiv Toggle */}
                                      <div className="flex items-center space-x-1 bg-gray-800/50 px-2 py-1 rounded">
                                        <span className="text-xs text-gray-300 whitespace-nowrap">Aktiv</span>
                                        <button
                                          onClick={() => updateUserStatus(user.email, 'active', !user.active)}
                                          disabled={updatingUserStatus[`${user.email}-active`]}
                                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                                            user.active ? 'bg-green-500' : 'bg-red-500'
                                          } ${updatingUserStatus[`${user.email}-active`] ? 'opacity-50' : ''}`}
                                        >
                                          <span
                                            className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                                              user.active ? 'translate-x-3.5' : 'translate-x-0.5'
                                            }`}
                                          />
                                        </button>
                                      </div>

                                      {/* Verifisert Toggle */}
                                      <div className="flex items-center space-x-1 bg-gray-800/50 px-2 py-1 rounded">
                                        <span className="text-xs text-gray-300 whitespace-nowrap">Verifisert</span>
                                        <button
                                          onClick={() => updateUserStatus(user.email, 'email_verified', !user.email_verified)}
                                          disabled={updatingUserStatus[`${user.email}-email_verified`]}
                                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                                            user.email_verified ? 'bg-green-500' : 'bg-gray-600'
                                          } ${updatingUserStatus[`${user.email}-email_verified`] ? 'opacity-50' : ''}`}
                                        >
                                          <span
                                            className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                                              user.email_verified ? 'translate-x-3.5' : 'translate-x-0.5'
                                            }`}
                                          />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Action Buttons - Same Width */}
                                    <div className="flex items-center space-x-2 ml-auto">
                                      {/* Slettet medlem: Vis gjenopprettings-knapper istedenfor normale actions */}
                                      {user.is_deleted ? (
                                        <>
                                          <span className="inline-flex items-center text-xs text-gray-300 bg-gray-700/60 px-2 py-0.5 rounded border border-gray-500" title="Soft-slettet medlem">
                                            🗑️ Slettet
                                          </span>
                                          <button
                                            onClick={() => restoreDeletedUser(user.id, user.email, user.name, 'deactivated')}
                                            disabled={updatingUserStatus[`${user.email}-restore`]}
                                            className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-500 transition-colors disabled:opacity-50 flex items-center space-x-1 whitespace-nowrap"
                                            title="Gjenopprett til status: Deaktivert (approved=false, is_deleted=false)"
                                            data-testid={`restore-deactivated-${user.email}`}
                                          >
                                            <span>↺</span>
                                            <span>Tilbakestill til Deaktivert</span>
                                          </button>
                                          <button
                                            onClick={() => restoreDeletedUser(user.id, user.email, user.name, 'active')}
                                            disabled={updatingUserStatus[`${user.email}-restore`]}
                                            className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-500 transition-colors disabled:opacity-50 flex items-center space-x-1 whitespace-nowrap"
                                            title="Gjenopprett som aktiv (approved=true, active=true, is_deleted=false)"
                                            data-testid={`restore-active-${user.email}`}
                                          >
                                            <span>✓</span>
                                            <span>Tilbakestill som Aktivert</span>
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                      {/* Unlock Button - Only show if account is locked */}
                                      {lockedAccounts.some(lockout => lockout.email === user.email) && (
                                        <button
                                          onClick={() => unlockAccount(user.email)}
                                          className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-500 transition-colors flex items-center space-x-1 whitespace-nowrap"
                                          title="Lås opp konto"
                                        >
                                          🔓
                                          <span>Lås opp</span>
                                        </button>
                                      )}
                                      <button
                                        onClick={() => sendPasswordResetLink(user.email, user.name)}
                                        className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-500 transition-colors flex items-center space-x-1 whitespace-nowrap"
                                        title="Send ny passord-reset e-post (gyldig i 1 uke)"
                                      >
                                        <Key className="h-3 w-3" />
                                        <span>Reset passord</span>
                                      </button>
                                      <button
                                        onClick={() => deleteUserPermanent(user.email, user.name)}
                                        disabled={deleteUserLoading}
                                        className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center space-x-1 whitespace-nowrap"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                        <span>Slett</span>
                                      </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Error Display */}
                  {allUsersData?.error && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">
                        ❌ <strong>Feil:</strong> {allUsersData.error}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-indigo-600/20 border border-indigo-600/50 rounded">
                    <p className="text-sm text-indigo-300">
                      <strong>⚠️ Hva vises her?</strong><br />
                      • <strong className="text-green-400">Grønn</strong>: Aktive medlemmer (synlige i medlemslisten)<br />
                      • <strong className="text-purple-400">Lilla</strong>: Ghost-medlemmer (approved=false, ikke synlige)<br />
                      • <strong className="text-red-400">Rød</strong>: Inaktive/slettede (active=false, sperrer e-post for ny registrering)<br />
                      • Alle kan slettes permanent med "Slett" knapp
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* Email Verification Migration */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Fix E-post Verifisering
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Setter <code className="bg-gray-800 px-2 py-1 rounded text-yellow-500">email_verified=true</code> for alle eksisterende brukere. 
                    Dette fikser innloggingsproblemer for brukere som ble opprettet før e-postverifisering ble implementert.
                  </p>

                  {migrationResult && (
                    <div className={`mb-4 p-4 rounded-lg ${
                      migrationResult.success 
                        ? 'bg-green-600/20 border border-green-600/50' 
                        : 'bg-red-600/20 border border-red-600/50'
                    }`}>
                      <h4 className="font-semibold text-white mb-2">Migrering Resultat:</h4>
                      {migrationResult.error ? (
                        <p className="text-red-400">{migrationResult.error}</p>
                      ) : (
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-300">
                            ✅ Brukere oppdatert (manglet felt): <span className="text-yellow-500 font-bold">{migrationResult.users_set_to_true_missing_field}</span>
                          </p>
                          <p className="text-gray-300">
                            ✅ Brukere endret (false → true): <span className="text-yellow-500 font-bold">{migrationResult.users_changed_false_to_true}</span>
                          </p>
                          <p className="text-gray-300">
                            📊 Totalt brukere: <span className="text-white font-bold">{migrationResult.total_users}</span>
                          </p>
                          <p className="text-gray-300">
                            ✅ Verifiserte brukere: <span className="text-green-400 font-bold">{migrationResult.verified_users}</span>
                          </p>
                          {migrationResult.success && (
                            <p className="text-green-400 font-semibold mt-2">
                              🎉 Alle brukere kan nå logge inn!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={runEmailVerificationMigration}
                    disabled={migrationLoading}
                    className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {migrationLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        <span>Kjører migrering...</span>
                      </>
                    ) : (
                      <>
                        <Database className="h-5 w-5" />
                        <span>Kjør Migrering</span>
                      </>
                    )}
                  </button>

                  <div className="mt-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded">
                    <p className="text-sm text-blue-300">
                      💡 <strong>Tips:</strong> Kjør denne hvis brukere får feilmeldingen "E-postadressen din er ikke bekreftet ennå" 
                      til tross for at de er godkjent i systemet.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Template Migration */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Mail className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Fix E-postmaler (Opprette & Oppdatere)
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Oppretter eller oppdaterer viktige e-postmaler: <strong>"E-postverifisering"</strong> (for nye registreringer), <strong>"Medlemskap godkjent"</strong>, og <strong>"Admin Passord-tilbakestilling"</strong>. 
                    Dette sikrer at e-poster har korrekt logo, header, footer, og at admin passord-reset sier "AKTIVLOGG" i stedet for organisasjonsnavn. <span className="text-yellow-400 font-semibold">Kjør denne hvis du får feilmeldingen "Unknown template type" eller hvis e-poster viser feil organisasjonsnavn!</span>
                  </p>

                  {templateMigrationResult && (
                    <div className={`mb-4 p-4 rounded-lg ${
                      templateMigrationResult.success 
                        ? 'bg-green-600/20 border border-green-600/50' 
                        : 'bg-red-600/20 border border-red-600/50'
                    }`}>
                      <h4 className="font-semibold text-white mb-2">Mal-oppdatering Resultat:</h4>
                      {templateMigrationResult.error ? (
                        <p className="text-red-400">{templateMigrationResult.error}</p>
                      ) : (
                        <div className="space-y-1 text-sm">
                          {templateMigrationResult.templates_updated > 0 && (
                            <p className="text-gray-300">
                              ✅ Maler oppdatert: <span className="text-yellow-500 font-bold">{templateMigrationResult.templates_updated}</span>
                            </p>
                          )}
                          {templateMigrationResult.templates_created > 0 && (
                            <p className="text-gray-300">
                              ✨ Maler opprettet: <span className="text-green-500 font-bold">{templateMigrationResult.templates_created}</span>
                            </p>
                          )}
                          {templateMigrationResult.total_changes > 0 && (
                            <p className="text-gray-300">
                              📊 Totalt endringer: <span className="text-white font-bold">{templateMigrationResult.total_changes}</span>
                            </p>
                          )}
                          <p className="text-gray-300">
                            📧 Maler: <span className="text-white font-bold">{templateMigrationResult.templates?.join(', ')}</span>
                          </p>
                          {templateMigrationResult.success && (
                            <p className="text-green-400 font-semibold mt-2">
                              🎉 E-postmaler er nå korrekt konfigurert!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={runEmailTemplateMigration}
                    disabled={templateMigrationLoading}
                    className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {templateMigrationLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        <span>Oppdaterer maler...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5" />
                        <span>Oppdater E-postmaler</span>
                      </>
                    )}
                  </button>

                  <div className="mt-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded">
                    <p className="text-sm text-blue-300">
                      💡 <strong>Tips:</strong> Kjør denne hvis du får feilmeldingen <strong>"Unknown template type: email_verification"</strong> 
                      eller hvis "Medlemskap godkjent" e-posten mangler logo/header/footer. 
                      Denne knappen oppretter manglende maler og oppdaterer eksisterende.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Test Email Sending */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Mail className="h-8 w-8 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Test E-post Sending (Diagnostikk)
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Send en test e-post til hvilken som helst e-postadresse for å diagnostisere e-post sending problemer. 
                    Bruker "E-postverifisering" malen for testing.
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      E-postadresse
                    </label>
                    <input
                      type="email"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      placeholder="test@example.com"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>

                  {testEmailResult && (
                    <div className={`mb-4 p-4 rounded-lg ${
                      testEmailResult.success 
                        ? 'bg-green-600/20 border border-green-600/50' 
                        : 'bg-red-600/20 border border-red-600/50'
                    }`}>
                      <h4 className="font-semibold text-white mb-2">Test Resultat:</h4>
                      {testEmailResult.error ? (
                        <div className="space-y-1 text-sm">
                          <p className="text-red-400">
                            ❌ <strong>Feil:</strong> {testEmailResult.error}
                          </p>
                          {testEmailResult.error_type && (
                            <p className="text-red-300">
                              <strong>Type:</strong> {testEmailResult.error_type}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1 text-sm">
                          <p className="text-green-400">
                            ✅ E-post sendt til: <span className="text-white font-bold">{testEmailResult.recipient}</span>
                          </p>
                          <p className="text-gray-300">
                            📧 Mal: <span className="text-yellow-500">{testEmailResult.template_type}</span>
                          </p>
                          {testEmailResult.success && (
                            <p className="text-green-400 font-semibold mt-2">
                              🎉 E-post systemet fungerer!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={sendTestEmail}
                    disabled={testEmailLoading || !testEmailAddress}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {testEmailLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Sender test e-post...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5" />
                        <span>Send Test E-post</span>
                      </>
                    )}
                  </button>

                  <div className="mt-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded">
                    <p className="text-sm text-blue-300">
                      💡 <strong>Tips:</strong> Hvis du får feilmeldingen <strong>"Unknown template type: email_verification"</strong>, 
                      må du først klikke på <strong>"Oppdater E-postmaler"</strong> knappen ovenfor for å opprette den manglende malen!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Birthdate Migration Tool */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Migrer Fødselsdatoer
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Konverterer alle fødselsdatoer til standard format (DD.MM.YYYY). Støtter: DDMMYY (150168), DDMMYYYY (15011968), YYYYMMDD (19680115).
                  </p>

                  {/* Migration Results */}
                  {birthdateMigrationResult && (
                    <div className="mb-4 p-4 rounded-lg bg-blue-600/20 border border-blue-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Migrerings Resultat:</h4>
                      <div className="grid grid-cols-4 gap-4 mb-3">
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Totalt brukere</p>
                          <p className="text-2xl font-bold text-white">{birthdateMigrationResult.total_users}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">✅ Oppdatert</p>
                          <p className="text-2xl font-bold text-green-500">{birthdateMigrationResult.updated}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">⏭️  Hoppet over</p>
                          <p className="text-2xl font-bold text-yellow-500">{birthdateMigrationResult.skipped}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">❌ Feil</p>
                          <p className="text-2xl font-bold text-red-500">{birthdateMigrationResult.errors}</p>
                        </div>
                      </div>

                      {birthdateMigrationResult.results && birthdateMigrationResult.results.length > 0 && (
                        <div className="max-h-60 overflow-y-auto">
                          <h5 className="text-sm font-semibold text-white mb-2">Detaljer:</h5>
                          <div className="space-y-1 text-xs">
                            {birthdateMigrationResult.results.map((result, idx) => (
                              <div key={idx} className="p-2 bg-gray-800 rounded">
                                <span className="text-gray-400">{result.email}:</span>{' '}
                                {result.status === 'updated' && (
                                  <span className="text-green-400">
                                    {result.old} → {result.new}
                                  </span>
                                )}
                                {result.status === 'already_correct' && (
                                  <span className="text-yellow-400">
                                    {result.birthdate} (allerede korrekt)
                                  </span>
                                )}
                                {result.status === 'error' && (
                                  <span className="text-red-400">Feil: {result.error}</span>
                                )}
                                {result.status === 'parse_error' && (
                                  <span className="text-orange-400">
                                    Kunne ikke parse: {result.birthdate}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={migrateBirthdates}
                    disabled={birthdateMigrationLoading}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      birthdateMigrationLoading
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {birthdateMigrationLoading ? '⏳ Migrerer...' : '🔄 Start Migrering'}
                  </button>
                </div>
              </div>
            </div>


            {/* Profile Image Cloudinary Migration Tool */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <img src="/patron-symbol.svg" alt="Cloudinary" className="h-8 w-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Migrer Profilbilder til Cloudinary
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Migrerer alle profilbilder fra data URL format til Cloudinary. Dette forbedrer ytelse og reduserer databasestørrelse.
                  </p>

                  {/* Migration Results */}
                  {profileImageMigrationResult && (
                    <div className="mb-4 p-4 rounded-lg bg-blue-600/20 border border-blue-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Migrerings Resultat:</h4>
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Totalt brukere</p>
                          <p className="text-2xl font-bold text-white">{profileImageMigrationResult.total_users}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">✅ Migrert</p>
                          <p className="text-2xl font-bold text-green-500">{profileImageMigrationResult.migrated}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">❌ Feil</p>
                          <p className="text-2xl font-bold text-red-500">{profileImageMigrationResult.failed}</p>
                        </div>
                      </div>

                      {profileImageMigrationResult.results && profileImageMigrationResult.results.length > 0 && (
                        <div className="max-h-60 overflow-y-auto">
                          <h5 className="text-sm font-semibold text-white mb-2">Detaljer:</h5>
                          <div className="space-y-1 text-xs">
                            {profileImageMigrationResult.results.map((result, idx) => (
                              <div key={idx} className="p-2 bg-gray-800 rounded">
                                <span className="text-gray-400">{result.email}:</span>{' '}
                                {result.status === 'migrated' && (
                                  <span className="text-green-400">
                                    ✅ Migrert til Cloudinary
                                  </span>
                                )}
                                {result.status === 'invalid_data_url' && (
                                  <span className="text-yellow-400">
                                    ⚠️ Ugyldig data URL
                                  </span>
                                )}
                                {result.status === 'invalid_base64' && (
                                  <span className="text-yellow-400">
                                    ⚠️ Ugyldig base64 data
                                  </span>
                                )}
                                {result.status === 'error' && (
                                  <span className="text-red-400">❌ Feil: {result.error}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={migrateProfileImages}
                    disabled={profileImageMigrationLoading}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      profileImageMigrationLoading
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {profileImageMigrationLoading ? '⏳ Migrerer...' : '🔄 Start Migrering'}
                  </button>
                </div>
              </div>
            </div>


            {/* Training Diary Images Cloudinary Migration Tool */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <img src="/patron-symbol.svg" alt="Cloudinary" className="h-8 w-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Migrer Treningsbilder til Cloudinary
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Migrerer alle bilder i skytedagbøker fra base64 format til Cloudinary. 
                    Eksisterende bilder blir bevart - kun formatet endres fra intern lagring til Cloudinary URL.
                  </p>

                  {/* Migration Results */}
                  {trainingImageMigrationResult && (
                    <div className="mb-4 p-4 rounded-lg bg-purple-600/20 border border-purple-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Migrerings Resultat:</h4>
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Økter analysert</p>
                          <p className="text-2xl font-bold text-white">{trainingImageMigrationResult.total_sessions_analyzed || 0}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">✅ Bilder migrert</p>
                          <p className="text-2xl font-bold text-green-500">{trainingImageMigrationResult.images_migrated || 0}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">❌ Feil</p>
                          <p className="text-2xl font-bold text-red-500">{trainingImageMigrationResult.images_failed || 0}</p>
                        </div>
                      </div>

                      {trainingImageMigrationResult.results && trainingImageMigrationResult.results.length > 0 && (
                        <div className="max-h-60 overflow-y-auto">
                          <h5 className="text-sm font-semibold text-white mb-2">Detaljer:</h5>
                          <div className="space-y-1 text-xs">
                            {trainingImageMigrationResult.results.map((result, idx) => (
                              <div key={idx} className="p-2 bg-gray-800 rounded">
                                <span className="text-gray-400">Økt {result.session_id?.substring(0, 8)}...:</span>{' '}
                                {result.status === 'success' && (
                                  <span className="text-green-400">
                                    ✅ {result.images_migrated} bilde(r) migrert
                                  </span>
                                )}
                                {result.status === 'error' && (
                                  <span className="text-red-400">❌ Feil: {result.error}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={migrateTrainingImages}
                    disabled={trainingImageMigrationLoading}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      trainingImageMigrationLoading
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                  >
                    {trainingImageMigrationLoading ? '⏳ Migrerer...' : '🔄 Start Migrering'}
                  </button>
                </div>
              </div>
            </div>


            {/* Pending Members Diagnostic Tool */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-orange-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Fix "Venter Godkjenning" Avvik
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Hvis Dashboard HJEM viser feil antall medlemmer "Venter godkjenning" sammenlignet med Skyteleder &gt; Dagens, 
                    kan dette verktøyet finne og fikse gamle medlemmer (&gt;30 dager) som forårsaker avviket.
                  </p>

                  {/* Diagnose Results */}
                  {pendingDiagnoseResult && !pendingDiagnoseResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-yellow-600/20 border border-yellow-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Diagnose Resultat:</h4>
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Totalt venter</p>
                            <p className="text-2xl font-bold text-white">{pendingDiagnoseResult.summary.total_pending}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Nye (&lt;30 dager)</p>
                            <p className="text-2xl font-bold text-green-500">{pendingDiagnoseResult.summary.recent_pending}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Gamle (&gt;=30 dager)</p>
                            <p className="text-2xl font-bold text-orange-500">{pendingDiagnoseResult.summary.old_pending}</p>
                          </div>
                        </div>

                        {pendingDiagnoseResult.organizations && pendingDiagnoseResult.organizations.length > 0 && (
                          <div className="mt-4">
                            <p className="text-white font-medium mb-2">Organisasjoner med gamle medlemmer:</p>
                            {pendingDiagnoseResult.organizations.map((org, idx) => (
                              <div key={idx} className="bg-gray-800 p-3 rounded mb-2">
                                <p className="text-white font-medium">{org.organization_name}</p>
                                <p className="text-gray-400 text-xs">
                                  {org.old_pending} gamle medlemmer av {org.total_pending} totalt venter
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {pendingDiagnoseResult.pending_members && pendingDiagnoseResult.pending_members.length > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-white font-medium">Velg medlemmer å godkjenne:</p>
                              <div className="flex space-x-2">
                                <button
                                  onClick={selectAllPending}
                                  className="px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-500 transition-colors"
                                >
                                  Velg alle
                                </button>
                                <button
                                  onClick={deselectAllPending}
                                  className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500 transition-colors"
                                >
                                  Avmerk alle
                                </button>
                              </div>
                            </div>
                            <p className="text-orange-300 text-xs mb-2">
                              {selectedPendingEmails.length} av {pendingDiagnoseResult.pending_members.length} valgt
                            </p>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {pendingDiagnoseResult.pending_members.map((member, idx) => (
                                <div 
                                  key={idx} 
                                  className={`bg-gray-900 p-3 rounded text-xs border-2 transition-colors ${
                                    selectedPendingEmails.includes(member.email) 
                                      ? 'border-orange-500 bg-orange-900/20' 
                                      : 'border-gray-700'
                                  }`}
                                >
                                  <label className="flex items-start cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={selectedPendingEmails.includes(member.email)}
                                      onChange={() => togglePendingSelection(member.email)}
                                      className="mt-1 mr-3 h-4 w-4 text-orange-500 rounded focus:ring-orange-500"
                                    />
                                    <div className="flex-1">
                                      <p className="text-white font-medium"><strong>{member.email}</strong></p>
                                      <p className="text-gray-400">Navn: {member.name}</p>
                                      <p className="text-gray-400">Organisasjon: {member.organization_name}</p>
                                      <p className="text-gray-400">Registrert: {member.days_since_registration} dager siden</p>
                                      <p className={`${member.has_training_sessions ? 'text-green-400' : 'text-gray-400'}`}>
                                        Treningsøkter: {member.has_training_sessions ? '✅ Ja' : '❌ Ingen'}
                                      </p>
                                    </div>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fix Results */}
                  {pendingFixResult && !pendingFixResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-green-600/20 border border-green-600/50">
                      <h4 className="font-semibold text-white mb-2">✅ Fix Fullført!</h4>
                      <p className="text-green-400 text-sm mb-3">
                        Deaktiverte {pendingFixResult.total_fixed} gamle medlemmer uten aktivitet.
                      </p>
                      {pendingFixResult.total_skipped > 0 && (
                        <p className="text-yellow-400 text-sm mb-3">
                          ⚠️ {pendingFixResult.total_skipped} medlemmer ble hoppet over fordi de har treningsøkter (aktiv bruk).
                        </p>
                      )}
                      {pendingFixResult.organizations && pendingFixResult.organizations.length > 0 && (
                        <div className="space-y-2">
                          {pendingFixResult.organizations.map((org, idx) => (
                            <div key={idx} className="bg-gray-800 p-2 rounded text-xs">
                              <p className="text-white">{org.organization_name}: {org.fixed_count} deaktivert{org.skipped_count > 0 && `, ${org.skipped_count} hoppet over`}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-green-300 text-sm mt-3">
                        🎉 Dashboard og Dagens skal nå vise samme tall!
                      </p>
                    </div>
                  )}

                  {/* Error Display */}
                  {(pendingDiagnoseResult?.error || pendingFixResult?.error) && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">
                        ❌ <strong>Feil:</strong> {pendingDiagnoseResult?.error || pendingFixResult?.error}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={diagnosePendingMembers}
                      disabled={pendingDiagnoseLoading}
                      className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {pendingDiagnoseLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                          <span>Analyserer...</span>
                        </>
                      ) : (
                        <>
                          <Users className="h-5 w-5" />
                          <span>Kjør Diagnose</span>
                        </>
                      )}
                    </button>

                    {pendingDiagnoseResult && pendingDiagnoseResult.pending_members && pendingDiagnoseResult.pending_members.length > 0 && (
                      <button
                        onClick={fixSelectedPendingMembers}
                        disabled={fixPendingLoading || selectedPendingEmails.length === 0}
                        className="px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {fixPendingLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Godkjenner...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5" />
                            <span>Godkjenn {selectedPendingEmails.length > 0 ? `${selectedPendingEmails.length} Valgte` : 'Valgte'}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-orange-600/20 border border-orange-600/50 rounded">
                    <p className="text-sm text-orange-300">
                      <strong>⚠️ Hva gjør dette?</strong><br />
                      1. <strong>Diagnose</strong>: Finner gamle medlemmer (&gt;30 dager) som fortsatt venter på godkjenning<br />
                      2. <strong>Fix</strong>: Deaktiverer gamle medlemmer slik at de ikke lenger telles i "Venter godkjenning"<br />
                      3. Dashboard HJEM og Skyteleder &gt; Dagens vil vise samme tall etter fix
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Banner Cleanup */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Trash2 className="h-8 w-8 text-red-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Rydd opp Salgsbannere
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Fjern alle salgsbannere for en organisasjon. Nyttig for å rydde opp i gamle/ødelagte bannere 
                    som ikke lar seg slette via frontend (f.eks. Cloudinary-bannere fra før AWS-migrering).
                  </p>

                  {/* Organization Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Velg organisasjon:
                    </label>
                    <select
                      value={bannerCleanupOrgId}
                      onChange={(e) => setBannerCleanupOrgId(e.target.value)}
                      className="w-full md:w-1/2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="">-- Velg organisasjon --</option>
                      {organizationsList.map(org => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Cleanup Result */}
                  {bannerCleanupResult && !bannerCleanupResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-green-600/20 border border-green-600/50">
                      <h4 className="font-semibold text-white mb-2">✅ Opprydding fullført!</h4>
                      <div className="text-sm text-gray-300">
                        <p>Organisasjon: <strong>{bannerCleanupResult.organization_name}</strong></p>
                        <p>Bannere fjernet: <strong>{bannerCleanupResult.removed_count}</strong></p>
                        <p className="text-xs text-gray-400 mt-2">
                          Legacy: {bannerCleanupResult.details?.legacy_banners_removed || 0}, 
                          Settings: {bannerCleanupResult.details?.settings_banners_removed || 0}
                        </p>
                      </div>
                    </div>
                  )}

                  {bannerCleanupResult?.error && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">❌ Feil: {bannerCleanupResult.error}</p>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={cleanupBanners}
                    disabled={bannerCleanupLoading || !bannerCleanupOrgId}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {bannerCleanupLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Rydder opp...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-5 w-5" />
                        <span>Fjern alle bannere</span>
                      </>
                    )}
                  </button>

                  <div className="mt-4 p-3 bg-red-600/20 border border-red-600/50 rounded">
                    <p className="text-sm text-red-300">
                      <strong>⚠️ Hva gjør dette?</strong><br />
                      1. Fjerner ALLE banner-URLer fra databasen for valgt organisasjon<br />
                      2. Rydder opp både legacy (banner_urls) og nye (settings.bannerUrls) felt<br />
                      3. Filer i S3/Cloudinary slettes IKKE<br />
                      4. Du kan laste opp nye bannere etterpå via Innstillinger
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cloudinary to S3 Migration */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Cloud className="h-8 w-8 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    🔄 Cloudinary → AWS S3 Migrering
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Migrer bilder fra Cloudinary til AWS S3 for bedre ytelse og lavere kostnader.
                    Cloudinary-bildene beholdes som backup inntil migreringen er verifisert.
                  </p>

                  {/* Profile Images Migration Subsection */}
                  <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-white mb-3 flex items-center">
                      <User className="h-5 w-5 mr-2 text-blue-400" />
                      Profilbilder (PROFIL-siden)
                    </h4>
                    <p className="text-sm text-gray-400 mb-4">
                      Migrer profilbilder fra Cloudinary til S3. Bilder serveres via CloudFront CDN for raskere lasting.
                    </p>

                    {/* Organization Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Velg organisasjon (valgfritt):
                      </label>
                      <select
                        value={profileS3MigrationOrgId}
                        onChange={(e) => setProfileS3MigrationOrgId(e.target.value)}
                        className="w-full md:w-1/2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Alle organisasjoner --</option>
                        {organizationsList.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Dry Run Toggle */}
                    <div className="mb-4 flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="profileS3DryRun"
                        checked={profileS3DryRun}
                        onChange={(e) => setProfileS3DryRun(e.target.checked)}
                        className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-500 focus:ring-blue-500"
                      />
                      <label htmlFor="profileS3DryRun" className="text-sm text-gray-300">
                        <strong>Dry Run</strong> - Bare tell bilder, ikke migrer (anbefalt først)
                      </label>
                    </div>

                    {/* Migration Result */}
                    {profileS3MigrationResult && !profileS3MigrationResult.error && (
                      <div className={`mb-4 p-4 rounded-lg ${profileS3MigrationResult.dry_run ? 'bg-blue-600/20 border border-blue-600/50' : 'bg-green-600/20 border border-green-600/50'}`}>
                        <h5 className="font-semibold text-white mb-2">
                          {profileS3MigrationResult.dry_run ? '📊 Dry Run Resultat' : '✅ Migrering Fullført!'}
                        </h5>
                        <div className="text-sm text-gray-300 space-y-1">
                          <p>Totalt brukere: <strong>{profileS3MigrationResult.total_users}</strong></p>
                          {!profileS3MigrationResult.dry_run && (
                            <>
                              <p className="text-green-400">Migrert: <strong>{profileS3MigrationResult.migrated}</strong></p>
                              <p className="text-yellow-400">Hoppet over: <strong>{profileS3MigrationResult.skipped}</strong></p>
                              <p className="text-red-400">Feilet: <strong>{profileS3MigrationResult.failed}</strong></p>
                            </>
                          )}
                          {profileS3MigrationResult.users && profileS3MigrationResult.users.length > 0 && (
                            <div className="mt-2 text-xs text-gray-400">
                              <p className="font-medium mb-1">Eksempel brukere:</p>
                              <ul className="list-disc list-inside">
                                {profileS3MigrationResult.users.slice(0, 5).map((u, i) => (
                                  <li key={i}>{u.email}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {profileS3MigrationResult?.error && (
                      <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                        <p className="text-red-400">❌ Feil: {profileS3MigrationResult.error}</p>
                      </div>
                    )}

                    {/* Action Button */}
                    <button
                      onClick={migrateProfileImagesToS3}
                      disabled={profileS3MigrationLoading}
                      className={`px-6 py-3 ${profileS3DryRun ? 'bg-blue-600 hover:bg-blue-500' : 'bg-green-600 hover:bg-green-500'} text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                    >
                      {profileS3MigrationLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>{profileS3DryRun ? 'Teller...' : 'Migrerer...'}</span>
                        </>
                      ) : (
                        <>
                          <Cloud className="h-5 w-5" />
                          <span>{profileS3DryRun ? 'Tell bilder (Dry Run)' : 'Start migrering'}</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="p-3 bg-blue-600/20 border border-blue-600/50 rounded">
                    <p className="text-sm text-blue-300">
                      <strong>ℹ️ Om migreringen:</strong><br />
                      1. Bilder lastes ned fra Cloudinary og opp til S3<br />
                      2. Nye felt <code className="bg-gray-700 px-1 rounded">profile_image_s3_url</code> legges til i databasen<br />
                      3. Cloudinary-bilder slettes IKKE (kan brukes som backup)<br />
                      4. Fremtidige opplastinger går direkte til S3
                    </p>
                  </div>
                </div>

                {/* User Documents Migration Subsection */}
                <div className="bg-gray-700/50 rounded-lg p-4 mb-4 mt-4">
                  <h4 className="font-semibold text-white mb-3 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-green-400" />
                    Brukerdokumenter (Startkort, Medlemskort, Våpenkort)
                  </h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Migrer brukerdokumenter fra Cloudinary til S3. Velg dokumenttype eller migrer alle.
                  </p>

                  {/* Document Type Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Dokumenttype:
                    </label>
                    <select
                      value={userDocsS3Type}
                      onChange={(e) => setUserDocsS3Type(e.target.value)}
                      className="w-full md:w-1/2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">Alle dokumenttyper</option>
                      <option value="startkort">Startkort</option>
                      <option value="medlemskort">Medlemskort</option>
                      <option value="weapon_cert">Våpenkort</option>
                    </select>
                  </div>

                  {/* Dry Run Toggle */}
                  <div className="mb-4 flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="userDocsS3DryRun"
                      checked={userDocsS3DryRun}
                      onChange={(e) => setUserDocsS3DryRun(e.target.checked)}
                      className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-green-500 focus:ring-green-500"
                    />
                    <label htmlFor="userDocsS3DryRun" className="text-sm text-gray-300">
                      <strong>Dry Run</strong> - Bare tell dokumenter, ikke migrer
                    </label>
                  </div>

                  {/* Migration Result */}
                  {userDocsS3Result && !userDocsS3Result.error && (
                    <div className={`mb-4 p-4 rounded-lg ${userDocsS3Result.dry_run ? 'bg-blue-600/20 border border-blue-600/50' : 'bg-green-600/20 border border-green-600/50'}`}>
                      <h5 className="font-semibold text-white mb-2">
                        {userDocsS3Result.dry_run ? '📊 Dry Run Resultat' : '✅ Migrering Fullført!'}
                      </h5>
                      <div className="text-sm text-gray-300 space-y-2">
                        {Object.entries(userDocsS3Result.results || {}).map(([type, data]) => (
                          <div key={type} className="bg-gray-800 p-2 rounded">
                            <p className="font-medium text-white capitalize">{type}:</p>
                            <p>Total: {data.total}, Migrert: <span className="text-green-400">{data.migrated}</span>, Feilet: <span className="text-red-400">{data.failed}</span>, Hoppet: {data.skipped}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {userDocsS3Result?.error && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">❌ Feil: {userDocsS3Result.error}</p>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={migrateUserDocsToS3}
                    disabled={userDocsS3Loading}
                    className={`px-6 py-3 ${userDocsS3DryRun ? 'bg-blue-600 hover:bg-blue-500' : 'bg-green-600 hover:bg-green-500'} text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                  >
                    {userDocsS3Loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{userDocsS3DryRun ? 'Teller...' : 'Migrerer...'}</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-5 w-5" />
                        <span>{userDocsS3DryRun ? 'Tell dokumenter (Dry Run)' : 'Start migrering'}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Training Images Migration Subsection */}
                <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-white mb-3 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-orange-400" />
                    Treningsdagbok-bilder (Skivebilder)
                  </h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Migrer målskivebilder fra treningsdagboken fra Cloudinary til S3.
                  </p>

                  {/* Dry Run Toggle */}
                  <div className="mb-4 flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="trainingImagesS3DryRun"
                      checked={trainingImagesS3DryRun}
                      onChange={(e) => setTrainingImagesS3DryRun(e.target.checked)}
                      className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-orange-500 focus:ring-orange-500"
                    />
                    <label htmlFor="trainingImagesS3DryRun" className="text-sm text-gray-300">
                      <strong>Dry Run</strong> - Bare tell bilder, ikke migrer
                    </label>
                  </div>

                  {/* Migration Result */}
                  {trainingImagesS3Result && !trainingImagesS3Result.error && (
                    <div className={`mb-4 p-4 rounded-lg ${trainingImagesS3Result.dry_run ? 'bg-blue-600/20 border border-blue-600/50' : 'bg-green-600/20 border border-green-600/50'}`}>
                      <h5 className="font-semibold text-white mb-2">
                        {trainingImagesS3Result.dry_run ? '📊 Dry Run Resultat' : '✅ Migrering Fullført!'}
                      </h5>
                      <div className="text-sm text-gray-300 space-y-1">
                        <p>Totalt økter: <strong>{trainingImagesS3Result.total_sessions}</strong></p>
                        <p>Totalt bilder: <strong>{trainingImagesS3Result.total_images}</strong></p>
                        {!trainingImagesS3Result.dry_run && (
                          <>
                            <p className="text-green-400">Migrert: <strong>{trainingImagesS3Result.migrated}</strong></p>
                            <p className="text-yellow-400">Hoppet over: <strong>{trainingImagesS3Result.skipped}</strong></p>
                            <p className="text-red-400">Feilet: <strong>{trainingImagesS3Result.failed}</strong></p>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {trainingImagesS3Result?.error && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">❌ Feil: {trainingImagesS3Result.error}</p>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={migrateTrainingImagesToS3}
                    disabled={trainingImagesS3Loading}
                    className={`px-6 py-3 ${trainingImagesS3DryRun ? 'bg-blue-600 hover:bg-blue-500' : 'bg-orange-600 hover:bg-orange-500'} text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                  >
                    {trainingImagesS3Loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{trainingImagesS3DryRun ? 'Teller...' : 'Migrerer...'}</span>
                      </>
                    ) : (
                      <>
                        <Target className="h-5 w-5" />
                        <span>{trainingImagesS3DryRun ? 'Tell bilder (Dry Run)' : 'Start migrering'}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Organization Logos Migration Subsection */}
                <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-white mb-3 flex items-center">
                    <Building className="h-5 w-5 mr-2 text-purple-400" />
                    Organisasjonslogoer
                  </h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Migrer organisasjonslogoer fra Cloudinary til S3.
                  </p>

                  {/* Dry Run Toggle */}
                  <div className="mb-4 flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="orgLogosS3DryRun"
                      checked={orgLogosS3DryRun}
                      onChange={(e) => setOrgLogosS3DryRun(e.target.checked)}
                      className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500"
                    />
                    <label htmlFor="orgLogosS3DryRun" className="text-sm text-gray-300">
                      <strong>Dry Run</strong> - Bare tell logoer, ikke migrer
                    </label>
                  </div>

                  {/* Migration Result */}
                  {orgLogosS3Result && !orgLogosS3Result.error && (
                    <div className={`mb-4 p-4 rounded-lg ${orgLogosS3Result.dry_run ? 'bg-blue-600/20 border border-blue-600/50' : 'bg-green-600/20 border border-green-600/50'}`}>
                      <h5 className="font-semibold text-white mb-2">
                        {orgLogosS3Result.dry_run ? '📊 Dry Run Resultat' : '✅ Migrering Fullført!'}
                      </h5>
                      <div className="text-sm text-gray-300 space-y-1">
                        <p>Totalt logoer: <strong>{orgLogosS3Result.total}</strong></p>
                        {!orgLogosS3Result.dry_run && (
                          <>
                            <p className="text-green-400">Migrert: <strong>{orgLogosS3Result.migrated}</strong></p>
                            <p className="text-yellow-400">Hoppet over: <strong>{orgLogosS3Result.skipped}</strong></p>
                            <p className="text-red-400">Feilet: <strong>{orgLogosS3Result.failed}</strong></p>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {orgLogosS3Result?.error && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">❌ Feil: {orgLogosS3Result.error}</p>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={migrateOrgLogosToS3}
                    disabled={orgLogosS3Loading}
                    className={`px-6 py-3 ${orgLogosS3DryRun ? 'bg-blue-600 hover:bg-blue-500' : 'bg-purple-600 hover:bg-purple-500'} text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                  >
                    {orgLogosS3Loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{orgLogosS3DryRun ? 'Teller...' : 'Migrerer...'}</span>
                      </>
                    ) : (
                      <>
                        <Building className="h-5 w-5" />
                        <span>{orgLogosS3DryRun ? 'Tell logoer (Dry Run)' : 'Start migrering'}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* S3 Migration Status Overview */}
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-3 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-cyan-400" />
                    Migrasjonsstatus Oversikt
                  </h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Se status for alle migreringer fra Cloudinary til AWS S3.
                  </p>

                  <button
                    onClick={fetchS3MigrationStatus}
                    disabled={s3MigrationStatusLoading}
                    className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mb-4"
                  >
                    {s3MigrationStatusLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Henter...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        <span>Oppdater status</span>
                      </>
                    )}
                  </button>

                  {s3MigrationStatus && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(s3MigrationStatus).map(([key, data]) => (
                        <div key={key} className={`p-3 rounded-lg ${data.migrated ? 'bg-green-600/20 border border-green-600/50' : 'bg-yellow-600/20 border border-yellow-600/50'}`}>
                          <p className="text-xs text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-lg font-bold text-white">{data.s3_count}</span>
                            {data.cloudinary_only_count > 0 && (
                              <span className="text-xs text-yellow-400">+{data.cloudinary_only_count} gjenstår</span>
                            )}
                            {data.migrated && (
                              <CheckCircle className="h-5 w-5 text-green-400" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ghost Members Cleanup */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-purple-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Rydd opp Ghost-medlemmer
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Finn og slett medlemmer som har <code className="bg-gray-700 px-2 py-1 rounded">approved=false</code> men fortsatt er aktive.
                    Dette er typisk test-brukere eller gamle registreringer som aldri ble godkjent.
                  </p>

                  {/* Diagnose Results */}
                  {ghostDiagnoseResult && !ghostDiagnoseResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-purple-600/20 border border-purple-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Ghost-medlemmer Funnet:</h4>
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Totalt ghost</p>
                            <p className="text-2xl font-bold text-white">{ghostDiagnoseResult.total_ghosts}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Organisasjoner påvirket</p>
                            <p className="text-2xl font-bold text-purple-500">{ghostDiagnoseResult.organizations_affected}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Duplikater</p>
                            <p className="text-2xl font-bold text-orange-500">{ghostDiagnoseResult.duplicate_count}</p>
                          </div>
                        </div>

                        {ghostDiagnoseResult.org_stats && ghostDiagnoseResult.org_stats.length > 0 && (
                          <div className="mt-4">
                            <p className="text-white font-medium mb-2">Organisasjoner med ghost-medlemmer:</p>
                            {ghostDiagnoseResult.org_stats.map((org, idx) => (
                              <div key={idx} className="bg-gray-800 p-3 rounded mb-2">
                                <p className="text-white font-medium">{org.organization_name}</p>
                                <p className="text-gray-400 text-xs">
                                  {org.ghost_count} ghost-medlemmer funnet
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {ghostDiagnoseResult.ghost_details && ghostDiagnoseResult.ghost_details.length > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-white font-medium">Velg medlemmer å slette:</p>
                              <div className="flex space-x-2">
                                <button
                                  onClick={selectAllGhosts}
                                  className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-500 transition-colors"
                                >
                                  Velg alle
                                </button>
                                <button
                                  onClick={deselectAllGhosts}
                                  className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500 transition-colors"
                                >
                                  Avmerk alle
                                </button>
                              </div>
                            </div>
                            <p className="text-purple-300 text-xs mb-2">
                              {selectedGhostEmails.length} av {ghostDiagnoseResult.ghost_details.length} valgt
                            </p>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {ghostDiagnoseResult.ghost_details.map((ghost, idx) => (
                                <div 
                                  key={idx} 
                                  className={`bg-gray-900 p-3 rounded text-xs border-2 transition-colors ${
                                    selectedGhostEmails.includes(ghost.email) 
                                      ? 'border-purple-500 bg-purple-900/20' 
                                      : 'border-gray-700'
                                  }`}
                                >
                                  <label className="flex items-start cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={selectedGhostEmails.includes(ghost.email)}
                                      onChange={() => toggleGhostSelection(ghost.email)}
                                      className="mt-1 mr-3 h-4 w-4 text-purple-500 rounded focus:ring-purple-500"
                                    />
                                    <div className="flex-1">
                                      <p className="text-white font-medium"><strong>{ghost.email}</strong></p>
                                      <p className="text-gray-400">Navn: {ghost.name}</p>
                                      <p className="text-gray-400">Registrert: {ghost.created_at}</p>
                                      <p className="text-gray-400">E-post verifisert: {ghost.email_verified ? '✅' : '❌'}</p>
                                    </div>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cleanup Results */}
                  {ghostCleanupResult && !ghostCleanupResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-green-600/20 border border-green-600/50">
                      <h4 className="font-semibold text-white mb-2">✅ Cleanup Fullført!</h4>
                      <p className="text-green-400 text-sm mb-3">
                        Slettet {ghostCleanupResult.deleted_count} ghost-medlemmer permanent.
                      </p>
                      {ghostCleanupResult.deleted_emails && ghostCleanupResult.deleted_emails.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto">
                          <p className="text-green-300 text-xs mb-2">Slettede e-poster:</p>
                          <ul className="space-y-1">
                            {ghostCleanupResult.deleted_emails.map((email, idx) => (
                              <li key={idx} className="text-green-400 text-xs">✓ {email}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-green-300 text-sm mt-3">
                        🎉 Dashboard "Venter godkjenning" skal nå vise 0!
                      </p>
                    </div>
                  )}

                  {/* Error Display */}
                  {(ghostDiagnoseResult?.error || ghostCleanupResult?.error) && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">
                        ❌ <strong>Feil:</strong> {ghostDiagnoseResult?.error || ghostCleanupResult?.error}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={diagnoseGhostMembers}
                      disabled={ghostDiagnoseLoading}
                      className="px-6 py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {ghostDiagnoseLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Analyserer...</span>
                        </>
                      ) : (
                        <>
                          <Users className="h-5 w-5" />
                          <span>Kjør Diagnose</span>
                        </>
                      )}
                    </button>

                    {ghostDiagnoseResult && ghostDiagnoseResult.total_ghosts > 0 && (
                      <button
                        onClick={cleanupGhostMembers}
                        disabled={ghostCleanupLoading || selectedGhostEmails.length === 0}
                        className="px-6 py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {ghostCleanupLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Sletter...</span>
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-5 w-5" />
                            <span>Slett {selectedGhostEmails.length > 0 ? `${selectedGhostEmails.length} Valgte` : 'Ghost-medlemmer'}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-purple-600/20 border border-purple-600/50 rounded">
                    <p className="text-sm text-purple-300">
                      <strong>⚠️ Hva gjør dette?</strong><br />
                      1. <strong>Diagnose</strong>: Finner alle medlemmer med approved=false<br />
                      2. <strong>Slett</strong>: Fjerner ghost-medlemmer PERMANENT (hard delete)<br />
                      3. Sjekker automatisk for duplikater før sletting<br />
                      4. Dashboard "Venter godkjenning" oppdateres til riktig tall
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Auto-Discovery and Re-linking (NEW - RECOMMENDED) */}
            <div className="bg-green-900/30 border-2 border-green-600 rounded-lg p-6 mt-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Search className="h-8 w-8 text-green-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-white">
                      Auto-Discovery og Re-linking (ANBEFALT)
                    </h3>
                    <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">NY & TRYGG</span>
                  </div>
                  <p className="text-gray-300 mb-4">
                    <strong>Reverse-tilnærming:</strong> Starter fra faktiske filer på disk og matcher dem med brukere. 
                    Mye tryggere enn å lete etter filer basert på database-paths!
                  </p>

                  {/* Organization Selection - CRITICAL */}
                  <div className="mb-4 p-4 bg-yellow-900/30 border-2 border-yellow-500 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      <h4 className="font-semibold text-white">Velg Organisasjon (Klubb)</h4>
                    </div>
                    <p className="text-sm text-yellow-200 mb-3">
                      ⚠️ VIKTIG: Prosesser én klubb om gangen for sikkerhet og nøyaktighet!
                    </p>
                    <select
                      value={selectedOrgId}
                      onChange={(e) => {
                        setSelectedOrgId(e.target.value);
                        setDiscoverDocsResult(null);  // Clear results when changing org
                        setSelectedUserIds([]);  // Clear user selection
                      }}
                      className="w-full px-4 py-2 bg-gray-800 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                      disabled={orgsLoading}
                    >
                      <option value="">-- Velg en klubb/organisasjon --</option>
                      {organizationsList.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} ({org.user_count} brukere)
                        </option>
                      ))}
                    </select>
                    
                    {selectedOrgId && (
                      <p className="text-sm text-green-300 mt-2">
                        ✓ Valgt: {organizationsList.find(o => o.id === selectedOrgId)?.name}
                      </p>
                    )}
                  </div>

                  {/* Discovery Results */}
                  {discoverDocsResult && discoverDocsResult.success && (
                    <div className="mb-4 p-4 rounded-lg bg-green-600/20 border border-green-600/50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-white">📊 Discovery Resultat:</h4>
                        {discoverDocsResult.organization_name && (
                          <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">
                            {discoverDocsResult.organization_name}
                          </span>
                        )}
                      </div>
                      
                      {/* Scan Statistics */}
                      {discoverDocsResult.scan_stats && (
                        <div className="mb-3 p-3 bg-blue-600/20 rounded">
                          <p className="text-sm text-blue-300">
                            🔍 Scannet: {discoverDocsResult.scan_stats.folders_scanned} mapper 
                            ({discoverDocsResult.scan_stats.subfolders_found} undermapper) • 
                            Funnet: {discoverDocsResult.scan_stats.files_found} filer totalt
                          </p>
                          {discoverDocsResult.users_filtered_out > 0 && (
                            <p className="text-sm text-yellow-300 mt-1">
                              ⚠️ Filtrert bort: {discoverDocsResult.users_filtered_out} brukere fra andre klubber
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Totalt filer på disk</p>
                          <p className="text-2xl font-bold text-green-400">{discoverDocsResult.total_files_on_disk}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Brukere med filer</p>
                          <p className="text-2xl font-bold text-green-400">{discoverDocsResult.users_with_files}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Umatchede filer</p>
                          <p className="text-2xl font-bold text-yellow-400">{discoverDocsResult.unmatched_files}</p>
                        </div>
                      </div>

                      {/* User Selection */}
                      {discoverDocsResult.discoveries && discoverDocsResult.discoveries.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-white font-medium">Velg brukere å prosessere:</p>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setSelectedUserIds([])}
                                className="px-3 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                              >
                                Fjern alle
                              </button>
                              <button
                                onClick={() => setSelectedUserIds(discoverDocsResult.discoveries.map(d => d.user_id))}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-500"
                              >
                                Velg alle
                              </button>
                            </div>
                          </div>
                          
                          <div className="max-h-60 overflow-y-auto bg-gray-900 rounded p-3">
                            {discoverDocsResult.discoveries.map((discovery, idx) => (
                              <label key={idx} className="flex items-center space-x-3 p-2 hover:bg-gray-800 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedUserIds.includes(discovery.user_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUserIds([...selectedUserIds, discovery.user_id]);
                                    } else {
                                      setSelectedUserIds(selectedUserIds.filter(id => id !== discovery.user_id));
                                    }
                                  }}
                                  className="w-4 h-4"
                                />
                                <div className="flex-1">
                                  <p className="text-white text-sm">{discovery.name} ({discovery.email})</p>
                                  <p className="text-green-400 text-xs">{discovery.files_count} filer</p>
                                </div>
                              </label>
                            ))}
                          </div>
                          
                          <p className="text-sm text-yellow-300 mt-2">
                            {selectedUserIds.length > 0 
                              ? `✓ ${selectedUserIds.length} bruker(e) valgt` 
                              : '⚠️ Ingen valgt = Prosesserer ALLE brukere'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Auto-Relink Results */}
                  {autoRelinkResult && autoRelinkResult.success && (
                    <div className={`mb-4 p-4 rounded-lg ${autoRelinkResult.dry_run ? 'bg-blue-600/20 border border-blue-600/50' : 'bg-green-600/20 border border-green-600/50'}`}>
                      <h4 className="font-semibold text-white mb-3">
                        {autoRelinkResult.dry_run ? '📋 Dry-Run Resultat (INGEN endringer gjort):' : '✅ Re-linking Fullført:'}
                      </h4>
                      
                      {!autoRelinkResult.dry_run && autoRelinkResult.backup && (
                        <div className="mb-3 p-2 bg-green-600/20 rounded">
                          <p className="text-xs text-green-300">
                            💾 Backup opprettet: {autoRelinkResult.backup.filename}
                          </p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="bg-gray-800 p-2 rounded">
                          <p className="text-gray-400 text-xs">Brukere prosessert</p>
                          <p className="text-xl font-bold text-white">{autoRelinkResult.stats.users_processed}</p>
                        </div>
                        <div className="bg-gray-800 p-2 rounded">
                          <p className="text-gray-400 text-xs">Brukere oppdatert</p>
                          <p className="text-xl font-bold text-green-400">{autoRelinkResult.stats.users_updated}</p>
                        </div>
                        <div className="bg-gray-800 p-2 rounded">
                          <p className="text-gray-400 text-xs">Filer linket</p>
                          <p className="text-xl font-bold text-green-400">{autoRelinkResult.stats.files_linked}</p>
                        </div>
                      </div>

                      {autoRelinkResult.changes && autoRelinkResult.changes.length > 0 && (
                        <div className="mt-3 max-h-96 overflow-y-auto">
                          <p className="text-white font-medium mb-2">
                            Detaljerte endringer ({autoRelinkResult.changes.length} brukere):
                          </p>
                          {autoRelinkResult.changes.map((change, idx) => (
                            <div key={idx} className="bg-gray-800 p-4 rounded mb-3 border-l-4 border-blue-500">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-white font-medium">{change.name} ({change.email})</p>
                                <span className="text-xs bg-green-600 px-2 py-1 rounded">{change.files_found} filer</span>
                              </div>
                              
                              {/* Show old vs new for each field */}
                              <div className="mt-3 space-y-3 text-xs">
                                {/* Startkort */}
                                {change.old_values.startkort_url !== undefined && (
                                  <div className="bg-gray-900 p-2 rounded">
                                    <p className="text-gray-400 font-semibold mb-1">Startkort:</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <p className="text-red-300 font-medium">Gammel:</p>
                                        <p className="text-red-200 break-all">{change.old_values.startkort_url || 'null'}</p>
                                      </div>
                                      <div>
                                        <p className="text-green-300 font-medium">Ny:</p>
                                        <p className="text-green-200 break-all">{change.new_values.startkort_url || 'null'}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Medlemskort */}
                                {change.old_values.medlemskort_urls !== undefined && (
                                  <div className="bg-gray-900 p-2 rounded">
                                    <p className="text-gray-400 font-semibold mb-1">Medlemskort:</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <p className="text-red-300 font-medium">Gammel:</p>
                                        {Array.isArray(change.old_values.medlemskort_urls) && change.old_values.medlemskort_urls.length > 0 ? (
                                          <ul className="text-red-200 list-disc list-inside">
                                            {change.old_values.medlemskort_urls.map((url, i) => (
                                              <li key={i} className="break-all text-xs">{url}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-red-200">[] (tom)</p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-green-300 font-medium">Ny:</p>
                                        {Array.isArray(change.new_values.medlemskort_urls) && change.new_values.medlemskort_urls.length > 0 ? (
                                          <ul className="text-green-200 list-disc list-inside">
                                            {change.new_values.medlemskort_urls.map((url, i) => (
                                              <li key={i} className="break-all text-xs">{url}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-green-200">[] (tom)</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Weapon certs */}
                                {change.old_values.weapon_cert_urls !== undefined && (
                                  <div className="bg-gray-900 p-2 rounded">
                                    <p className="text-gray-400 font-semibold mb-1">Våpengodkjenning:</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <p className="text-red-300 font-medium">Gammel:</p>
                                        {Array.isArray(change.old_values.weapon_cert_urls) && change.old_values.weapon_cert_urls.length > 0 ? (
                                          <ul className="text-red-200 list-disc list-inside">
                                            {change.old_values.weapon_cert_urls.map((url, i) => (
                                              <li key={i} className="break-all text-xs">{url}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-red-200">[] (tom)</p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-green-300 font-medium">Ny:</p>
                                        {Array.isArray(change.new_values.weapon_cert_urls) && change.new_values.weapon_cert_urls.length > 0 ? (
                                          <ul className="text-green-200 list-disc list-inside">
                                            {change.new_values.weapon_cert_urls.map((url, i) => (
                                              <li key={i} className="break-all text-xs">{url}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-green-200">[] (tom)</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleDiscoverDocuments}
                      disabled={discoverDocsLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {discoverDocsLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Scanner...</span>
                        </>
                      ) : (
                        <>
                          <Search className="h-5 w-5" />
                          <span>1. Scan Filer (trygg)</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => handleAutoRelinkDocuments(true)}
                      disabled={autoRelinkLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {autoRelinkLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Kjører...</span>
                        </>
                      ) : (
                        <>
                          <Eye className="h-5 w-5" />
                          <span>2. Dry-Run Re-link</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => handleAutoRelinkDocuments(false)}
                      disabled={autoRelinkLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {autoRelinkLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Utfører...</span>
                        </>
                      ) : (
                        <>
                          <Check className="h-5 w-5" />
                          <span>3. KJØR RE-LINK</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mt-4 p-3 bg-green-600/20 border border-green-600/50 rounded">
                    <p className="text-sm text-green-300">
                      <strong>🎯 Hvorfor denne er bedre:</strong><br />
                      • Starter fra FAKTISKE filer på disk<br />
                      • Matcher user_id automatisk fra filnavn<br />
                      • Kategoriserer filer basert på navn (startkort, medlemskort, våpen)<br />
                      • Ingen gjetting - kun faktiske filer<br />
                      • Trygg backup før endringer
                    </p>
                  </div>

                  {/* Backup Download Section */}
                  <div className="mt-6 p-4 bg-blue-900/30 border border-blue-600 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-white flex items-center space-x-2">
                        <Download className="h-5 w-5 text-blue-400" />
                        <span>Last ned Backup-filer</span>
                      </h4>
                      <button
                        onClick={handleListBackups}
                        disabled={backupsLoading}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 disabled:opacity-50"
                      >
                        {backupsLoading ? 'Oppdaterer...' : 'Oppdater liste'}
                      </button>
                    </div>
                    
                    {backupsList.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {backupsList.map((backup, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-800 p-3 rounded">
                            <div className="flex-1">
                              <p className="text-white text-sm font-medium">{backup.filename}</p>
                              <p className="text-gray-400 text-xs">
                                {backup.size_kb} KB • {new Date(backup.created * 1000).toLocaleString('no-NO')}
                              </p>
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  const response = await axios.get(
                                    `${API_BASE}${backup.download_url}`,
                                    {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      },
                                      responseType: 'blob'
                                    }
                                  );
                                  
                                  // Create download link
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', backup.filename);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                  
                                  toast.success('Backup lastet ned!');
                                } catch (error) {
                                  console.error('Download error:', error);
                                  toast.error('Kunne ikke laste ned backup');
                                }
                              }}
                              className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 flex items-center space-x-1"
                            >
                              <Download className="h-4 w-4" />
                              <span>Last ned</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">
                        {backupsLoading ? 'Laster...' : 'Ingen backup-filer funnet ennå. Kjør "KJØR RE-LINK" for å opprette en backup.'}
                      </p>
                    )}
                    
                    <p className="text-xs text-blue-300 mt-3">
                      💾 Backup-filer inneholder database-paths (ikke selve bildene). Last ned og lagre trygt!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Fix Document Paths (OLD METHOD - DEPRECATED) */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mt-6 opacity-50">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-green-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Fikse Dokumentpaths (PDF-vedlegg)
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Hvis brukere får 404-feil når de prøver å laste ned PDF med vedlegg, kan dette verktøyet fikse feil dokumentpaths i databasen.
                  </p>

                  {/* Diagnose Results */}
                  {diagnoseDocPathsResult && diagnoseDocPathsResult.success && (
                    <div className="mb-4 p-4 rounded-lg bg-blue-600/20 border border-blue-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Diagnose Resultat:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Brukere sjekket</p>
                          <p className="text-2xl font-bold text-white">{diagnoseDocPathsResult.stats.total_users_checked}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Med problemer</p>
                          <p className="text-2xl font-bold text-orange-500">{diagnoseDocPathsResult.stats.users_with_issues}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Kan fikses</p>
                          <p className="text-2xl font-bold text-green-500">{diagnoseDocPathsResult.stats.total_fixable}</p>
                        </div>
                        <div className="bg-gray-800 p-3 rounded">
                          <p className="text-gray-400 text-xs">Kan ikke fikses</p>
                          <p className="text-2xl font-bold text-red-500">{diagnoseDocPathsResult.stats.total_unfixable}</p>
                        </div>
                      </div>

                      {diagnoseDocPathsResult.users_with_issues && diagnoseDocPathsResult.users_with_issues.length > 0 && (
                        <div className="mt-4 max-h-96 overflow-y-auto">
                          <p className="text-white font-medium mb-2">Detaljer per bruker:</p>
                          {diagnoseDocPathsResult.users_with_issues.map((user, idx) => (
                            <div key={idx} className="bg-gray-800 p-4 rounded mb-3">
                              <p className="text-white font-medium">{user.name} ({user.email})</p>
                              <p className="text-gray-400 text-xs mb-2">User ID: {user.user_id}</p>
                              
                              {user.issues.map((issue, issueIdx) => (
                                <div key={issueIdx} className="mt-2 p-3 bg-gray-900 rounded border-l-4 border-orange-500">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-orange-300">{issue.type}</span>
                                    <span className={`text-xs px-2 py-1 rounded ${issue.fixable ? 'bg-green-600/30 text-green-300' : 'bg-red-600/30 text-red-300'}`}>
                                      {issue.fixable ? '✓ Kan fikses' : '✗ Mangler fil'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-400 mb-1">
                                    <span className="text-red-400">Nåværende:</span> {issue.current_filename}
                                  </p>
                                  {issue.fixable && issue.proposed_fix && (
                                    <p className="text-xs text-gray-400">
                                      <span className="text-green-400">Ny:</span> {issue.proposed_fix.filename} ({issue.proposed_fix.size_kb} KB)
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fix Results Display */}
                  {fixDocPathsResult && fixDocPathsResult.success && (
                    <div className={`mb-4 p-4 rounded-lg ${fixDocPathsResult.dry_run ? 'bg-blue-600/20 border border-blue-600/50' : 'bg-green-600/20 border border-green-600/50'}`}>
                      <h4 className="font-semibold text-white mb-3">
                        {fixDocPathsResult.dry_run ? '📋 Dry-Run Resultat (Ingen endringer gjort):' : '✅ Endringer Gjennomført:'}
                      </h4>
                      
                      {!fixDocPathsResult.dry_run && fixDocPathsResult.backup && (
                        <div className="mb-3 p-2 bg-green-600/20 rounded">
                          <p className="text-xs text-green-300">
                            💾 Backup opprettet: {fixDocPathsResult.backup.filename}
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-2 text-sm">
                        <p className={fixDocPathsResult.dry_run ? 'text-blue-300' : 'text-green-300'}>
                          {fixDocPathsResult.dry_run ? 'Ville fikse' : 'Fikset'} {fixDocPathsResult.changes.length} brukere
                        </p>
                        
                        {fixDocPathsResult.changes.length > 0 && (
                          <div className="mt-4 max-h-96 overflow-y-auto">
                            <p className="text-white font-medium mb-2">
                              {fixDocPathsResult.dry_run ? 'Planlagte endringer:' : 'Utførte endringer:'}
                            </p>
                            {fixDocPathsResult.changes.map((change, idx) => (
                              <div key={idx} className="bg-gray-800 p-3 rounded mb-2">
                                <p className="text-white font-medium">{change.name} ({change.email})</p>
                                <p className="text-gray-400 text-xs mb-2">User ID: {change.user_id}</p>
                                
                                {change.changes.map((fieldChange, cIdx) => (
                                  <div key={cIdx} className="mt-2 text-xs">
                                    <p className="text-gray-300 font-medium">{fieldChange.field}:</p>
                                    <p className="text-red-300">Gammel: {JSON.stringify(fieldChange.old_value)}</p>
                                    <p className="text-green-300">Ny: {JSON.stringify(fieldChange.new_value)}</p>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {fixDocPathsResult && !fixDocPathsResult.success && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-300">❌ Feil: {fixDocPathsResult.error}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    {/* Step 1: Diagnose */}
                    <button
                      onClick={handleDiagnoseDocumentPaths}
                      disabled={diagnoseDocPathsLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {diagnoseDocPathsLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Diagnostiserer...</span>
                        </>
                      ) : (
                        <>
                          <Search className="h-5 w-5" />
                          <span>1. Diagnostiser (trygg)</span>
                        </>
                      )}
                    </button>

                    {/* Step 2: Dry-Run */}
                    <button
                      onClick={() => handleFixDocumentPaths(true)}
                      disabled={fixDocPathsLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {fixDocPathsLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Kjører dry-run...</span>
                        </>
                      ) : (
                        <>
                          <Eye className="h-5 w-5" />
                          <span>2. Dry-Run (simuler)</span>
                        </>
                      )}
                    </button>

                    {/* Step 3: Actual Fix */}
                    <button
                      onClick={() => handleFixDocumentPaths(false)}
                      disabled={fixDocPathsLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {fixDocPathsLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Utfører endringer...</span>
                        </>
                      ) : (
                        <>
                          <FileText className="h-5 w-5" />
                          <span>3. KJØR FIKSET (endrer DB)</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mt-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded">
                    <p className="text-sm text-blue-300">
                      <strong>🔧 Trinn-for-trinn prosess:</strong><br />
                      <strong>1. Diagnostiser:</strong> Sjekk hvilke brukere som har problemer (ingen endringer)<br />
                      <strong>2. Dry-Run:</strong> Se nøyaktig hva som vil bli endret (ingen endringer)<br />
                      <strong>3. Kjør Fikset:</strong> Utfør faktiske endringer (lager backup først!)<br />
                      <br />
                      <strong className="text-yellow-300">⚠️ Sikkerhet:</strong><br />
                      • Automatisk backup før endringer<br />
                      • Søker kun etter filer med riktig user_id<br />
                      • Ingen cross-user file matching
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Resend Verification Emails */}
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Mail className="h-8 w-8 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Send Verifikasjons-e-post på nytt
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Finn brukere med <code className="bg-gray-700 px-2 py-1 rounded">email_verified=false</code> og send ny verifikasjons-e-post.
                    Nyttig hvis brukere ikke fikk e-post eller linken har utløpt.
                  </p>

                  {/* Find Results */}
                  {unverifiedResult && !unverifiedResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-blue-600/20 border border-blue-600/50">
                      <h4 className="font-semibold text-white mb-3">📊 Uverifiserte Brukere:</h4>
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Totalt uverifiserte</p>
                            <p className="text-2xl font-bold text-white">{unverifiedResult.total_unverified}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-400 text-xs">Organisasjoner</p>
                            <p className="text-2xl font-bold text-blue-500">{unverifiedResult.org_stats?.length || 0}</p>
                          </div>
                        </div>

                        {unverifiedResult.org_stats && unverifiedResult.org_stats.length > 0 && (
                          <div className="mt-4">
                            <p className="text-white font-medium mb-2">Per organisasjon:</p>
                            {unverifiedResult.org_stats.map((stat, idx) => (
                              <div key={idx} className="bg-gray-800 p-3 rounded mb-2">
                                <p className="text-white font-medium">{stat.org}</p>
                                <p className="text-gray-400 text-xs">{stat.count} uverifiserte brukere</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {unverifiedResult.user_details && unverifiedResult.user_details.length > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-white font-medium">Velg brukere:</p>
                              <div className="flex space-x-2">
                                <button
                                  onClick={selectAllUnverified}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-500 transition-colors"
                                >
                                  Velg alle
                                </button>
                                <button
                                  onClick={deselectAllUnverified}
                                  className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500 transition-colors"
                                >
                                  Avmerk alle
                                </button>
                              </div>
                            </div>
                            <p className="text-blue-300 text-xs mb-2">
                              {selectedUnverifiedEmails.length} av {unverifiedResult.user_details.length} valgt
                            </p>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {unverifiedResult.user_details.map((user, idx) => (
                                <div 
                                  key={idx} 
                                  className={`bg-gray-900 p-3 rounded text-xs border-2 transition-colors ${
                                    selectedUnverifiedEmails.includes(user.email) 
                                      ? 'border-blue-500 bg-blue-900/20' 
                                      : 'border-gray-700'
                                  }`}
                                >
                                  <label className="flex items-start cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={selectedUnverifiedEmails.includes(user.email)}
                                      onChange={() => toggleUnverifiedSelection(user.email)}
                                      className="mt-1 mr-3 h-4 w-4 text-blue-500 rounded focus:ring-blue-500"
                                    />
                                    <div className="flex-1">
                                      <p className="text-white font-medium"><strong>{user.email}</strong></p>
                                      <p className="text-gray-400">Navn: {user.name}</p>
                                      <p className="text-gray-400">Organisasjon: {user.organization}</p>
                                      <p className="text-gray-400">Registrert: {user.created_at}</p>
                                      <p className="text-gray-400">Godkjent: {user.approved ? '✅' : '❌'}</p>
                                    </div>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resend Results */}
                  {resendResult && !resendResult.error && (
                    <div className="mb-4 p-4 rounded-lg bg-green-600/20 border border-green-600/50">
                      <h4 className="font-semibold text-white mb-2">✅ E-poster Sendt!</h4>
                      <p className="text-green-400 text-sm mb-3">
                        Sendt {resendResult.sent_count} verifikasjons-e-poster.
                        {resendResult.failed_count > 0 && ` ${resendResult.failed_count} feilet.`}
                      </p>
                      {resendResult.results && resendResult.results.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto">
                          <p className="text-green-300 text-xs mb-2">Detaljer:</p>
                          <ul className="space-y-1">
                            {resendResult.results.map((result, idx) => (
                              <li key={idx} className={`text-xs ${result.status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                {result.status === 'success' ? '✓' : '✗'} {result.email}
                                {result.reason && ` (${result.reason})`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error Display */}
                  {(unverifiedResult?.error || resendResult?.error) && (
                    <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/50">
                      <p className="text-red-400">
                        ❌ <strong>Feil:</strong> {unverifiedResult?.error || resendResult?.error}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={findUnverifiedUsers}
                      disabled={unverifiedLoading}
                      className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {unverifiedLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Søker...</span>
                        </>
                      ) : (
                        <>
                          <Users className="h-5 w-5" />
                          <span>Finn Uverifiserte</span>
                        </>
                      )}
                    </button>

                    {unverifiedResult && unverifiedResult.total_unverified > 0 && (
                      <button
                        onClick={resendVerificationEmails}
                        disabled={resendLoading || selectedUnverifiedEmails.length === 0}
                        className="px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {resendLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Sender...</span>
                          </>
                        ) : (
                          <>
                            <Send className="h-5 w-5" />
                            <span>Send til {selectedUnverifiedEmails.length > 0 ? `${selectedUnverifiedEmails.length} Valgte` : 'Valgte'}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded">
                    <p className="text-sm text-blue-300">
                      <strong>⚠️ Hva gjør dette?</strong><br />
                      1. <strong>Finn</strong>: Lister alle brukere med email_verified=false<br />
                      2. <strong>Velg</strong>: Velg hvem som skal få ny e-post<br />
                      3. <strong>Send</strong>: Sender nye verifikasjonslenker (utløper om 24 timer)<br />
                      4. Brukere kan deretter verifisere e-posten sin med ny lenke
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-6">Systeminnstillinger</h2>
          <div className="space-y-6">
            <div className="p-4 bg-green-600/20 border border-green-600/50 rounded-lg">
              <h3 className="text-green-400 font-medium mb-2">System Status</h3>
              <ul className="text-green-300 text-sm space-y-1">
                <li>• Database: Tilkoblet og fungerer</li>
                <li>• API: Alle endepunkter operative</li>
                <li>• Plattform: Aktivlogg v1.0.0</li>
                <li>• Oppetid: 99.9%</li>
              </ul>
            </div>

            {/* Test Brukerkontoer */}
            <div className="p-4 bg-purple-600/20 border border-purple-500/50 rounded-lg">
              <h3 className="text-purple-400 font-medium mb-4 flex items-center">
                <Key className="h-5 w-5 mr-2" />
                Test Brukerkontoer
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <Shield className="h-5 w-5 text-red-400 mr-2" />
                    <h4 className="text-red-400 font-medium">Super Admin</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">E-post:</div>
                      <div className="text-white font-mono">superadmin@aktivlogg.no</div>
                    </div>
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">Passord:</div>
                      <div className="text-white font-mono">AktivSuper2024!</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Tilgang til alle organisasjoner og system-administrasjon
                  </div>
                </div>

                <div className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <User className="h-5 w-5 text-blue-400 mr-2" />
                    <h4 className="text-blue-400 font-medium">Org Admin</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">E-post:</div>
                      <div className="text-white font-mono">post@svpk.no</div>
                    </div>
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">Passord:</div>
                      <div className="text-white font-mono">SkyteAdmin2024!</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Admin tilgang for Svolvær Pistolklubb organisasjon
                  </div>
                </div>

                <div className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <Target className="h-5 w-5 text-purple-400 mr-2" />
                    <h4 className="text-purple-400 font-medium">Skyteleder</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">E-post:</div>
                      <div className="text-white font-mono text-xs">temp-skyteleder-4114490669@svpk.no</div>
                    </div>
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">Passord:</div>
                      <div className="text-white font-mono">SkyteLeder2024!</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Dedikert skyteleder rolle for Svolvær Pistolklubb
                  </div>
                </div>

                <div className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <Users className="h-5 w-5 text-green-400 mr-2" />
                    <h4 className="text-green-400 font-medium">Medlem</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">E-post:</div>
                      <div className="text-white font-mono">medlem@svpk.no</div>
                    </div>
                    <div className="bg-gray-800 rounded p-2">
                      <div className="text-gray-400">Passord:</div>
                      <div className="text-white font-mono">Medlem2024Safe!</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Standard medlem i Svolvær Pistolklubb
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <div className="flex items-start space-x-2">
                  <div className="text-purple-400 mt-0.5">💡</div>
                  <div>
                    <h5 className="text-purple-300 font-medium text-sm">Testing Tips</h5>
                    <ul className="text-gray-300 text-xs mt-1 space-y-1">
                      <li>• Bruk Super Admin for å teste all funksjonalitet</li>
                      <li>• Skyteleder kan godkjenne/avvise treningsøkter</li>
                      <li>• Medlem kan kun se egne logger og profil</li>
                      <li>• Alle kontoer er forhånds-godkjent og aktive</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Emergent Abonnement Oversikt */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-4 bg-yellow-600/20 border border-yellow-500/50 rounded-lg">
                <h3 className="text-yellow-400 font-medium mb-4 flex items-center">
                  <Monitor className="h-5 w-5 mr-2" />
                  Emergent Platform Abonnement
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Plan:</span>
                    <span className="text-white font-medium">Standard</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Månedlig kostnad:</span>
                    <span className="text-yellow-400 font-bold">$20 USD (~220 NOK)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Neste faktura:</span>
                    <span className="text-gray-300">15. oktober 2025</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Status:</span>
                    <span className="text-green-400 font-medium">Aktiv</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-yellow-500/30">
                  <a 
                    href="https://emergentagent.com/dashboard/billing" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors w-full justify-center"
                  >
                    <Monitor className="h-4 w-4 mr-2" />
                    Administrer abonnement
                  </a>
                </div>
              </div>

              <div className="p-4 bg-blue-600/20 border border-blue-500/50 rounded-lg">
                <h3 className="text-blue-400 font-medium mb-4 flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Tilleggstjenester
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">E-post tjeneste:</span>
                    <span className="text-white font-medium">Amazon SES</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Månedlig kostnad:</span>
                    <span className="text-blue-400 font-bold">~50 NOK</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">E-poster/måned:</span>
                    <span className="text-gray-300">1,000 inkludert</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Status:</span>
                    <span className="text-green-400 font-medium">Aktiv</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-500/30">
                  <a 
                    href="https://signin.aws.amazon.com/signin?client_id=arn%3Aaws%3Asignin%3A%3A%3Aconsole%2Fcanvas&redirect_uri=https%3A%2F%2Fconsole.aws.amazon.com%2Fconsole%2Fhome%3FhashArgs%3D%2523%26isauthcode%3Dtrue%26nc2%3Dh_si%26src%3Dheader-signin%26state%3DhashArgsFromTB_eu-north-1_27aad57c9a4fc7bd&page=resolve&code_challenge=Y-yLsrSpehS82VaMx8uxMAzj4CYEjLjwTbBw4cmlApA&code_challenge_method=SHA-256&backwards_compatible=true" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors w-full justify-center"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Administrer integrasjoner
                  </a>
                </div>
              </div>
            </div>

            {/* Business Dashboard - Subtil design */}
            <div className="p-6 bg-gray-800/30 border border-gray-700/30 rounded-lg">
              <h3 className="text-gray-200 font-semibold text-lg mb-6 flex items-center">
                <Download className="h-5 w-5 mr-3 text-gray-400" />
                Business Dashboard - Økonomisk Oversikt
              </h3>

              {/* Business Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Revenue Section */}
                <div className="bg-gray-700/30 border border-gray-600/30 rounded-lg p-5">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-600 p-2 rounded-full mr-3">
                      <span className="text-gray-200 font-medium text-sm">💵</span>
                    </div>
                    <h4 className="text-gray-200 font-medium text-base">Månedlige Inntekter</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">START klubber (<span className="text-lg font-semibold text-blue-400">299 NOK</span>):</span>
                      <span className="text-white font-bold text-lg">{financialStats.organizations.start_plan} x 299 = <span className="text-xl text-blue-300">{financialStats.revenue.start_revenue} NOK</span></span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">UBEGRENSET klubber (<span className="text-lg font-semibold text-green-400">499 NOK</span>):</span>
                      <span className="text-white font-bold text-lg">{financialStats.organizations.unlimited_plan} x 499 = <span className="text-xl text-green-300">{financialStats.revenue.unlimited_revenue} NOK</span></span>
                    </div>
                    <div className="border-t border-gray-600/30 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-200 font-medium text-lg">Total Inntekt:</span>
                        <span className="text-white font-bold text-2xl text-green-400">{financialStats.revenue.total_revenue} NOK</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expense Section */}
                <div className="bg-gray-700/30 border border-gray-600/30 rounded-lg p-5">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-600 p-2 rounded-full mr-3">
                      <span className="text-gray-200 font-medium text-sm">📊</span>
                    </div>
                    <h4 className="text-gray-200 font-medium text-base">Månedlige Utgifter</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Emergent Platform:</span>
                      <span className="text-white font-bold">{financialStats.expenses.emergent_platform} NOK</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Amazon SES:</span>
                      <span className="text-white font-bold">{financialStats.expenses.amazon_ses} NOK</span>
                    </div>
                    <div className="border-t border-gray-600/30 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-200 font-medium text-lg">Total Utgifter:</span>
                        <span className="text-gray-100 font-bold text-2xl text-orange-400">{financialStats.expenses.total} NOK</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profit & Growth Section */}
              <div className="bg-gray-800/40 border border-gray-600/40 rounded-lg p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-gray-200 font-medium text-lg flex items-center">
                    📈 Profitt & Vekst
                  </h4>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-yellow-400">{financialStats.profit.monthly_profit} NOK</div>
                    <div className="text-base text-gray-300 font-medium">Månedlig overskudd</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Profittmargin:</span>
                    <span className="text-gray-200">{Math.round(financialStats.profit.profit_margin)}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-blue-400 to-blue-500 h-3 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.max(Math.min(financialStats.profit.profit_margin, 100), 0)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Growth Incentives */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-600/30 rounded-lg p-3 text-center">
                    <div className="text-blue-400 font-bold text-lg">🎯 Neste Mål</div>
                    <div className="text-white font-bold">5 klubber</div>
                    <div className="text-blue-300 text-sm">+1.200 NOK profit</div>
                  </div>
                  <div className="bg-purple-600/30 rounded-lg p-3 text-center">
                    <div className="text-purple-400 font-bold text-lg">💎 VIP Status</div>
                    <div className="text-white font-bold">10 klubber</div>
                    <div className="text-purple-300 text-sm">+3.000 NOK profit</div>
                  </div>
                  <div className="bg-orange-600/30 rounded-lg p-3 text-center">
                    <div className="text-orange-400 font-bold text-lg">👑 Empire Mode</div>
                    <div className="text-white font-bold">20 klubber</div>
                    <div className="text-orange-300 text-sm">+7.000 NOK profit</div>
                  </div>
                </div>
              </div>

              {/* Revenue vs Expense Chart */}
              <div className="bg-gray-800/20 border border-gray-700/30 rounded-lg p-5 mb-6">
                <h4 className="text-gray-200 font-medium text-base mb-4 flex items-center">
                  📊 Økonomisk Sammenligning
                </h4>
                
                {/* Bar Chart */}
                <div className="space-y-4">
                  {/* Revenue Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300 font-medium">💰 Inntekt</span>
                      <span className="text-gray-200 font-medium">{financialStats.revenue.total_revenue} NOK</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-4">
                      <div 
                        className="bg-gradient-to-r from-blue-400 to-blue-500 h-4 rounded-full flex items-center justify-end pr-2 transition-all duration-1000"
                        style={{ width: `${financialStats.revenue.total_revenue > 0 ? Math.max(financialStats.revenue.total_revenue / Math.max(financialStats.revenue.total_revenue, financialStats.expenses.total) * 100, 10) : 10}%` }}
                      >
                        <span className="text-white text-xs">Inntekt</span>
                      </div>
                    </div>
                  </div>

                  {/* Expense Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300 font-medium">💸 Utgifter</span>
                      <span className="text-gray-200 font-medium">{financialStats.expenses.total} NOK</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-4">
                      <div 
                        className="bg-gradient-to-r from-orange-400 to-orange-500 h-4 rounded-full flex items-center justify-end pr-2 transition-all duration-1000"
                        style={{ width: `${financialStats.expenses.total > 0 ? Math.max(financialStats.expenses.total / Math.max(financialStats.revenue.total_revenue, financialStats.expenses.total) * 100, 10) : 10}%` }}
                      >
                        <span className="text-white text-xs">Utgifter</span>
                      </div>
                    </div>
                  </div>

                  {/* Profit Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300 font-medium">📈 Overskudd</span>
                      <span className={`font-medium ${financialStats.profit.monthly_profit >= 0 ? 'text-gray-200' : 'text-gray-300'}`}>
                        {financialStats.profit.monthly_profit} NOK
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-4">
                      <div 
                        className={`h-4 rounded-full flex items-center justify-center transition-all duration-1000 ${
                          financialStats.profit.monthly_profit >= 0 
                            ? 'bg-gradient-to-r from-green-400 to-green-500' 
                            : 'bg-gradient-to-r from-red-400 to-red-500'
                        }`}
                        style={{ width: `${Math.max(Math.abs(financialStats.profit.monthly_profit) / Math.max(financialStats.revenue.total_revenue, 500) * 100, 10)}%` }}
                      >
                        <span className="text-white text-xs">
                          {financialStats.profit.monthly_profit >= 0 ? 'Profitt' : 'Tap'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Growth Motivation */}
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg border border-blue-500/30">
                  <h5 className="text-blue-400 font-bold mb-2">🚀 Vekstmuligheter</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-blue-300">+1 START klubb</div>
                      <div className="text-green-400 font-bold text-lg">+299 NOK/mnd</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple-300">+1 UBEGRENSET klubb</div>
                      <div className="text-green-400 font-bold text-lg">+499 NOK/mnd</div>
                    </div>
                    <div className="text-center">
                      <div className="text-yellow-300">Mål: {financialStats.growth.growth_target} klubber</div>
                      <div className="text-blue-400 font-bold">{financialStats.growth.current_orgs}/{financialStats.growth.growth_target} ({Math.round(financialStats.growth.progress_percent)}%)</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy Policy Editor */}
              <div className="bg-gray-800/30 border border-gray-700/30 rounded-lg p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-gray-200 font-medium text-base flex items-center">
                    🔒 Personvernerklæring
                  </h4>
                  <div className="flex flex-wrap items-center gap-2">
                    {!privacyPolicy.editing ? (
                      <button
                        onClick={() => setPrivacyPolicy(prev => ({ ...prev, editing: true }))}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                      >
                        ✏️ Rediger
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center bg-gray-700 rounded text-xs">
                          <button
                            onClick={() => setPrivacyPolicy(prev => ({ ...prev, viewMode: 'html' }))}
                            className={`px-3 py-1 rounded-l ${privacyPolicy.viewMode === 'html' ? 'bg-blue-500 text-white' : 'text-gray-300 hover:text-white'}`}
                          >
                            📝 HTML
                          </button>
                          <button
                            onClick={() => setPrivacyPolicy(prev => ({ ...prev, viewMode: 'preview' }))}
                            className={`px-3 py-1 rounded-r ${privacyPolicy.viewMode === 'preview' ? 'bg-blue-500 text-white' : 'text-gray-300 hover:text-white'}`}
                          >
                            👁️ Forhåndsvisning
                          </button>
                        </div>
                        <button
                          onClick={previewPrivacyPolicyInNewWindow}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded text-sm"
                          title="Åpne forhåndsvisning i nytt vindu"
                        >
                          🔗 Åpne
                        </button>
                        <button
                          onClick={() => setPrivacyPolicy(prev => ({ ...prev, editing: false, viewMode: 'html' }))}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                        >
                          Avbryt
                        </button>
                        <button
                          onClick={savePrivacyPolicy}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                        >
                          💾 Lagre
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {privacyPolicy.editing ? (
                  <div>
                    {privacyPolicy.viewMode === 'html' ? (
                      <>
                        {/* Simple Rich Text Editor */}
                        <div className="space-y-4">
                          {/* Formatting Toolbar */}
                          <div className="bg-gray-700/50 border border-gray-600 rounded-t p-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.getElementById('privacy-textarea');
                                const start = textarea.selectionStart;
                                const end = textarea.selectionEnd;
                                const selectedText = privacyPolicy.content.substring(start, end);
                                const newContent = privacyPolicy.content.substring(0, start) + 
                                  `<strong>${selectedText || 'Fet tekst'}</strong>` + 
                                  privacyPolicy.content.substring(end);
                                setPrivacyPolicy(prev => ({ ...prev, content: newContent }));
                              }}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-500 font-bold"
                              title="Fet tekst"
                            >
                              B
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.getElementById('privacy-textarea');
                                const start = textarea.selectionStart;
                                const end = textarea.selectionEnd;
                                const selectedText = privacyPolicy.content.substring(start, end);
                                const newContent = privacyPolicy.content.substring(0, start) + 
                                  `<em>${selectedText || 'Kursiv tekst'}</em>` + 
                                  privacyPolicy.content.substring(end);
                                setPrivacyPolicy(prev => ({ ...prev, content: newContent }));
                              }}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-500 italic"
                              title="Kursiv tekst"
                            >
                              I
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.getElementById('privacy-textarea');
                                const start = textarea.selectionStart;
                                const end = textarea.selectionEnd;
                                const selectedText = privacyPolicy.content.substring(start, end);
                                const newContent = privacyPolicy.content.substring(0, start) + 
                                  `<h2>${selectedText || 'Overskrift'}</h2>` + 
                                  privacyPolicy.content.substring(end);
                                setPrivacyPolicy(prev => ({ ...prev, content: newContent }));
                              }}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-500"
                              title="Overskrift"
                            >
                              H2
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.getElementById('privacy-textarea');
                                const start = textarea.selectionStart;
                                const newContent = privacyPolicy.content.substring(0, start) + 
                                  `<ul><li>Punkt 1</li><li>Punkt 2</li></ul>` + 
                                  privacyPolicy.content.substring(start);
                                setPrivacyPolicy(prev => ({ ...prev, content: newContent }));
                              }}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-500"
                              title="Punktliste"
                            >
                              • Liste
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.getElementById('privacy-textarea');
                                const start = textarea.selectionStart;
                                const end = textarea.selectionEnd;
                                const selectedText = privacyPolicy.content.substring(start, end);
                                const url = prompt('Skriv inn URL:', 'https://');
                                if (url) {
                                  const newContent = privacyPolicy.content.substring(0, start) + 
                                    `<a href="${url}" target="_blank">${selectedText || 'Lenketekst'}</a>` + 
                                    privacyPolicy.content.substring(end);
                                  setPrivacyPolicy(prev => ({ ...prev, content: newContent }));
                                }
                              }}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-500"
                              title="Legg til lenke"
                            >
                              🔗
                            </button>
                          </div>
                          
                          <textarea
                            id="privacy-textarea"
                            value={privacyPolicy.content}
                            onChange={(e) => setPrivacyPolicy(prev => ({ ...prev, content: e.target.value }))}
                            className="w-full h-80 px-3 py-2 bg-gray-700 border border-gray-600 rounded-b text-white text-sm resize-y"
                            placeholder="Skriv inn HTML-kode for personvernerklæringen her, eller bruk verktøyene over..."
                          />
                        </div>
                        <div className="text-gray-400 text-xs mt-2 space-y-1">
                          <p>💡 <strong>Tips:</strong> Bruk verktøylinjen for enkel formatering eller skriv HTML direkte</p>
                          <p>🔧 <strong>Funksjoner:</strong> Fet/kursiv tekst, overskrifter, lister og lenker</p>
                        </div>
                      </>
                    ) : (
                      // Preview Mode
                      <div className="bg-white border border-gray-300 rounded p-6 h-80 overflow-y-auto">
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                          <p className="text-blue-800 text-sm font-medium">📋 Live Forhåndsvisning</p>
                          <p className="text-blue-600 text-xs">Slik vil personvernerklæringen se ut for brukerne</p>
                        </div>
                        {privacyPolicy.content ? (
                          <div 
                            className="prose prose-sm max-w-none text-gray-800"
                            style={{ 
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                              lineHeight: '1.6',
                              color: '#333'
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: privacyPolicy.content 
                            }}
                          />
                        ) : (
                          <p className="text-gray-500 italic text-center py-8">
                            Ingen innhold å vise. Gå til HTML-modus for å legge til tekst.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-700/30 p-4 rounded border">
                    {privacyPolicy.content ? (
                      <div 
                        className="text-gray-200 text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: privacyPolicy.content }}
                      />
                    ) : (
                      <p className="text-gray-400 text-sm italic">
                        Ingen personvernerklæring registrert. Klikk "Rediger" for å legge til.
                      </p>
                    )}
                    {privacyPolicy.updated_at && (
                      <p className="text-gray-500 text-xs mt-3 pt-3 border-t border-gray-600">
                        Sist oppdatert: {new Date(privacyPolicy.updated_at).toLocaleDateString('no-NO')}
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4 p-3 bg-gray-700/20 rounded border border-gray-600/30">
                  <h5 className="text-gray-300 font-medium text-sm mb-2">📋 Juridiske lenker</h5>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a 
                      href="https://aktivlogg.no/personvernerklaering-2" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm"
                    >
                      🔗 Se Personvernerklæring (brukerside)
                    </a>
                    <a 
                      href="https://aktivlogg.no/betingelser/" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm"
                    >
                      📄 Salgs- og leveringsbetingelser
                    </a>
                  </div>
                </div>
              </div>

              {/* Action Center */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a 
                  href="https://www.sendregning.no/side/#/oversikt" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-500 transition-colors"
                >
                  📊 Se fakturahistorikk
                </a>
                <button
                  onClick={() => setActiveTab('organizations')}
                  className="flex items-center justify-center px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-bold hover:bg-yellow-400 transition-colors"
                >
                  🎯 Legg til ny klubb!
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Plattform konfigurasjon - Technical Information Cheat Sheet */}
              <div className="p-4 bg-gray-700 rounded-lg">
                <h3 className="text-white font-medium mb-4 flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Plattform konfigurasjon
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-800 p-3 rounded">
                      <h4 className="text-blue-400 font-medium text-sm mb-2">Server & Infrastruktur</h4>
                      <ul className="text-gray-300 text-xs space-y-1">
                        <li>• <strong>Leverandør:</strong> Emergent Agent Platform</li>
                        <li>• <strong>Serversted:</strong> EU/Norge (GDPR-kompatibel)</li>
                        <li>• <strong>Platform:</strong> Kubernetes Container</li>
                        <li>• <strong>Miljø:</strong> Preview (aktivlogg-qr.preview.emergentagent.com)</li>
                        <li>• <strong>Oppetid:</strong> 99.9% SLA</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800 p-3 rounded">
                      <h4 className="text-green-400 font-medium text-sm mb-2">Teknologi Stack</h4>
                      <ul className="text-gray-300 text-xs space-y-1">
                        <li>• <strong>Backend:</strong> Python 3.11 + FastAPI</li>
                        <li>• <strong>Frontend:</strong> React 18 + JavaScript</li>
                        <li>• <strong>Database:</strong> MongoDB Atlas</li>
                        <li>• <strong>Styling:</strong> Tailwind CSS</li>
                        <li>• <strong>PDF:</strong> jsPDF bibliotek</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800 p-3 rounded">
                      <h4 className="text-yellow-400 font-medium text-sm mb-2">Tredjeparts Tjenester</h4>
                      <ul className="text-gray-300 text-xs space-y-1">
                        <li>• <strong>E-post:</strong> SendGrid API</li>
                        <li>• <strong>Betaling:</strong> Stripe (planlagt)</li>
                        <li>• <strong>QR Scanner:</strong> qr-scanner bibliotek</li>
                        <li>• <strong>Ikoner:</strong> Lucide React</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800 p-3 rounded">
                      <h4 className="text-purple-400 font-medium text-sm mb-2">Emergent Kontakt</h4>
                      <ul className="text-gray-300 text-xs space-y-1">
                        <li>• <strong>Platform:</strong> <a href="https://emergentagent.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">emergentagent.com</a></li>
                        <li>• <strong>Dashboard:</strong> <a href="https://emergentagent.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Min Emergent konto</a></li>
                        <li>• <strong>Support:</strong> support@emergentagent.com</li>
                        <li>• <strong>Dokumentasjon:</strong> <a href="https://docs.emergentagent.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">docs.emergentagent.com</a></li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="bg-blue-600/20 border border-blue-600/50 rounded p-3">
                    <h4 className="text-blue-400 font-medium text-sm mb-2">Viktig Systeminfo</h4>
                    <ul className="text-blue-300 text-xs space-y-1">
                      <li>• <strong>Versjon:</strong> Aktivlogg v1.0.0 (2025)</li>
                      <li>• <strong>Sist oppdatert:</strong> September 2025</li>
                      <li>• <strong>Backup frekvens:</strong> Daglig automatisk</li>
                      <li>• <strong>Roller:</strong> Super Admin, Org Admin, Range Officer, Member</li>
                      <li>• <strong>Sikkerhet:</strong> JWT tokens, bcrypt hashing, HTTPS</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Sikkerhetskopier */}
              <div className="p-4 bg-gray-700 rounded-lg">
                <h3 className="text-white font-medium mb-4 flex items-center">
                  <Download className="h-5 w-5 mr-2" />
                  Sikkerhetskopier
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-800 p-3 rounded">
                    <h4 className="text-green-400 font-medium text-sm mb-2">Automatiske Sikkerhetskopier</h4>
                    <ul className="text-gray-300 text-xs space-y-1">
                      <li>• <strong>Frekvens:</strong> Daglig kl. 03:00 (GMT+1)</li>
                      <li>• <strong>Oppbevaring:</strong> 30 dager</li>
                      <li>• <strong>Inkluderer:</strong> Brukerdata, treningslogger, organisasjoner</li>
                      <li>• <strong>Ekskluderer:</strong> Passord (kun hasher), midlertidige filer</li>
                      <li>• <strong>Kryptering:</strong> AES-256 encryption</li>
                    </ul>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors">
                      <Download className="h-4 w-4 mr-2" />
                      Last ned siste sikkerhetskopi
                    </button>
                    <button className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors">
                      <Upload className="h-4 w-4 mr-2" />
                      Importer backup
                    </button>
                  </div>
                </div>
              </div>
              
              {/* GDPR Seksjon */}
              <div className="p-4 bg-gray-700 rounded-lg">
                <h3 className="text-white font-medium mb-4 flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  GDPR & Personvern
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-800 p-3 rounded">
                      <h4 className="text-green-400 font-medium text-sm mb-2">Databehandling</h4>
                      <ul className="text-gray-300 text-xs space-y-1">
                        <li>• <strong>Behandlingsgrunnlag:</strong> Samtykke + Legitim interesse</li>
                        <li>• <strong>Formål:</strong> Treningslogg for pistolklubbmedlemmer</li>
                        <li>• <strong>Lagringsperiode:</strong> Til bruker sletter konto</li>
                        <li>• <strong>Geografisk område:</strong> EU/EØS</li>
                        <li>• <strong>Tredjepartsdeling:</strong> Ingen</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800 p-3 rounded">
                      <h4 className="text-blue-400 font-medium text-sm mb-2">Brukerrettigheter</h4>
                      <ul className="text-gray-300 text-xs space-y-1">
                        <li>• <strong>Innsyn:</strong> Profil + treningshistorikk</li>
                        <li>• <strong>Retting:</strong> Kan endre egen profil</li>
                        <li>• <strong>Sletting:</strong> Kan slette egen konto</li>
                        <li>• <strong>Dataportabilitet:</strong> PDF eksport</li>
                        <li>• <strong>Klage:</strong> Datatilsynet.no</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="bg-red-600/20 border border-red-600/50 rounded p-3">
                    <h4 className="text-red-400 font-medium text-sm mb-2">Persondata vi behandler</h4>
                    <ul className="text-red-300 text-xs space-y-1">
                      <li>• <strong>Identifikasjon:</strong> Navn, fødselsdato, medlemsnummer</li>
                      <li>• <strong>Kontakt:</strong> E-postadresse</li>
                      <li>• <strong>Aktivitetsdata:</strong> Treningsøkter, tidspunkt, lokasjon</li>
                      <li>• <strong>Teknisk:</strong> IP-adresse, innloggingstider</li>
                    </ul>
                  </div>
                  
                  <div className="text-center">
                    <a 
                      href="https://aktivlogg.no/personvernerklaering-2" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-500 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Les full personvernerklæring
                    </a>
                  </div>
                </div>
              </div>

              {/* Mobile Bookmark Banner Settings */}
              <div className="p-4 bg-gray-700 rounded-lg">
                <h3 className="text-white font-medium mb-4 flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Innstillinger for mobilbannere
                </h3>
                <MobileBannerSettings />
              </div>

              {/* Service Message Settings */}
              <div className="bg-gray-700 p-6 rounded-lg">
                <h3 className="text-white font-medium mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Servicemelding på login-side
                </h3>
                <ServiceMessageSettings />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <CreateOrgModal
          onClose={() => {
            setShowCreateOrgModal(false);
            setEditingOrg(null);
          }}
          onCreate={() => {
            loadSuperAdminData();
            setShowCreateOrgModal(false);
          }}
        />
      )}

      {/* Create Super User Modal */}
      {showCreateSuperUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">
              Opprett ny Super Admin
            </h2>
            <form onSubmit={(e) => { e.preventDefault(); createSuperUser(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Fullt navn
                </label>
                <input
                  type="text"
                  value={newSuperUserForm.full_name}
                  onChange={(e) => setNewSuperUserForm({...newSuperUserForm, full_name: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  E-postadresse
                </label>
                <input
                  type="email"
                  value={newSuperUserForm.email}
                  onChange={(e) => setNewSuperUserForm({...newSuperUserForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Passord
                </label>
                <input
                  type="password"
                  value={newSuperUserForm.password}
                  onChange={(e) => setNewSuperUserForm({...newSuperUserForm, password: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                  minLength="6"
                />
              </div>
              
              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateSuperUserModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded hover:bg-yellow-400"
                >
                  Opprett
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Super User Modal */}
      {showEditSuperUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">
              Rediger Super Admin
            </h2>
            <form onSubmit={(e) => { e.preventDefault(); updateSuperUser(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Fullt navn
                </label>
                <input
                  type="text"
                  value={superUserEditForm.full_name}
                  onChange={(e) => setSuperUserEditForm({...superUserEditForm, full_name: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  E-postadresse
                </label>
                <input
                  type="email"
                  value={superUserEditForm.email}
                  onChange={(e) => setSuperUserEditForm({...superUserEditForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nytt passord <span className="text-gray-500">(la stå tomt for å beholde nåværende)</span>
                </label>
                <input
                  type="password"
                  value={superUserEditForm.password}
                  onChange={(e) => setSuperUserEditForm({...superUserEditForm, password: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  minLength="6"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-300">
                  <input
                    type="checkbox"
                    checked={superUserEditForm.active}
                    onChange={(e) => setSuperUserEditForm({...superUserEditForm, active: e.target.checked})}
                    className="mr-2"
                  />
                  Aktiv bruker
                </label>
              </div>
              
              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditSuperUserModal(false);
                    setEditingSuperUser(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded hover:bg-yellow-400"
                >
                  Oppdater
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-90vh overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">Opprett Ny Automatisk Rapport</h2>
            
            <form onSubmit={(e) => { e.preventDefault(); createReport(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Rapport-navn</label>
                <input
                  type="text"
                  value={newReportForm.name}
                  onChange={(e) => setNewReportForm({...newReportForm, name: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                  placeholder="f.eks. Månedlig Eierrapport"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Epost-mottakere</label>
                <div className="space-y-2">
                  {newReportForm.recipient_emails.map((email, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => updateEmailField(index, e.target.value)}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                        placeholder="epost@example.com"
                        required={index === 0}
                      />
                      {newReportForm.recipient_emails.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEmailField(index)}
                          className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-md"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addEmailField}
                    className="flex items-center space-x-1 text-yellow-500 hover:text-yellow-400"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Legg til epost</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sending-intervall</label>
                <select
                  value={newReportForm.interval_months}
                  onChange={(e) => setNewReportForm({...newReportForm, interval_months: parseInt(e.target.value)})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value={1}>Månedlig (hver måned)</option>
                  <option value={3}>Kvartalsvis (hver 3. måned)</option>
                  <option value={6}>Halvårlig (hver 6. måned)</option>
                  <option value={12}>Årlig (hver 12. måned)</option>
                </select>
              </div>

              {/* Custom Date Selection */}
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-medium text-gray-200 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Velg sending-dato
                </h3>
                
                {/* For Yearly - Select Month and Day */}
                {newReportForm.interval_months === 12 && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Måned</label>
                      <select
                        value={newReportForm.custom_month || 12}
                        onChange={(e) => setNewReportForm({...newReportForm, custom_month: parseInt(e.target.value)})}
                        className="w-full bg-gray-600 border border-gray-500 rounded-md px-2 py-1 text-white text-sm"
                      >
                        <option value={1}>Januar</option>
                        <option value={2}>Februar</option>
                        <option value={3}>Mars</option>
                        <option value={4}>April</option>
                        <option value={5}>Mai</option>
                        <option value={6}>Juni</option>
                        <option value={7}>Juli</option>
                        <option value={8}>August</option>
                        <option value={9}>September</option>
                        <option value={10}>Oktober</option>
                        <option value={11}>November</option>
                        <option value={12}>Desember</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Dag i måned</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={newReportForm.custom_day_of_month || 1}
                        onChange={(e) => setNewReportForm({...newReportForm, custom_day_of_month: parseInt(e.target.value)})}
                        className="w-full bg-gray-600 border border-gray-500 rounded-md px-2 py-1 text-white text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* For Monthly/Quarterly - Select Day of Month */}
                {(newReportForm.interval_months === 1 || newReportForm.interval_months === 3 || newReportForm.interval_months === 6) && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Dag i måned (1-31)</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={newReportForm.custom_day_of_month || 1}
                      onChange={(e) => setNewReportForm({...newReportForm, custom_day_of_month: parseInt(e.target.value)})}
                      className="w-full bg-gray-600 border border-gray-500 rounded-md px-2 py-1 text-white text-sm"
                      placeholder="F.eks. 1 for første dag i måneden"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {newReportForm.interval_months === 1 && 'Rapporten sendes den valgte dagen hver måned'}
                      {newReportForm.interval_months === 3 && 'Rapporten sendes den valgte dagen hvert kvartal (Jan, Apr, Jul, Okt)'}
                      {newReportForm.interval_months === 6 && 'Rapporten sendes den valgte dagen hver halvår (Jan, Jul)'}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Inkluder i rapport</label>
                
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={newReportForm.include_financial_data}
                    onChange={(e) => setNewReportForm({...newReportForm, include_financial_data: e.target.checked})}
                    className="form-checkbox h-4 w-4 text-yellow-600"
                  />
                  <span className="text-gray-300">Økonomiske data (inntekter, utgifter, overskudd)</span>
                </label>
                
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={newReportForm.include_growth_metrics}
                    onChange={(e) => setNewReportForm({...newReportForm, include_growth_metrics: e.target.checked})}
                    className="form-checkbox h-4 w-4 text-yellow-600"
                  />
                  <span className="text-gray-300">Vekstmålinger (medlemmer, aktivitet, måloppnåelse)</span>
                </label>
                
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={newReportForm.include_customer_details}
                    onChange={(e) => setNewReportForm({...newReportForm, include_customer_details: e.target.checked})}
                    className="form-checkbox h-4 w-4 text-yellow-600"
                  />
                  <span className="text-gray-300">Kunde-detaljer (organisasjonstabeller, aktivitetsnivå)</span>
                </label>
              </div>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={newReportForm.enabled}
                  onChange={(e) => setNewReportForm({...newReportForm, enabled: e.target.checked})}
                  className="form-checkbox h-4 w-4 text-yellow-600"
                />
                <span className="text-gray-300">Aktiver automatisk sending</span>
              </label>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateReportModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500"
                >
                  Opprett Rapport
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Report Modal */}
      {editingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-90vh overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">Rediger Rapport: {editingReport.name}</h2>
            
            <form onSubmit={(e) => { 
              e.preventDefault(); 
              updateReport(editingReport.id, {
                name: editingReport.name,
                recipient_emails: editingReport.recipient_emails,
                interval_months: editingReport.interval_months,
                enabled: editingReport.enabled,
                include_financial_data: editingReport.include_financial_data,
                include_growth_metrics: editingReport.include_growth_metrics,
                include_customer_details: editingReport.include_customer_details
              }); 
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Rapport-navn</label>
                <input
                  type="text"
                  value={editingReport.name}
                  onChange={(e) => setEditingReport({...editingReport, name: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Epost-mottakere</label>
                <div className="space-y-2">
                  {editingReport.recipient_emails.map((email, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          const newEmails = [...editingReport.recipient_emails];
                          newEmails[index] = e.target.value;
                          setEditingReport({...editingReport, recipient_emails: newEmails});
                        }}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                        required
                      />
                      {editingReport.recipient_emails.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newEmails = editingReport.recipient_emails.filter((_, i) => i !== index);
                            setEditingReport({...editingReport, recipient_emails: newEmails});
                          }}
                          className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-md"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingReport({
                        ...editingReport, 
                        recipient_emails: [...editingReport.recipient_emails, '']
                      });
                    }}
                    className="flex items-center space-x-1 text-yellow-500 hover:text-yellow-400"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Legg til epost</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sending-intervall</label>
                <select
                  value={editingReport.interval_months}
                  onChange={(e) => setEditingReport({...editingReport, interval_months: parseInt(e.target.value)})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value={1}>Månedlig (hver måned)</option>
                  <option value={3}>Kvartalsvis (hver 3. måned)</option>
                  <option value={6}>Halvårlig (hver 6. måned)</option>
                  <option value={12}>Årlig (hver 12. måned)</option>
                </select>
              </div>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={editingReport.enabled}
                  onChange={(e) => setEditingReport({...editingReport, enabled: e.target.checked})}
                  className="form-checkbox h-4 w-4 text-yellow-600"
                />
                <span className="text-gray-300">Aktiv (send automatisk)</span>
              </label>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingReport(null)}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500"
                >
                  Lagre Endringer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdmin;